import { useState, useEffect, useCallback, useMemo, memo, useRef } from "react";
import { createPortal } from "react-dom";
import { TapCard, Currencies, Direction, Edges, Locale, Theme, tokenize } from '@tap-payments/card-sdk';
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  getDoc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp,
  getDocs,
  arrayUnion
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Firebase Configuration
const firebaseConfig = {
  apiKey: "AIzaSyAo_aAqvlh3jb0Mq3altvGk6IQJX-vFwBg",
  authDomain: "impressabuild-app.firebaseapp.com",
  projectId: "impressabuild-app",
  storageBucket: "impressabuild-app.firebasestorage.app",
  messagingSenderId: "1003880720801",
  appId: "1:1003880720801:web:c6c40fe7d78f0cd1e7c3e0"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);

// FCM (Push Notifications) Init
let messaging = null;
try {
  messaging = getMessaging(app);
} catch (err) {
  console.log('FCM not supported in this browser:', err);
}

// ⚠️ PASTE YOUR VAPID KEY FROM FIREBASE CONSOLE HERE
const FCM_VAPID_KEY = 'BLh7_IXMdk5UZLxb_-tVMS5wvunm0IQCAzHBqqE_AHQhQ6gg8__xxe_WFnYMqCNd6cMbhhz1zabPqf2zEEexaJA';

// ════════════════════════════════════════════════════════════
//  PUSH NOTIFICATION HELPERS
// ════════════════════════════════════════════════════════════

async function requestNotificationPermission(clientEmail) {
  if (!messaging || !('Notification' in window) || !('serviceWorker' in navigator)) {
    console.log('FCM not available: missing messaging, Notification, or serviceWorker');
    return null;
  }
  try {
    // Check if already granted
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      console.log('Notification permission:', permission);
      return null;
    }
    // Register or get existing service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;
    console.log('SW registered for push');
    // Always get token (each device gets a unique token)
    const token = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    if (token) {
      console.log('FCM token obtained:', token.substring(0, 20) + '...');
      await saveFCMToken(clientEmail, token);
      return token;
    } else {
      console.log('No FCM token returned');
    }
  } catch (err) {
    console.error('FCM setup error:', err);
  }
  return null;
}

async function saveFCMToken(email, token) {
  try {
    const tokenRef = doc(db, 'fcmTokens', email);
    const existing = await getDoc(tokenRef);
    if (existing.exists()) {
      const tokens = existing.data().tokens || [];
      if (!tokens.includes(token)) {
        await updateDoc(tokenRef, { tokens: arrayUnion(token), updatedAt: serverTimestamp() });
      }
    } else {
      await setDoc(tokenRef, {
        email: email,
        tokens: [token],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (err) {
    console.error('FCM token save error:', err);
  }
}

async function createPushNotification(clientEmail, type, title, body, projectName) {
  try {
    await addDoc(collection(db, 'notifications'), {
      clientEmail,
      type,
      title,
      body,
      projectName: projectName || '',
      read: false,
      sent: false,
      createdAt: serverTimestamp()
    });
  } catch (err) {
    console.error('Push notification create error:', err);
  }
}

// Helper: make file URLs viewable/downloadable
function viewableUrl(url) {
  if (!url) return url;
  return url;
}

// Check if URL is likely a PDF
function isPdf(url) {
  if (!url) return false;
  return /\.pdf/i.test(url) || url.includes('/raw/upload/');
}

// Get viewer URL - Firebase Storage URLs work directly in browsers for PDFs
function googleViewerUrl(url) {
  if (!url) return url;
  // Firebase Storage URLs with tokens work directly
  if (url.includes('firebasestorage')) return url;
  // Cloudinary old URLs - use Google viewer
  if (url.includes('cloudinary.com')) {
    return 'https://docs.google.com/gview?url=' + encodeURIComponent(url);
  }
  return url;
}

let foregroundListenerSetup = false;
function setupForegroundMessages(callback) {
  if (!messaging || foregroundListenerSetup) return;
  foregroundListenerSetup = true;
  onMessage(messaging, (payload) => {
    console.log('Foreground message:', payload);
    const data = payload.data || {};
    const title = data.title || payload.notification?.title || 'Update';
    const body = data.body || payload.notification?.body || 'You have a new update';
    if (callback) callback({ title, body });
  });
}

// Cloudinary Configuration
const CLOUDINARY_CLOUD_NAME = "dvd2f7zx9";
const CLOUDINARY_UPLOAD_PRESET = "ImpressaBuild";

// Admin email
const ADMIN_EMAIL = "contact@impressabuild.com";

// Master password for admin to access any client account
const MASTER_PASSWORD = "Impressa@2026";

// Tap Payments Configuration
const TAP_PUBLIC_KEY = "pk_live_HuUaSlCRB6gTriIYphjMEmf1UFJo7";
const TAP_MERCHANT_ID = "68034932";
const TAP_CHARGE_URL = "https://us-central1-impressabuild-app.cloudfunctions.net/createTapCharge";
const TAP_VERIFY_URL = "https://us-central1-impressabuild-app.cloudfunctions.net/verifyTapCharge";
const TAP_SECRET_KEY = "sk_test_fBegS036JLbavVUIcjynAlMP";

// Default client password (must change on first login)
const DEFAULT_CLIENT_PASSWORD = "123456";

// Quotation Workflow Stages
const QUOTE_STAGES = [
  { id: 0, name: 'Order Received', icon: '📥', color: '#6b7280' },
  { id: 1, name: 'Design', icon: '🎨', color: '#8b5cf6' },
  { id: 2, name: 'BOQ & Costing', icon: '📊', color: '#3b82f6' },
  { id: 3, name: 'Preparing Quotation', icon: '📝', color: '#f59e0b' },
  { id: 4, name: 'Final Review', icon: '🔍', color: '#ec4899' },
  { id: 5, name: 'Quotation Delivered', icon: '✅', color: '#22c55e' }
];

// Theme
const BG = "#0a0a0b";
const CARD = "rgba(255,255,255,0.03)";
const WHITE = "#fff";
const GRAY = "rgba(255,255,255,0.5)";
const O = "#f97316";
const OGLOW = "rgba(255,107,0,0.08)";
const OBORDER = "rgba(255,107,0,0.25)";
const GREEN = "#4cd49a";
const FONT = "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// Categories
const CATEGORIES = [
  { name: "Exhibition Stands", icon: "🏛", color: "#f97316", options: ["Custom Build", "Modular System", "Pop-Up Display", "Island Booth", "Double-Decker", "Other"] },
  { name: "Retail Fixtures", icon: "🛍", color: "#a855f7", options: ["Display Units", "Shelving Systems", "Checkout Counters", "Window Displays", "Signage", "Other"] },
  { name: "Corporate Interiors", icon: "🏢", color: "#3b82f6", options: ["Reception Areas", "Meeting Rooms", "Open Office", "Executive Suites", "Breakout Spaces", "Other"] },
  { name: "Event Production", icon: "🎪", color: "#10b981", options: ["Stage Design", "Backdrop Systems", "Lighting Rigs", "Audio Setup", "Full Production", "Other"] },
  { name: "Signage & Branding", icon: "✨", color: "#ec4899", options: ["3D Letters", "LED Signs", "Wayfinding", "Vehicle Wraps", "Building Signage", "Other"] },
  { name: "Custom Fabrication", icon: "🔧", color: "#f59e0b", options: [] }
];

const STAGES = ["Design", "Production", "Install", "QA", "Complete"];
const STAGE_ICONS = ["✏️", "🔨", "🏗", "✅", "🎉"];

// Demo clients
const DEMO_DB = {
  "ahmed@alnakheel.sa": {
    password: "1234", name: "Ahmed Al-Rashid", company: "Al Nakheel Developments",
    projects: [
      { id: "PRJ-2026-041", name: "GITEX Global Booth", category: "Exhibition Stands", type: "Island Booth", stage: 2, progress: 65, date: "Feb 2026", photos: [{ id: "p1", label: "Booth_Render_Final.jpg", date: "Mar 12", size: "4.2 MB", color: "#f97316" }, { id: "p2", label: "Structure_Progress.jpg", date: "Mar 28", size: "3.1 MB", color: "#3b82f6" }] },
      { id: "PRJ-2026-038", name: "Riyadh HQ Reception", category: "Corporate Interiors", type: "Reception Areas", stage: 4, progress: 100, date: "Jan 2026", photos: [{ id: "p3", label: "Reception_Complete.jpg", date: "Feb 20", size: "5.8 MB", color: "#10b981" }] }
    ]
  },
  "sara@riyadhretail.sa": {
    password: "1234", name: "Sara Al-Mutairi", company: "Riyadh Retail Group",
    projects: [
      { id: "PRJ-2026-055", name: "Mall Kiosk Network", category: "Retail Fixtures", type: "Display Units", stage: 1, progress: 35, date: "Mar 2026", photos: [] },
      { id: "PRJ-2026-048", name: "Flagship Store Rebrand", category: "Signage & Branding", type: "3D Letters", stage: 3, progress: 85, date: "Feb 2026", photos: [{ id: "p4", label: "Signage_Install.jpg", date: "Apr 2", size: "2.9 MB", color: "#ec4899" }] },
      { id: "PRJ-2026-032", name: "Annual Conference Stage", category: "Event Production", type: "Stage Design", stage: 4, progress: 100, date: "Dec 2025", photos: [{ id: "p5", label: "Stage_Final.jpg", date: "Jan 15", size: "6.1 MB", color: "#a855f7" }, { id: "p6", label: "Event_Live.jpg", date: "Jan 16", size: "4.5 MB", color: "#a855f7" }] }
    ]
  }
};

// Admin project stages
const PROJECT_STAGES = [
  { id: 'design', label: 'Design', color: '#6366f1' },
  { id: 'production', label: 'Production', color: '#f59e0b' },
  { id: 'install', label: 'Install', color: '#10b981' },
  { id: 'qa', label: 'Quality Check', color: '#3b82f6' },
  { id: 'complete', label: 'Complete', color: '#22c55e' }
];

const SUB_CATEGORIES = [
  'AV Contractor', 'Carpenter', 'Electrician', 'Painter', 'Installer',
  'Furniture', 'Cleaning', 'Gypsum Board', 'Lighting', 'Flooring',
  'Graphics & Printing', 'Rigging', 'Plumbing', 'HVAC', 'Other'
];

const NAV = [
  { key: "Home", icon: "🏠" },
  { key: "Order", icon: "📝" },
  { key: "Projects", icon: "📊" },
  { key: "Financials", icon: "💰" },
  { key: "Account", icon: "👤" }
];

// Logo components - using your uploaded images
function FullLogo({ width = 200 }) {
  return <img src="/logo.png" alt="ImpressaBuild" style={{ width, height: 'auto' }} />;
}

function IconMark({ size = 40 }) {
  return <img src="/app-icon-192.png" alt="ImpressaBuild" style={{ width: size, height: size, borderRadius: 8 }} />;
}

function HeaderLogo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <IconMark size={36} />
      <span style={{ color: WHITE, fontSize: 18, fontWeight: 700 }}>ImpressaBuild</span>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>{children}</div>;
}

function FInput({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={s.inputLabel}>{label}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={s.input} />
    </div>
  );
}

function InstallPrompt({ onClose }) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
  
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: CARD, border: "1px solid " + OBORDER, borderRadius: 24, padding: 28, width: "100%", maxWidth: 400, textAlign: "center", maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ width: 80, height: 80, margin: "0 auto", borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 32px rgba(249,115,22,0.3)" }}>
          <img src="/app-icon-192.png" alt="ImpressaBuild" style={{ width: "100%", height: "100%" }} />
        </div>
        <h3 style={{ color: WHITE, fontSize: 22, fontWeight: 700, marginTop: 20 }}>Install ImpressaBuild</h3>
        <p style={{ color: GRAY, fontSize: 14, lineHeight: 1.6, marginTop: 8 }}>Add to your home screen for the best experience!</p>
        
        {isIOS && isSafari ? (
          /* Safari on iOS */
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: 20, marginTop: 20, textAlign: "left" }}>
            <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>FOLLOW THESE STEPS</div>
            
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: O, display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>1</div>
              <div>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Tap the three dots menu</div>
                <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>At the <span style={{ color: O, fontWeight: 700 }}>bottom right</span> of your screen</div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ padding: '8px 14px', background: 'rgba(255,107,0,0.15)', border: '2px solid rgba(255,107,0,0.5)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: O }} />
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: O }} />
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: O }} />
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: O, display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>2</div>
              <div>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Tap <span style={{ color: O }}>"Share"</span></div>
                <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>First option at the top of the menu</div>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: O, display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>3</div>
              <div>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Scroll down and tap <span style={{ color: O }}>"Add to Home Screen"</span></div>
                <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>Swipe up on the popup to find it</div>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: O, display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>4</div>
              <div>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Tap <span style={{ color: O, fontWeight: 700 }}>Add</span> to confirm</div>
                <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>The app will appear on your home screen</div>
              </div>
            </div>
          </div>
        ) : (
          /* Chrome / Android / Other browsers */
          <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 16, padding: 20, marginTop: 20, textAlign: "left" }}>
            <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 16 }}>FOLLOW THESE STEPS</div>
            
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: O, display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>1</div>
              <div>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Tap the three dots menu</div>
                <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>Located at the <span style={{ color: O, fontWeight: 700 }}>bottom right</span> of your screen</div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ padding: '8px 14px', background: 'rgba(255,107,0,0.15)', border: '2px solid ' + O, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: O }} />
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: O }} />
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: O }} />
                  </div>
                  <span style={{ color: GRAY, fontSize: 12 }}>or</span>
                  <div style={{ padding: '8px 10px', background: 'rgba(255,107,0,0.15)', border: '2px solid ' + O, borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: O }} />
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: O }} />
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: O }} />
                  </div>
                </div>
                <div style={{ color: GRAY, fontSize: 12, marginTop: 6 }}>It may be at the bottom right or top right of your browser</div>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: O, display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>2</div>
              <div>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Tap <span style={{ color: O }}>"Add to Home Screen"</span></div>
                <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>Or <span style={{ color: O }}>"Add to Dock"</span> or <span style={{ color: O }}>"Install App"</span> depending on your browser</div>
              </div>
            </div>
            
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: O, display: "flex", alignItems: "center", justifyContent: "center", color: WHITE, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>3</div>
              <div>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Tap <span style={{ color: O, fontWeight: 700 }}>Add</span> to confirm</div>
                <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>The app will appear on your home screen</div>
              </div>
            </div>
          </div>
        )}

        <button onClick={onClose} style={{ ...s.cta, width: "100%", marginTop: 20 }}>Got it!</button>
      </div>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================
export default function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState(null);
  const [showInstall, setShowInstall] = useState(false);

  // Prevent horizontal scroll and fix modal stacking
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = 'html,body{overflow-x:hidden!important;max-width:100vw!important}';
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const [clientNotifications, setClientNotifications] = useState([]);
  const [showClientNotifications, setShowClientNotifications] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const clientLoginRef = useRef(false);

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  useEffect(() => {
    // Restore client session if saved (for payment redirects), otherwise sign out
    const init = async () => {
      const saved = localStorage.getItem('ib_client_session');
      if (saved) {
        try {
          const u = JSON.parse(saved);
          if (u && u.email && !u.isAdmin) {
            await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
            setUser(u);
            setIsAdmin(false);
            setLoaded(true);
            // If returning from a payment redirect, go straight to Financials
            if (new URLSearchParams(window.location.search).get('tap_id')) setTab('Financials');
            requestNotificationPermission(u.email);
            setLoading(false);
            return;
          }
        } catch (e) {
          localStorage.removeItem('ib_client_session');
        }
      }
      try { await signOut(auth); } catch(e) {}
      setLoading(false);
    };
    init();
  }, []);

  const logout = async () => {
    localStorage.removeItem('ib_client_session');
    if (isAdmin) await signOut(auth);
    setUser(null);
    setIsAdmin(false);
    setTab("Home");
    setMenuOpen(false);
    setShowPasswordChange(false);
    setPendingUser(null);
  };

  const notify = useCallback((msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); }, []);

  const handleClientLogin = (u) => {
    // Check if first login - require password change
    if (u.isFirstLogin && !u.isMasterLogin) {
      setPendingUser(u);
      setShowPasswordChange(true);
      return;
    }
    
    // Persist client session (survives payment redirects)
    try { localStorage.setItem('ib_client_session', JSON.stringify({ ...u, isFirstLogin: false })); } catch(e) {}
    
    setUser(u);
    setIsAdmin(false);
    setLoaded(true);
    if (!window.matchMedia('(display-mode: standalone)').matches) {
      // Show install prompt - will be shown after notification popup is dismissed
      setTimeout(() => setShowInstall(true), 2000);
    }
    
    // Request push notification permission
    requestNotificationPermission(u.email);
    setupForegroundMessages((msg) => {
      setToast(msg.title + ': ' + msg.body);
      setTimeout(() => setToast(null), 5000);
    });
    
    // Fetch client notifications
    const fetchNotifications = async () => {
      const notifs = [];
      try {
        const invoicesSnap = await getDocs(query(collection(db, 'invoices'), where('clientEmail', '==', u.email)));
        invoicesSnap.forEach(docSnap => {
          const inv = docSnap.data();
          const due = (inv.amount || 0) - (inv.paid || 0);
          if (due > 0 && inv.status !== 'paid') {
            notifs.push({ type: 'payment', amount: due, projectName: inv.projectName || 'Invoice', description: inv.description || '' });
          }
        });
        
        const extraOrdersSnap = await getDocs(query(collection(db, 'extraOrders'), where('clientEmail', '==', u.email)));
        extraOrdersSnap.forEach(docSnap => {
          const eo = docSnap.data();
          const due = (eo.amount || 0) - (eo.paid || 0);
          if (due > 0 && eo.status !== 'paid') {
            notifs.push({ type: 'payment', amount: due, projectName: eo.projectName || 'Extra Order', description: eo.description || '' });
          }
        });
        
        const projectsSnap = await getDocs(query(collection(db, 'projects'), where('clientEmail', '==', u.email)));
        projectsSnap.forEach(docSnap => {
          const proj = docSnap.data();
          if ((proj.progress === 100 || proj.stage === 'complete') && !proj.thankYouSeen) {
            notifs.push({ type: 'project', message: `Project completed!`, project: proj.name });
          }
        });
        if (notifs.length > 0) {
          setClientNotifications(notifs);
          setShowClientNotifications(true);
        }
      } catch (err) { console.error(err); }
    };
    setTimeout(fetchNotifications, 1000);
  };

  const handlePasswordChanged = (u) => {
    setShowPasswordChange(false);
    setPendingUser(null);
    setUser(u);
    setIsAdmin(false);
    setLoaded(true);
    notify('Password changed successfully!');
  };

  const handleAdminLogin = (firebaseUser) => {
    if (clientLoginRef) clientLoginRef.current = false;
    setUser(firebaseUser);
    setIsAdmin(true);
    setLoaded(true);
    // Register admin for push notifications
    requestNotificationPermission(ADMIN_EMAIL);
    setupForegroundMessages((msg) => {
      setToast(msg.title + ': ' + msg.body);
      setTimeout(() => setToast(null), 5000);
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: BG }}>
        <div style={{ textAlign: "center" }}>
          <IconMark size={60} />
          <div style={{ color: GRAY, marginTop: 16 }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.root}>
      <div style={{ ...s.app, maxWidth: (isAdmin || user?.isProjectManager) ? 1400 : user?.isSubcontractor ? 600 : 500 }}>
        <div style={{ height: 3, background: O, flexShrink: 0 }} />
        
        {showPasswordChange && pendingUser ? (
          <PasswordChangeScreen user={pendingUser} onSuccess={handlePasswordChanged} onCancel={logout} />
        ) : !user ? (
          <LoginScreen onClientLogin={handleClientLogin} onAdminLogin={handleAdminLogin} clientLoginRef={clientLoginRef} />
        ) : isAdmin ? (
          <AdminPanel user={user} logout={logout} notify={notify} />
        ) : user.isSubcontractor ? (
          <SubcontractorPortal user={user} logout={logout} notify={notify} />
        ) : user.isProjectManager ? (
          <AdminPanel user={user} logout={logout} notify={notify} pmUser={user} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative', overflow: 'hidden' }}>
            {/* Fixed Header */}
            <div style={{ ...s.header, flexShrink: 0, background: BG }}>
              <HeaderLogo />
              <button style={s.burger} onClick={() => setMenuOpen(!menuOpen)}>
                <div style={{ ...s.bLine, transform: menuOpen ? "rotate(45deg) translate(3px,3px)" : "none" }} />
                <div style={{ ...s.bLine, opacity: menuOpen ? 0 : 1 }} />
                <div style={{ ...s.bLine, transform: menuOpen ? "rotate(-45deg) translate(3px,-3px)" : "none" }} />
              </button>
            </div>
            
            {/* Dropdown Menu */}
            <div style={{ overflow: "hidden", maxHeight: menuOpen ? 400 : 0, opacity: menuOpen ? 1 : 0, background: CARD, borderBottom: "1px solid " + (menuOpen ? OBORDER : "transparent"), transition: "all .35s", flexShrink: 0 }}>
              {["Home", "Order", "Projects", "Financials", "Contact", "Account"].map(t => (
                <button key={t} onClick={() => { setTab(t); setMenuOpen(false); }} style={{ display: "block", width: "100%", background: "none", border: "none", padding: "16px 24px", textAlign: "left", fontSize: 18, fontWeight: tab === t ? 700 : 400, color: tab === t ? O : WHITE, cursor: "pointer", fontFamily: FONT }}>{t}</button>
              ))}
            </div>
            
            {/* Scrollable Content */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', paddingBottom: 100 }}>
              {tab === "Home" && <Home loaded={loaded} go={setTab} user={user} />}
              {tab === "Order" && <OrderForm user={user} onDone={() => setTab("Projects")} onCancel={() => setTab("Home")} notify={notify} />}
              {tab === "Projects" && <MyProjects user={user} notify={notify} />}
              {tab === "Financials" && <FinancialsPage user={user} />}
              {tab === "Contact" && <ContactPage notify={notify} />}
              {tab === "Account" && <AccountPage user={user} logout={logout} onShowInstall={() => setShowInstall(true)} />}
            </div>
            
            {/* Fixed Tab Bar at bottom */}
            <div style={{ 
              flexShrink: 0,
              display: "flex", 
              justifyContent: "space-around", 
              alignItems: "center", 
              padding: "12px 8px calc(20px + env(safe-area-inset-bottom, 0px))", 
              background: BG, 
              borderTop: "1px solid rgba(255,255,255,0.08)"
            }}>
              {NAV.map(n => {
                const on = tab === n.key;
                return (
                  <button key={n.key} onClick={() => setTab(n.key)} style={s.tabBtn}>
                    <span style={{ fontSize: 24, opacity: on ? 1 : .35, filter: on ? "drop-shadow(0 0 8px " + O + ")" : "none", transition: "all .3s" }}>{n.icon}</span>
                    <span style={{ fontSize: 15, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 700, color: on ? O : "rgba(255,255,255,.3)" }}>{n.key}</span>
                    {on && <div style={s.tabDot} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        <div style={{ height: 5, background: O, flexShrink: 0 }} />
        {toast && <div style={{ position: "fixed", bottom: 120, left: 20, right: 20, maxWidth: 460, margin: '0 auto', background: CARD, border: "1px solid " + OBORDER, borderRadius: 14, padding: "16px 20px", color: WHITE, fontSize: 16, fontWeight: 600, textAlign: "center", zIndex: 999 }}>{toast}</div>}
        {showInstall && !showClientNotifications && <InstallPrompt onClose={() => setShowInstall(false)} />}
        
        {/* Client Notification Popup */}
        {showClientNotifications && clientNotifications.length > 0 && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 24, width: '100%', maxWidth: 380, overflow: 'hidden' }}>
              <div style={{ background: OGLOW, borderBottom: '1px solid ' + OBORDER, padding: 24, textAlign: 'center' }}>
                <div style={{ fontSize: 44, marginBottom: 8 }}>🔔</div>
                <h2 style={{ color: WHITE, fontSize: 22, fontWeight: 700, margin: 0 }}>Notifications</h2>
              </div>
              <div style={{ padding: 20 }}>
                {clientNotifications.map((notif, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: CARD, border: '1px solid ' + (notif.type === 'payment' ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.2)'), borderRadius: 14, marginBottom: 12 }}>
                    <span style={{ fontSize: 28 }}>{notif.type === 'payment' ? '💰' : '✅'}</span>
                    <div style={{ flex: 1 }}>
                      {notif.type === 'payment' ? (
                        <>
                          <div style={{ color: WHITE, fontWeight: 600, fontSize: 15 }}>{notif.projectName || 'Payment Due'}</div>
                          {notif.description && <div style={{ color: GRAY, fontSize: 13, marginTop: 2 }}>{notif.description}</div>}
                          <div style={{ color: '#ef4444', fontSize: 18, fontWeight: 700, marginTop: 4 }}>SAR {notif.amount.toLocaleString()}</div>
                        </>
                      ) : (
                        <>
                          <div style={{ color: WHITE, fontWeight: 600, fontSize: 15 }}>{notif.message}</div>
                          <div style={{ color: GRAY, fontSize: 15, marginTop: 2 }}>{notif.project}</div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <button onClick={() => setShowClientNotifications(false)} style={{ ...s.cta, width: '100%', marginTop: 8 }}>Got it</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// PASSWORD CHANGE SCREEN (First Login)
// ============================================
function PasswordChangeScreen({ user, onSuccess, onCancel }) {
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);

  const handleSubmit = async () => {
    setError('');
    
    if (newPass.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (newPass !== confirmPass) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPass === DEFAULT_CLIENT_PASSWORD) {
      setError('Please choose a different password');
      return;
    }
    
    setLoading(true);
    try {
      try {
        await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
      } catch (authErr) {
        setError('Auth failed: ' + authErr.code + '. Contact support.');
        setLoading(false);
        return;
      }
      
      const emailLower = (user.email || '').toLowerCase().trim();
      const collectionName = user.isSubcontractor ? 'subcontractors' : user.isProjectManager ? 'projectManagers' : 'users';
      let updated = false;
      
      // Method 1: Query by email field
      try {
        const q = query(collection(db, collectionName), where('email', '==', emailLower));
        const snap = await getDocs(q);
        if (!snap.empty) {
          for (const d of snap.docs) {
            await updateDoc(d.ref, { password: newPass, isFirstLogin: false });
            updated = true;
          }
        }
      } catch (e) { console.error('Method 1 failed:', e); }
      
      // Method 2: Try all docs
      if (!updated) {
        try {
          const allDocs = await getDocs(collection(db, collectionName));
          allDocs.forEach(async (d) => {
            const data = d.data();
            if (data.email && data.email.toLowerCase() === emailLower) {
              await updateDoc(d.ref, { password: newPass, isFirstLogin: false });
              updated = true;
            }
          });
        } catch (e) { console.error('Method 2 failed:', e); }
      }
      
      // Method 3: Direct doc by email ID
      if (!updated) {
        try {
          await setDoc(doc(db, collectionName, emailLower), { password: newPass, isFirstLogin: false }, { merge: true });
          updated = true;
        } catch (e) { console.error('Method 3 failed:', e); }
      }
      
      if (updated) {
        try {
          const verifyQ = query(collection(db, collectionName), where('email', '==', emailLower));
          const verifySnap = await getDocs(verifyQ);
          if (!verifySnap.empty) {
            const savedPass = verifySnap.docs[0].data().password;
            if (savedPass !== newPass) {
              setError('Password save failed. Please try again.');
              setLoading(false);
              return;
            }
          }
        } catch (e) {}
        onSuccess({ ...user, isFirstLogin: false });
      } else {
        setError('Could not update password. Contact support.');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to change password: ' + (err.message || 'Unknown error'));
    }
    setLoading(false);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '0 24px', minHeight: '100vh' }}>
      <div style={{ position: 'absolute', top: '10%', left: '-20%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, ' + OGLOW + ' 0%, transparent 70%)' }} />
      
      <div style={{ position: 'relative', zIndex: 2, opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(30px)', transition: 'all .7s', width: '100%', maxWidth: 400, textAlign: 'center' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <IconMark size={60} />
        </div>
        
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <h1 style={{ color: WHITE, fontSize: 28, fontWeight: 700, margin: '0 0 8px' }}>Set Your Password</h1>
        <p style={{ color: GRAY, fontSize: 15, margin: '0 0 32px' }}>Welcome! Please create a new password for your account.</p>
        
        <div style={{ textAlign: 'left' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: GRAY, fontSize: 14, fontWeight: 600, marginBottom: 8, letterSpacing: 1 }}>NEW PASSWORD</div>
            <input 
              type="password" 
              value={newPass} 
              onChange={e => setNewPass(e.target.value)} 
              placeholder="Enter new password"
              style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: GRAY, fontSize: 14, fontWeight: 600, marginBottom: 8, letterSpacing: 1 }}>CONFIRM PASSWORD</div>
            <input 
              type="password" 
              value={confirmPass} 
              onChange={e => setConfirmPass(e.target.value)} 
              placeholder="Confirm new password"
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{ ...s.input, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          
          {error && <div style={{ color: '#ef4444', fontSize: 14, marginBottom: 16, textAlign: 'center' }}>{error}</div>}
          
          <button 
            onClick={handleSubmit} 
            disabled={loading}
            style={{ ...s.cta, width: '100%', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Updating...' : 'Set Password & Continue'}
          </button>
          
          <button 
            onClick={onCancel}
            style={{ background: 'none', border: 'none', color: GRAY, fontSize: 14, marginTop: 16, cursor: 'pointer', width: '100%' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LOGIN SCREEN WITH PORTAL SELECTION
// ============================================
function LoginScreen({ onClientLogin, onAdminLogin, clientLoginRef }) {
  const [portal, setPortal] = useState(null); // null = landing, 'admin' or 'client'
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => { setTimeout(() => setShow(true), 100); }, []);

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    const emailLower = email.toLowerCase().trim();

    if (portal === 'admin') {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, emailLower, pass);
        setLoading(false);
        onAdminLogin(userCredential.user);
        return;
      } catch (err) {
        setLoading(false);
        if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
          setError("Incorrect password.");
        } else if (err.code === 'auth/user-not-found') {
          setError("Admin account not set up in Firebase.");
        } else {
          setError(err.message);
        }
        return;
      }
    }

    // Subcontractor login
    if (portal === 'subcontractor') {
      try {
        const q = query(collection(db, 'subcontractors'), where('email', '==', emailLower));
        const snap = await getDocs(q);
        let subData = null;
        if (!snap.empty) subData = { id: snap.docs[0].id, ...snap.docs[0].data() };
        if (!subData) {
          try { const d = await getDoc(doc(db, 'subcontractors', emailLower)); if (d.exists()) subData = { id: d.id, ...d.data() }; } catch(e) {}
        }
        if (subData) {
          if (subData.password === pass) {
            if (clientLoginRef) clientLoginRef.current = true;
            try { await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD); } catch(e) {}
            setLoading(false);
            onClientLogin({ email: emailLower, uid: 'sub-' + emailLower, name: subData.name || emailLower.split('@')[0], company: subData.company || '', phone: subData.phone || '', category: subData.category || '', isSubcontractor: true, isFirstLogin: subData.isFirstLogin !== false, subDocId: subData.id });
            return;
          } else {
            setLoading(false); setError("Incorrect password."); return;
          }
        } else {
          setLoading(false); setError("No partner account found."); return;
        }
      } catch (e) { setLoading(false); setError("Login failed. Please try again."); return; }
    }

    // Project Manager login
    if (portal === 'pm') {
      try {
        const pmQ = query(collection(db, 'projectManagers'), where('email', '==', emailLower));
        const pmSnap = await getDocs(pmQ);
        if (!pmSnap.empty) {
          const pmData = { id: pmSnap.docs[0].id, ...pmSnap.docs[0].data() };
          if (pmData.password === pass) {
            if (clientLoginRef) clientLoginRef.current = true;
            try { await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD); } catch(e) {}
            setLoading(false);
            onClientLogin({ email: emailLower, uid: 'pm-' + emailLower, name: pmData.name || emailLower.split('@')[0], phone: pmData.phone || '', isProjectManager: true, isFirstLogin: pmData.isFirstLogin !== false, pmDocId: pmData.id });
            return;
          } else {
            setLoading(false); setError("Incorrect password."); return;
          }
        } else {
          setLoading(false); setError("No project manager account found."); return;
        }
      } catch (e) { setLoading(false); setError("Login failed. Please try again."); return; }
    }

    // Client login
      // Check for master password first
      if (pass === MASTER_PASSWORD) {
        try {
          if (clientLoginRef) clientLoginRef.current = true;
          await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
          setLoading(false);
          onClientLogin({ email: emailLower, uid: 'master-' + emailLower, name: emailLower.split('@')[0], isMasterLogin: true });
          return;
        } catch (adminErr) {
          if (clientLoginRef) clientLoginRef.current = false;
          setLoading(false);
          setError("Master login failed: " + adminErr.code);
          return;
        }
      }
      
      // Check Firestore users collection WITHOUT needing auth (rules allow public read on users)
      try {
        // Try query by email field
        const q = query(collection(db, 'users'), where('email', '==', emailLower));
        const snap = await getDocs(q);
        
        let userData = null;
        if (!snap.empty) {
          userData = snap.docs[0].data();
        } else {
          // Also try direct doc lookup
          try {
            const directDoc = await getDoc(doc(db, 'users', emailLower));
            if (directDoc.exists()) userData = directDoc.data();
          } catch (e) {}
        }
        
        if (userData) {
          if (userData.password === pass) {
            // Password correct - now sign in as admin for Firebase access
            if (clientLoginRef) clientLoginRef.current = true;
            try {
              await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
            } catch (e) {
              console.error('Admin auth for client failed:', e.code);
              // Continue anyway - some features may not work
            }
            setLoading(false);
            onClientLogin({ 
              email: emailLower, 
              uid: 'client-' + emailLower, 
              name: userData.name || emailLower.split('@')[0],
              company: userData.company || '',
              phone: userData.phone || '',
              isFirstLogin: userData.isFirstLogin || false,
              isCompanyAdmin: userData.isCompanyAdmin !== false
            });
            return;
          } else {
            setLoading(false);
            setError("Incorrect password.");
            return;
          }
        } else {
          setLoading(false);
          setError("No account found. Contact ImpressaBuild to get access.");
          return;
        }
      } catch (e) {
        console.error('Login error:', e);
        setLoading(false);
        setError("Login failed. Please try again.");
        return;
      }
  };

  // Landing Page - Portal Selection
  if (portal === null) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center", padding: "60px 24px 80px", position: "relative", overflowX: "hidden", overflowY: "auto", minHeight: "100vh", width: "100%" }}>
        <div style={{ position: "absolute", top: "10%", left: 0, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, " + OGLOW + " 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "10%", right: 0, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(249,115,22,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 2, opacity: show ? 1 : 0, transform: show ? "none" : "translateY(30px)", transition: "all .7s", width: "100%", maxWidth: 500, textAlign: "center" }}>
          <div style={{ marginBottom: 20, display: "flex", justifyContent: "center" }}>
            <FullLogo width={280} />
          </div>
          <h1 style={{ color: WHITE, fontSize: 36, fontWeight: 700, margin: "0 0 12px" }}>Welcome</h1>
          <p style={{ color: GRAY, fontSize: 18, margin: "0 0 28px", lineHeight: 1.6 }}>Precision in Production, Excellence in Execution</p>
          
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* ImpressaBuild (Staff) Button */}
            <button 
              onClick={() => setPortal('impressa')}
              style={{
                display: "flex", alignItems: "center", gap: 20, padding: "18px 22px",
                background: CARD, border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 20,
                cursor: "pointer", fontFamily: FONT, textAlign: "left", transition: "all .2s",
                width: "100%"
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = O; e.currentTarget.style.background = OGLOW; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = CARD; }}
            >
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, " + O + ", #ea580c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>🏢</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: WHITE, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>ImpressaBuild</div>
                <div style={{ color: GRAY, fontSize: 15 }}>Admin & Project Managers</div>
              </div>
              <span style={{ color: O, fontSize: 24, fontWeight: 700 }}>→</span>
            </button>

            {/* Client Portal Button */}
            <button 
              onClick={() => setPortal('client')}
              style={{
                display: "flex", alignItems: "center", gap: 20, padding: "18px 22px",
                background: CARD, border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 20,
                cursor: "pointer", fontFamily: FONT, textAlign: "left", transition: "all .2s",
                width: "100%"
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = O; e.currentTarget.style.background = OGLOW; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = CARD; }}
            >
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>👤</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: WHITE, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Client Portal</div>
                <div style={{ color: GRAY, fontSize: 15 }}>Track projects & place orders</div>
              </div>
              <span style={{ color: O, fontSize: 24, fontWeight: 700 }}>→</span>
            </button>

            {/* Partners Portal Button */}
            <button 
              onClick={() => setPortal('subcontractor')}
              style={{
                display: "flex", alignItems: "center", gap: 20, padding: "18px 22px",
                background: CARD, border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 20,
                cursor: "pointer", fontFamily: FONT, textAlign: "left", transition: "all .2s",
                width: "100%"
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = O; e.currentTarget.style.background = OGLOW; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = CARD; }}
            >
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, #8b5cf6, #6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>🔧</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: WHITE, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Partners Portal</div>
                <div style={{ color: GRAY, fontSize: 15 }}>View tasks & update progress</div>
              </div>
              <span style={{ color: O, fontSize: 24, fontWeight: 700 }}>→</span>
            </button>
          </div>

          <div style={{ marginTop: 24, color: GRAY, fontSize: 14 }}>
            <a href="/about.html" style={{ color: O, textDecoration: 'none', fontWeight: 600 }}>About Us</a>
            <span style={{ margin: '0 10px', opacity: 0.4 }}>|</span>
            <a href="/privacy.html" style={{ color: GRAY, textDecoration: 'none' }}>Privacy</a>
            <span style={{ margin: '0 10px', opacity: 0.4 }}>|</span>
            <a href="/refund.html" style={{ color: GRAY, textDecoration: 'none' }}>Refund Policy</a>
          </div>
          <div style={{ marginTop: 12, color: GRAY, fontSize: 14 }}>
            <span style={{ opacity: 0.6 }}>Need help?</span>{" "}
            <a href="mailto:contact@impressabuild.com" style={{ color: O, textDecoration: "none", fontWeight: 600 }}>Contact Support</a>
          </div>
        </div>
      </div>
    );
  }

  // ImpressaBuild staff selection (Admin or PM)
  if (portal === 'impressa') {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 24px", position: "relative", overflowX: "hidden", overflowY: "auto", minHeight: "100vh" }}>
        <div style={{ position: "relative", zIndex: 2, maxWidth: 440, margin: "0 auto", width: "100%" }}>
          <button onClick={() => setPortal(null)} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", color: O, fontSize: 16, fontWeight: 600, cursor: "pointer", marginBottom: 24, padding: 0, fontFamily: FONT }}>
            <span style={{ fontSize: 20 }}>←</span> Back
          </button>
          <div style={{ marginBottom: 20, display: "flex", justifyContent: "center" }}>
            <FullLogo width={240} />
          </div>
          <h2 style={{ color: WHITE, fontSize: 26, fontWeight: 700, margin: "0 0 8px", textAlign: "center" }}>ImpressaBuild Team</h2>
          <p style={{ color: GRAY, fontSize: 16, margin: "0 0 28px", textAlign: "center" }}>Select your role</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <button onClick={() => setPortal('admin')} style={{ display: "flex", alignItems: "center", gap: 20, padding: "18px 22px", background: CARD, border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 20, cursor: "pointer", fontFamily: FONT, textAlign: "left", width: "100%" }}
              onMouseOver={e => { e.currentTarget.style.borderColor = O; }} onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, " + O + ", #ea580c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>🔐</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: WHITE, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Admin</div>
                <div style={{ color: GRAY, fontSize: 15 }}>Full management access</div>
              </div>
              <span style={{ color: O, fontSize: 24, fontWeight: 700 }}>→</span>
            </button>
            <button onClick={() => setPortal('pm')} style={{ display: "flex", alignItems: "center", gap: 20, padding: "18px 22px", background: CARD, border: "1.5px solid rgba(255,255,255,0.08)", borderRadius: 20, cursor: "pointer", fontFamily: FONT, textAlign: "left", width: "100%" }}
              onMouseOver={e => { e.currentTarget.style.borderColor = "#3b82f6"; }} onMouseOut={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>👔</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: WHITE, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Project Manager</div>
                <div style={{ color: GRAY, fontSize: 15 }}>Track your assigned projects</div>
              </div>
              <span style={{ color: "#3b82f6", fontSize: 24, fontWeight: 700 }}>→</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Login Form (Admin or Client)
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 24px", position: "relative", overflowX: "hidden", overflowY: "auto", minHeight: "100vh" }}>
      <div style={{ position: "absolute", top: "15%", left: 0, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, " + OGLOW + " 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "relative", zIndex: 2, opacity: show ? 1 : 0, transform: show ? "none" : "translateY(30px)", transition: "all .7s", maxWidth: 440, margin: "0 auto", width: "100%" }}>
        
        {/* Back Button */}
        <button onClick={() => { setPortal(null); setEmail(""); setPass(""); setError(""); }} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", color: O, fontSize: 16, fontWeight: 600, cursor: "pointer", marginBottom: 24, padding: 0, fontFamily: FONT }}>
          <span style={{ fontSize: 20 }}>←</span> Back
        </button>

        <div style={{ marginBottom: 20, display: "flex", justifyContent: "center" }}>
          <FullLogo width={220} />
        </div>

        {/* Portal Badge */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <div style={{ 
            display: "inline-flex", alignItems: "center", gap: 10, 
            padding: "10px 20px", borderRadius: 100,
            background: portal === 'admin' ? "linear-gradient(135deg, " + O + ", #ea580c)" : portal === 'subcontractor' ? "linear-gradient(135deg, #8b5cf6, #6d28d9)" : portal === 'pm' ? "linear-gradient(135deg, #3b82f6, #1d4ed8)" : "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: WHITE, fontSize: 14, fontWeight: 700, letterSpacing: 1
          }}>
            <span style={{ fontSize: 18 }}>{portal === 'admin' ? '🔐' : portal === 'subcontractor' ? '🔧' : portal === 'pm' ? '👔' : '👤'}</span>
            {portal === 'admin' ? 'ADMIN PORTAL' : portal === 'subcontractor' ? 'PARTNERS PORTAL' : portal === 'pm' ? 'PROJECT MANAGER' : 'CLIENT PORTAL'}
          </div>
        </div>

        <h2 style={{ color: WHITE, fontSize: 28, fontWeight: 700, margin: "0 0 8px", textAlign: "center" }}>Sign In</h2>
        <p style={{ color: GRAY, fontSize: 16, margin: "0 0 32px", textAlign: "center" }}>
          {portal === 'admin' ? 'Access your admin dashboard' : portal === 'subcontractor' ? 'View tasks & updates' : portal === 'pm' ? 'Manage your assigned projects' : 'Track your projects & orders'}
        </p>

        <div style={{ marginBottom: 20 }}>
          <div style={{ color: GRAY, fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Email</div>
          <input 
            value={email} 
            onChange={e => { setEmail(e.target.value); setError(""); }} 
            placeholder={portal === 'admin' ? "admin@company.com" : "your@email.com"} 
            style={{ ...s.input, fontSize: 20, padding: "20px 22px" }} 
            onKeyDown={e => e.key === "Enter" && handleLogin()} 
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: GRAY, fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Password</div>
          <input 
            value={pass} 
            onChange={e => { setPass(e.target.value); setError(""); }} 
            placeholder="••••••••" 
            type="password" 
            style={{ ...s.input, fontSize: 20, padding: "20px 22px" }} 
            onKeyDown={e => e.key === "Enter" && handleLogin()} 
          />
        </div>
        {error && <div style={{ color: "#E85454", fontSize: 15, fontWeight: 500, marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>⚠️ {error}</div>}
        <button 
          style={{ 
            ...s.cta, width: "100%", marginTop: 24, padding: "20px 24px", fontSize: 18, 
            opacity: loading ? .6 : 1,
            background: portal === 'admin' ? O : "#3b82f6"
          }} 
          onClick={handleLogin} 
          disabled={loading}
        >
          {loading ? "Signing in..." : "Sign In →"}
        </button>

        {/* Client help */}
        {portal === 'client' && (
          <div style={{ marginTop: 28, background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "20px 22px", textAlign: "center" }}>
            <div style={{ color: GRAY, fontSize: 14, lineHeight: 1.6 }}>
              Don't have an account?<br />
              <a href="mailto:contact@impressabuild.com" style={{ color: "#3b82f6", textDecoration: "none", fontWeight: 600 }}>Contact us</a> to get access.
            </div>
          </div>
        )}

        {/* Admin hint */}
        {portal === 'admin' && (
          <div style={{ marginTop: 28, background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "20px 22px", textAlign: "center" }}>
            <div style={{ color: GRAY, fontSize: 14, lineHeight: 1.6 }}>
              Admin access is restricted to authorized personnel only.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// ADMIN PANEL (Mobile Responsive)
// ============================================
function AdminPanel({ user, logout, notify, pmUser }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [allProjectsRaw, setProjects] = useState([]);
  const [allOrdersRaw, setOrders] = useState([]);
  const [allCRRaw, setChangeRequests] = useState([]);
  const [allInvoicesRaw, setInvoices] = useState([]);
  const [pmClients, setPmClients] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showNotifications, setShowNotifications] = useState(false);

  // PM scope: fetch clients belonging to this PM
  useEffect(() => {
    if (!pmUser) return;
    const q = query(collection(db, 'users'), where('pmEmail', '==', pmUser.email));
    const unsub = onSnapshot(q, (snap) => {
      setPmClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [pmUser]);

  const pmClientEmails = useMemo(() => pmClients.map(c => (c.email || '').toLowerCase()), [pmClients]);

  // Scoped data: PM sees only his projects/clients; admin sees everything
  const projects = useMemo(() => {
    if (!pmUser) return allProjectsRaw;
    return allProjectsRaw.filter(p => p.pmEmail === pmUser.email || pmClientEmails.includes((p.clientEmail || '').toLowerCase()));
  }, [allProjectsRaw, pmUser, pmClientEmails]);

  const scopedProjectNames = useMemo(() => projects.map(p => p.name), [projects]);
  const scopedProjectIds = useMemo(() => projects.map(p => p.id), [projects]);

  const orders = useMemo(() => {
    if (!pmUser) return allOrdersRaw;
    return allOrdersRaw.filter(o => pmClientEmails.includes((o.userEmail || '').toLowerCase()) || (o.projectName && scopedProjectNames.includes(o.projectName)));
  }, [allOrdersRaw, pmUser, pmClientEmails, scopedProjectNames]);

  const changeRequests = useMemo(() => {
    if (!pmUser) return allCRRaw;
    return allCRRaw.filter(c => pmClientEmails.includes((c.userEmail || '').toLowerCase()) || (c.projectId && scopedProjectIds.includes(c.projectId)));
  }, [allCRRaw, pmUser, pmClientEmails, scopedProjectIds]);

  const invoices = useMemo(() => {
    if (!pmUser) return allInvoicesRaw;
    return allInvoicesRaw.filter(i => pmClientEmails.includes((i.clientEmail || '').toLowerCase()) || (i.projectName && scopedProjectNames.includes(i.projectName)));
  }, [allInvoicesRaw, pmUser, pmClientEmails, scopedProjectNames]);

  useEffect(() => {
    let timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setIsMobile(window.innerWidth < 768), 100);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const unsubProjects = onSnapshot(collection(db, 'projects'), (snap) => {
      const projs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      projs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setProjects(projs);
    });
    const unsubOrders = onSnapshot(collection(db, 'orders'), (snap) => {
      const ords = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      ords.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setOrders(ords);
    });
    const unsubCR = onSnapshot(collection(db, 'changeRequests'), (snap) => {
      const crs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      crs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setChangeRequests(crs);
    });
    const unsubInvoices = onSnapshot(collection(db, 'invoices'), (snap) => {
      const invs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInvoices(invs);
    });
    return () => { unsubProjects(); unsubOrders(); unsubCR(); unsubInvoices(); };
  }, []);

  // Calculate notifications with useMemo
  const { pendingOrders, pendingChanges, overdueInvoices, totalNotifications } = useMemo(() => {
    const pending = orders.filter(o => o.status === 'pending').length;
    const changes = changeRequests.filter(c => c.status === 'pending').length;
    const overdue = invoices.filter(inv => inv.status !== 'paid' && (inv.totalAmount || inv.amount) - (inv.paid || 0) > 0).length;
    return { pendingOrders: pending, pendingChanges: changes, overdueInvoices: overdue, totalNotifications: pending + changes + overdue };
  }, [orders, changeRequests, invoices]);

  // Show notification popup on mount if there are notifications
  useEffect(() => {
    if (totalNotifications > 0) {
      setShowNotifications(true);
    }
  }, []);

  const activeProjects = useMemo(() => projects.filter(p => !p.archived), [projects]);
  const archivedProjects = useMemo(() => projects.filter(p => p.archived), [projects]);

  const adminNav = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'projects', label: 'Projects', icon: '🏗' },
    { id: 'orders', label: 'Orders', icon: '📦', count: pendingOrders },
    { id: 'financials', label: 'Financials', icon: '💰' },
    { id: 'costs', label: 'Costs', icon: '📉' },
    { id: 'clients', label: 'Clients', icon: '👥' },
    { id: 'subcontractors', label: 'Partners', icon: '🔧' },
    ...(pmUser ? [] : [{ id: 'team', label: 'Team', icon: '👔' }]),
    { id: 'archive', label: 'Archive', icon: '🗄' }
  ], [pendingOrders, pendingChanges, pmUser]);

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Fixed Header */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: BG, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <IconMark size={36} />
          <div>
            <div style={{ color: WHITE, fontSize: 15, fontWeight: 700 }}>ImpressaBuild</div>
            <div style={{ color: pmUser ? '#3b82f6' : O, fontSize: 15, fontWeight: 600 }}>{pmUser ? 'PM: ' + (pmUser.name || '') : 'Admin Panel'}</div>
          </div>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
          <div style={{ width: 20, height: 2, background: O, marginBottom: 4, borderRadius: 2, transform: sidebarOpen ? 'rotate(45deg) translate(4px, 4px)' : 'none', transition: 'all .3s' }} />
          <div style={{ width: 20, height: 2, background: O, marginBottom: 4, borderRadius: 2, opacity: sidebarOpen ? 0 : 1, transition: 'all .3s' }} />
          <div style={{ width: 20, height: 2, background: O, borderRadius: 2, transform: sidebarOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none', transition: 'all .3s' }} />
        </button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <>
          <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 500 }} />
          <div style={{ position: 'fixed', top: 0, left: 0, width: '85%', maxWidth: 300, height: '100vh', background: BG, borderRight: '1px solid rgba(255,255,255,0.06)', zIndex: 501, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <IconMark size={36} />
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>Menu</div>
              </div>
              <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 28, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
              {adminNav.map(item => (
                <button key={item.id} onClick={() => handleNavClick(item.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px', background: activeTab === item.id ? OGLOW : 'transparent', border: activeTab === item.id ? '1px solid ' + OBORDER : '1px solid transparent', borderRadius: 12, color: activeTab === item.id ? O : GRAY, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginBottom: 4, fontFamily: FONT, textAlign: 'left' }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.count > 0 && <span style={{ background: O, color: WHITE, fontSize: 15, fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>{item.count}</span>}
                </button>
              ))}
            </nav>
            <div style={{ padding: '16px 12px calc(40px + env(safe-area-inset-bottom, 30px))', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button onClick={logout} style={{ width: '100%', padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>Sign Out</button>
            </div>
          </div>
        </>
      )}

      {/* Scrollable Content - same pattern as client portal */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ padding: '16px 16px 100px' }}>
          {activeTab === 'dashboard' && <AdminDashboard projects={activeProjects} allProjects={projects} orders={orders} changeRequests={changeRequests} setActiveTab={setActiveTab} setShowCreateModal={setShowCreateModal} isMobile={isMobile} />}
          {activeTab === 'projects' && <AdminProjects projects={activeProjects} setSelectedProject={setSelectedProject} setShowCreateModal={setShowCreateModal} notify={notify} isMobile={isMobile} />}
          {activeTab === 'orders' && <AdminOrders orders={orders} notify={notify} />}
          {activeTab === 'clients' && <AdminClients projects={activeProjects} notify={notify} pmUser={pmUser} />}
          {activeTab === 'subcontractors' && <AdminSubcontractors projects={activeProjects} notify={notify} />}
          {activeTab === 'team' && <AdminTeam notify={notify} />}
          {activeTab === 'financials' && <AdminFinancials projects={activeProjects} allProjects={projects} notify={notify} pmScope={pmUser ? { clientEmails: pmClientEmails, projectNames: scopedProjectNames } : null} />}
          {activeTab === 'costs' && <AdminCosts projects={projects} notify={notify} restrictIds={pmUser ? projects.map(p => p.id) : null} />}
          {activeTab === 'archive' && <AdminArchive projects={archivedProjects} notify={notify} />}
        </div>
      </div>

      {showCreateModal && createPortal(<CreateProjectModal onClose={() => setShowCreateModal(false)} notify={notify} pmUser={pmUser} />, document.body)}
      {selectedProject && createPortal(<ProjectDetailModal project={selectedProject} changeRequests={changeRequests} onClose={() => setSelectedProject(null)} notify={notify} />, document.body)}
      
      {/* Notification Popup */}
      {showNotifications && totalNotifications > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 400, overflow: 'hidden' }}>
            <div style={{ background: OGLOW, borderBottom: '1px solid ' + OBORDER, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🔔</div>
              <h2 style={{ color: WHITE, fontSize: 22, fontWeight: 700, margin: 0 }}>Notifications</h2>
              <p style={{ color: GRAY, fontSize: 14, marginTop: 8 }}>You have {totalNotifications} items requiring attention</p>
            </div>
            <div style={{ padding: 20 }}>
              {pendingOrders > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: CARD, border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 28 }}>📦</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: WHITE, fontWeight: 600 }}>{pendingOrders} New Order{pendingOrders > 1 ? 's' : ''}</div>
                    <div style={{ color: '#f59e0b', fontSize: 15 }}>Pending review</div>
                  </div>
                </div>
              )}
              {pendingChanges > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: CARD, border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 28 }}>🔄</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: WHITE, fontWeight: 600 }}>{pendingChanges} Change Request{pendingChanges > 1 ? 's' : ''}</div>
                    <div style={{ color: '#8b5cf6', fontSize: 15 }}>Awaiting approval</div>
                  </div>
                </div>
              )}
              {overdueInvoices > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, background: CARD, border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 28 }}>💰</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: WHITE, fontWeight: 600 }}>{overdueInvoices} Unpaid Invoice{overdueInvoices > 1 ? 's' : ''}</div>
                    <div style={{ color: '#ef4444', fontSize: 15 }}>Payment pending</div>
                  </div>
                </div>
              )}
              <button onClick={() => setShowNotifications(false)} style={{ ...s.cta, width: '100%', marginTop: 8 }}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ projects, allProjects, orders, changeRequests, setActiveTab, setShowCreateModal, isMobile }) {
  const stats = [
    { label: 'Active Projects', value: projects.length, icon: '🏗', color: '#3b82f6' },
    { label: 'Pending Orders', value: orders.filter(o => o.status === 'pending').length, icon: '📦', color: '#f59e0b' },
    { label: 'Change Requests', value: changeRequests.filter(c => c.status === 'pending').length, icon: '🔄', color: '#8b5cf6' },
    { label: 'Completed', value: projects.filter(p => p.stage === 'complete').length, icon: '✅', color: '#22c55e' }
  ];

  // Section Header Component - BIGGER FONTS
  const SectionHeader = ({ icon, title, count, action, actionLabel }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 12, 
      marginBottom: 18,
      paddingBottom: 14,
      borderBottom: '2px solid rgba(255,255,255,0.08)'
    }}>
      <span style={{ fontSize: 24 }}>{icon}</span>
      <span style={{ color: WHITE, fontSize: 20, fontWeight: 700, flex: 1 }}>{title}</span>
      {count !== undefined && (
        <span style={{ 
          background: count > 0 ? O : 'rgba(255,255,255,0.1)', 
          color: count > 0 ? BG : GRAY, 
          fontSize: 14, 
          fontWeight: 700, 
          padding: '5px 12px', 
          borderRadius: 20,
          minWidth: 28,
          textAlign: 'center'
        }}>{count}</span>
      )}
      {action && (
        <button onClick={action} style={{ background: 'none', border: 'none', color: O, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{actionLabel || 'View All →'}</button>
      )}
    </div>
  );

  // Orders in quotation process - exclude approved/rejected quotations
  const quotationOrders = orders.filter(o => o.status === 'approved' && typeof o.quoteStage === 'number' && o.quotationStatus !== 'approved' && o.quotationStatus !== 'rejected');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 16 : 0, marginBottom: 28 }}>
        <div>
          <h1 style={{ color: WHITE, fontSize: isMobile ? 24 : 28, fontWeight: 700, margin: 0 }}>Dashboard</h1>
          <p style={{ color: GRAY, fontSize: 14, marginTop: 4 }}>Welcome back, Admin</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} style={{ ...s.cta, fontSize: 14, padding: isMobile ? '12px 20px' : '14px 24px', width: isMobile ? '100%' : 'auto' }}>+ New Project</button>
      </div>

      {/* Stats Overview */}
      <div style={{ marginBottom: 28 }}>
        <SectionHeader icon="📊" title="Overview" />
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: isMobile ? 12 : 16 }}>
          {stats.map((stat, i) => (
            <div key={i} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: isMobile ? 16 : 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: isMobile ? 18 : 22 }}>{stat.icon}</span>
                <span style={{ color: GRAY, fontSize: isMobile ? 11 : 13, fontWeight: 600 }}>{stat.label}</span>
              </div>
              <div style={{ color: stat.color, fontSize: isMobile ? 28 : 32, fontWeight: 700 }}>{stat.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Quotation Workflow Section */}
      {quotationOrders.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <SectionHeader icon="📋" title="Quotation Workflow" count={quotationOrders.length} action={() => setActiveTab('orders')} />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {quotationOrders.slice(0, 4).map(order => {
              const stage = QUOTE_STAGES[order.quoteStage] || QUOTE_STAGES[0];
              return (
                <div key={order.id} onClick={() => setActiveTab('orders')} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: 16, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{order.category}</div>
                      <div style={{ color: GRAY, fontSize: 14 }}>{order.userName}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: stage.color + '20', padding: '4px 10px', borderRadius: 20 }}>
                      <span style={{ fontSize: 14 }}>{stage.icon}</span>
                      <span style={{ color: stage.color, fontSize: 15, fontWeight: 600 }}>{stage.name}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {QUOTE_STAGES.map((s, idx) => (
                      <div key={idx} style={{ flex: 1, height: 4, borderRadius: 2, background: order.quoteStage >= idx ? s.color : 'rgba(255,255,255,0.08)' }} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 20 : 24 }}>
        {/* Recent Projects */}
        <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: isMobile ? 16 : 20 }}>
          <SectionHeader icon="🏗" title="Recent Projects" count={projects.length} action={() => setActiveTab('projects')} />
          {projects.slice(0, 5).map(project => (
            <div key={project.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.name}</div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 2 }}>{project.clientName}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12 }}>
                <div style={{ width: 50, height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${project.progress || 0}%`, background: O, borderRadius: 3 }} />
                </div>
                <span style={{ color: O, fontSize: 14, fontWeight: 700, minWidth: 32 }}>{project.progress || 0}%</span>
              </div>
            </div>
          ))}
          {projects.length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 20 }}>No projects yet</div>}
        </div>

        {/* Pending Orders */}
        <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: isMobile ? 16 : 20 }}>
          <SectionHeader icon="📦" title="Pending Orders" count={orders.filter(o => o.status === 'pending').length} action={() => setActiveTab('orders')} />
          {orders.filter(o => o.status === 'pending').slice(0, 5).map(order => (
            <div key={order.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.category || 'New Order'}</div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 2 }}>{order.userName} • {order.type}</div>
              </div>
              <div style={{ padding: '5px 12px', borderRadius: 100, fontSize: 15, fontWeight: 600, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', marginLeft: 8 }}>Pending</div>
            </div>
          ))}
          {orders.filter(o => o.status === 'pending').length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 20 }}>No pending orders</div>}
        </div>
      </div>

      {/* Change Requests */}
      {changeRequests.filter(c => c.status === 'pending').length > 0 && (
        <div style={{ marginTop: 24, background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: isMobile ? 16 : 20 }}>
          <SectionHeader icon="🔄" title="Pending Change Requests" count={changeRequests.filter(c => c.status === 'pending').length} action={() => setActiveTab('changes')} />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {changeRequests.filter(c => c.status === 'pending').slice(0, 4).map(cr => (
              <div key={cr.id} style={{ background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, padding: 14 }}>
                <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{cr.title}</div>
                <div style={{ color: GRAY, fontSize: 14 }}>{cr.projectName} • {cr.requestedBy}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client Feedback */}
      {allProjects && allProjects.filter(p => p.feedbackSubmitted).length > 0 && (
        <div style={{ marginTop: 24, background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: isMobile ? 16 : 20 }}>
          <SectionHeader icon="⭐" title="Client Feedback" count={allProjects.filter(p => p.feedbackSubmitted).length} />
          {allProjects.filter(p => p.feedbackSubmitted).map(p => (
            <div key={p.id} style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div>
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 700 }}>{p.name}</div>
                  <div style={{ color: GRAY, fontSize: 13 }}>{p.clientName}</div>
                </div>
                <div style={{ color: '#f59e0b', fontSize: 18, fontWeight: 700 }}>{'★'.repeat(p.surveyRating || 0)}{'☆'.repeat(5 - (p.surveyRating || 0))}</div>
              </div>
              {p.feedbackComment && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
                  <div style={{ color: GRAY, fontSize: 11, fontWeight: 600, marginBottom: 3 }}>COMMENT</div>
                  <div style={{ color: WHITE, fontSize: 14, lineHeight: 1.5 }}>"{p.feedbackComment}"</div>
                </div>
              )}
              <div style={{ color: GRAY, fontSize: 12, marginTop: 6 }}>{p.feedbackDate?.toDate?.()?.toLocaleDateString?.() || ''}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminProjects({ projects, setSelectedProject, setShowCreateModal, notify, isMobile }) {
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 16 : 0, marginBottom: 24 }}>
        <div>
          <h1 style={{ color: WHITE, fontSize: isMobile ? 24 : 28, fontWeight: 700, margin: 0 }}>Projects</h1>
          <p style={{ color: GRAY, fontSize: 14, marginTop: 4 }}>{projects.length} active projects</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} style={{ ...s.cta, fontSize: 14, padding: isMobile ? '12px 20px' : '14px 24px', width: isMobile ? '100%' : 'auto' }}>+ New Project</button>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {projects.map(project => (
          <div key={project.id} onClick={() => setSelectedProject(project)} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: isMobile ? 16 : 24, cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <h3 style={{ color: WHITE, fontSize: isMobile ? 16 : 18, fontWeight: 700, margin: 0 }}>{project.name}</h3>
                  <span style={{ padding: '3px 8px', borderRadius: 100, fontSize: 14, fontWeight: 600, background: project.priority === 'urgent' ? 'rgba(239,68,68,0.1)' : OGLOW, color: project.priority === 'urgent' ? '#ef4444' : O, textTransform: 'uppercase' }}>{project.priority || 'medium'}</span>
                </div>
                <div style={{ color: GRAY, fontSize: 15 }}>{project.clientName} • {project.type}</div>
              </div>
              <div style={{ textAlign: 'right', marginLeft: 12 }}>
                <div style={{ color: O, fontSize: isMobile ? 20 : 24, fontWeight: 700 }}>{project.progress || 0}%</div>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${project.progress || 0}%`, background: O, borderRadius: 3 }} />
              </div>
            </div>
            {!isMobile && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {PROJECT_STAGES.map(stage => (
                  <span key={stage.id} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 14, fontWeight: 600, background: project.stage === stage.id ? stage.color + '20' : 'rgba(255,255,255,0.02)', color: project.stage === stage.id ? stage.color : 'rgba(255,255,255,0.3)', border: project.stage === stage.id ? '1px solid ' + stage.color + '40' : '1px solid transparent' }}>{stage.label}</span>
                ))}
              </div>
            )}
            {project.progress >= 100 && (
              <button onClick={async (e) => {
                e.stopPropagation();
                if (!window.confirm('Complete and archive "' + project.name + '"? A thank you note will be sent to the client.')) return;
                try {
                  await updateDoc(doc(db, 'projects', project.id), { archived: true, stage: 'complete', progress: 100, updatedAt: serverTimestamp() });
                  if (project.clientEmail) {
                    createPushNotification(project.clientEmail, 'project_complete', 'Project Completed! 🎉', 'Thank you for choosing ImpressaBuild! Your project "' + project.name + '" has been completed and delivered. We look forward to working with you again.', project.name);
                  }
                  // Open WhatsApp
                  const phone = (project.clientPhone || '').replace(/^0/, '966').replace(/[^0-9]/g, '');
                  const msg = `Dear ${project.clientName || 'Valued Client'},\n\n🎉 Congratulations!\n\nYour project *"${project.name}"* has been completed and delivered!\n\nThank you for choosing ImpressaBuild. We truly appreciate your trust in our work and we look forward to partnering with you again.\n\n⭐ If you have any feedback, we'd love to hear from you.\n\nBest regards,\nImpressaBuild Team`;
                  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
                  notify('Project completed & archived!');
                } catch (err) { notify('Error'); }
              }} style={{ marginTop: 12, width: '100%', padding: '14px 20px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 12, color: '#22c55e', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                ✅ Complete & Archive — Send Thank You
              </button>
            )}
          </div>
        ))}
        {projects.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: GRAY }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏗</div>
            <div style={{ fontSize: 18, marginBottom: 8 }}>No projects yet</div>
            <button onClick={() => setShowCreateModal(true)} style={{ ...s.cta, marginTop: 16 }}>Create Your First Project</button>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminOrders({ orders, notify }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [orderQuoteAmount, setOrderQuoteAmount] = useState('');
  const [firstInvoicePercent, setFirstInvoicePercent] = useState('50');
  
  // Upload quotation file to an order
  const uploadOrderQuotation = async (orderId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { notify('File too large. Max 10MB'); return; }
    setUploadingQuote(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/order-quotations');
      const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.secure_url) {
        const updateData = {
          quotationFileUrl: data.secure_url,
          quotationFileName: file.name,
          quotationStatus: 'pending_approval',
          updatedAt: serverTimestamp()
        };
        if (orderQuoteAmount) updateData.quotationAmount = parseFloat(orderQuoteAmount) || 0;
        updateData.firstInvoicePercent = parseFloat(firstInvoicePercent) || 50;
        await updateDoc(doc(db, 'orders', orderId), updateData);
        const order = orders.find(o => o.id === orderId);
        if (order) {
          setSelectedOrder({ ...order, quotationFileUrl: data.secure_url, quotationFileName: file.name, quotationStatus: 'pending_approval', quotationAmount: parseFloat(orderQuoteAmount) || 0 });
          createPushNotification(order.userEmail, 'quotation', 'Quotation Ready for Approval', 'Your quotation for "' + order.category + ' - ' + order.type + '" is ready. Please review and approve it on your portal.', '');
        }
        notify('Quotation uploaded & sent to client!');
        setOrderQuoteAmount('');
      }
    } catch (err) { console.error(err); notify('Upload failed'); }
    setUploadingQuote(false);
  };

  const updateOrderStatus = async (orderId, status, quoteStage = 0) => {
    try {
      const updateData = { 
        status, 
        updatedAt: serverTimestamp()
      };
      // When approved, set initial quote stage and add notification
      if (status === 'approved') {
        updateData.quoteStage = 0;
        updateData.quoteHistory = [{
          stage: 0,
          timestamp: new Date().toISOString(),
          note: 'Order received and approved'
        }];
        updateData.clientNotification = 'Your order has been approved! We will be working on your request and will send you a quotation soon.';
        updateData.notificationRead = false;
      }
      await updateDoc(doc(db, 'orders', orderId), updateData);
      notify(`Order ${status}`);
      // Push notification to client
      const targetOrder = orders.find(o => o.id === orderId);
      if (targetOrder && targetOrder.userEmail) {
        const orderName = targetOrder.projectName || (targetOrder.category + ' - ' + targetOrder.type);
        createPushNotification(
          targetOrder.userEmail,
          'order_update',
          status === 'approved' ? 'Order Approved!' : 'Order ' + status.charAt(0).toUpperCase() + status.slice(1),
          status === 'approved' ? 'Great news! We will be working on your request "' + orderName + '". A quotation will be sent to your portal soon.' : 'Your order for "' + orderName + '" has been updated to: ' + status,
          orderName
        );
      }
      setSelectedOrder(null);
    } catch (err) { notify('Error updating order'); }
  };

  const updateQuoteStage = async (orderId, newStage, note = '') => {
    try {
      const order = orders.find(o => o.id === orderId);
      const history = order.quoteHistory || [];
      history.push({
        stage: newStage,
        timestamp: new Date().toISOString(),
        note: note || QUOTE_STAGES[newStage].name
      });
      
      // Set client notification based on stage
      let clientNotification = '';
      if (newStage === 1) clientNotification = 'Design phase has started for your project.';
      else if (newStage === 2) clientNotification = 'We are working on BOQ & Costing for your project.';
      else if (newStage === 3) clientNotification = 'Your quotation is being prepared.';
      else if (newStage === 4) clientNotification = 'Your quotation is in final review.';
      else if (newStage === 5) clientNotification = '🎉 Your quotation is ready! Please check your portal to review and approve.';
      
      await updateDoc(doc(db, 'orders', orderId), { 
        quoteStage: newStage, 
        quoteHistory: history,
        clientNotification,
        notificationRead: false,
        updatedAt: serverTimestamp() 
      });
      notify(`Updated to: ${QUOTE_STAGES[newStage].name}`);
      // Push notification to client
      if (order.userEmail && clientNotification) {
        createPushNotification(
          order.userEmail,
          'order_update',
          'Quotation Update: ' + QUOTE_STAGES[newStage].name,
          clientNotification,
          ''
        );
      }
      // Refresh selected order
      setSelectedOrder({ ...order, quoteStage: newStage, quoteHistory: history });
    } catch (err) { notify('Error updating stage'); }
  };

  // Section Header Component - BIGGER FONTS
  const SectionHeader = ({ icon, title, count }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 12, 
      marginBottom: 18,
      paddingBottom: 14,
      borderBottom: '2px solid rgba(255,255,255,0.08)'
    }}>
      <span style={{ fontSize: 24 }}>{icon}</span>
      <span style={{ color: WHITE, fontSize: 20, fontWeight: 700, flex: 1 }}>{title}</span>
      {count !== undefined && count > 0 && (
        <span style={{ 
          background: O, 
          color: BG, 
          fontSize: 14, 
          fontWeight: 700, 
          padding: '5px 12px', 
          borderRadius: 20 
        }}>{count}</span>
      )}
    </div>
  );

  const [orderView, setOrderView] = useState('active');

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const approvedOrders = orders.filter(o => o.status === 'approved' && o.quotationStatus !== 'approved');
  const rejectedOrders = orders.filter(o => o.status === 'rejected');
  const archivedOrders = orders.filter(o => o.quotationStatus === 'approved' || o.quotationStatus === 'rejected');

  return (
    <div>
      <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 16 }}>Orders</h1>
      
      {/* Active / Archive Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button onClick={() => setOrderView('active')} style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, background: orderView === 'active' ? O : CARD, color: orderView === 'active' ? BG : WHITE, border: '1px solid ' + (orderView === 'active' ? O : 'rgba(255,255,255,0.1)') }}>
          Active ({pendingOrders.length + approvedOrders.length})
        </button>
        <button onClick={() => setOrderView('archive')} style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, background: orderView === 'archive' ? O : CARD, color: orderView === 'archive' ? BG : WHITE, border: '1px solid ' + (orderView === 'archive' ? O : 'rgba(255,255,255,0.1)') }}>
          📁 Archive ({archivedOrders.length})
        </button>
        {rejectedOrders.length > 0 && (
          <button onClick={() => setOrderView('rejected')} style={{ padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, background: orderView === 'rejected' ? O : CARD, color: orderView === 'rejected' ? BG : WHITE, border: '1px solid ' + (orderView === 'rejected' ? O : 'rgba(255,255,255,0.1)') }}>
            Rejected ({rejectedOrders.length})
          </button>
        )}
      </div>

      {orderView === 'active' && (<>
      {/* Pending Orders Section */}
      {pendingOrders.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <SectionHeader icon="⏳" title="Pending Approval" count={pendingOrders.length} />
          {pendingOrders.map(order => (
            <div key={order.id} style={{ background: CARD, border: '1px solid rgba(245,158,11,0.2)', borderRadius: 16, padding: 16, marginBottom: 12, cursor: 'pointer' }} onClick={() => setSelectedOrder(order)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <h3 style={{ color: WHITE, fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{order.category}</h3>
                  <p style={{ color: GRAY, fontSize: 14, margin: '0 0 4px' }}>{order.type} • {order.location}</p>
                  <p style={{ color: GRAY, fontSize: 15 }}>From: {order.userName} ({order.company})</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ padding: '6px 14px', borderRadius: 100, fontSize: 14, fontWeight: 600, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>Pending</span>
                  <span style={{ color: O, fontSize: 18 }}>→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* In Progress (Quotation Workflow) Section */}
      {approvedOrders.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <SectionHeader icon="📋" title="Quotation Workflow" count={approvedOrders.length} />
          {approvedOrders.map(order => (
            <div key={order.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 16, marginBottom: 12, cursor: 'pointer' }} onClick={() => setSelectedOrder(order)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <h3 style={{ color: WHITE, fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{order.category}</h3>
                  <p style={{ color: GRAY, fontSize: 14, margin: '0 0 4px' }}>{order.type} • {order.location}</p>
                  <p style={{ color: GRAY, fontSize: 15 }}>From: {order.userName} ({order.company})</p>
                  {typeof order.quoteStage === 'number' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, background: 'rgba(255,255,255,0.03)', padding: '6px 10px', borderRadius: 8, width: 'fit-content' }}>
                      <span style={{ fontSize: 14 }}>{QUOTE_STAGES[order.quoteStage]?.icon}</span>
                      <span style={{ color: QUOTE_STAGES[order.quoteStage]?.color, fontSize: 15, fontWeight: 600 }}>{QUOTE_STAGES[order.quoteStage]?.name}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ padding: '6px 14px', borderRadius: 100, fontSize: 13, fontWeight: 600, background: order.quotationStatus === 'pending_approval' ? 'rgba(245,158,11,0.1)' : order.quotationFileUrl ? 'rgba(59,130,246,0.1)' : 'rgba(139,92,246,0.1)', color: order.quotationStatus === 'pending_approval' ? '#f59e0b' : order.quotationFileUrl ? '#3b82f6' : '#8b5cf6' }}>{order.quotationStatus === 'pending_approval' ? 'Awaiting Client' : order.quotationFileUrl ? 'Quote Sent' : 'In Progress'}</span>
                  <span style={{ color: O, fontSize: 18 }}>→</span>
                </div>
              </div>
              {/* Mini Progress Bar */}
              {typeof order.quoteStage === 'number' && (
                <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
                  {QUOTE_STAGES.map((stage, idx) => (
                    <div key={idx} style={{ flex: 1, height: 4, borderRadius: 2, background: order.quoteStage >= idx ? stage.color : 'rgba(255,255,255,0.08)' }} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {pendingOrders.length === 0 && approvedOrders.length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No active orders</div>}
      </>)}

      {/* Archive View */}
      {orderView === 'archive' && (
        <div>
          {archivedOrders.length > 0 ? archivedOrders.map(order => (
            <div key={order.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 16, marginBottom: 12, cursor: 'pointer' }} onClick={() => setSelectedOrder(order)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <h3 style={{ color: WHITE, fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{order.projectName || order.category}</h3>
                  <p style={{ color: GRAY, fontSize: 14, margin: '0 0 4px' }}>{order.type} • {order.location}</p>
                  <p style={{ color: GRAY, fontSize: 15 }}>From: {order.userName} ({order.company})</p>
                  {order.quotationAmount > 0 && <div style={{ color: O, fontSize: 14, fontWeight: 700, marginTop: 4 }}>SAR {order.quotationAmount.toLocaleString()}</div>}
                </div>
                <span style={{ padding: '6px 14px', borderRadius: 100, fontSize: 14, fontWeight: 600, background: order.quotationStatus === 'approved' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: order.quotationStatus === 'approved' ? '#22c55e' : '#ef4444' }}>{order.quotationStatus === 'approved' ? 'Quote Approved' : 'Quote Rejected'}</span>
              </div>
            </div>
          )) : <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No archived orders</div>}
        </div>
      )}

      {/* Rejected View */}
      {orderView === 'rejected' && (
        <div>
          {rejectedOrders.map(order => (
            <div key={order.id} style={{ background: CARD, border: '1px solid rgba(239,68,68,0.2)', borderRadius: 16, padding: 16, marginBottom: 12, cursor: 'pointer', opacity: 0.7 }} onClick={() => setSelectedOrder(order)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <h3 style={{ color: WHITE, fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{order.category}</h3>
                  <p style={{ color: GRAY, fontSize: 14, margin: '0 0 4px' }}>{order.type} • {order.location}</p>
                  <p style={{ color: GRAY, fontSize: 15 }}>From: {order.userName} ({order.company})</p>
                </div>
                <span style={{ padding: '6px 14px', borderRadius: 100, fontSize: 14, fontWeight: 600, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>Rejected</span>
              </div>
            </div>
          ))}
          {rejectedOrders.length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No rejected orders</div>}
        </div>
      )}
      
      {/* Order Detail Modal */}
      {selectedOrder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 550, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: 24, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: WHITE, fontSize: 22, fontWeight: 700, margin: 0 }}>Order Details</h2>
              <button onClick={() => setSelectedOrder(null)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 28, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 24 }}>
              {/* Status Badge */}
              {(() => {
                const isArchived = selectedOrder.quotationStatus === 'approved' || selectedOrder.quotationStatus === 'rejected';
                return (<>
              <div style={{ marginBottom: 20 }}>
                <span style={{ padding: '8px 16px', borderRadius: 100, fontSize: 14, fontWeight: 700, background: selectedOrder.status === 'pending' ? 'rgba(245,158,11,0.15)' : selectedOrder.quotationStatus === 'pending_approval' ? 'rgba(245,158,11,0.15)' : selectedOrder.quotationStatus === 'approved' ? 'rgba(34,197,94,0.15)' : selectedOrder.quotationStatus === 'rejected' ? 'rgba(239,68,68,0.15)' : selectedOrder.quotationFileUrl ? 'rgba(59,130,246,0.15)' : 'rgba(139,92,246,0.15)', color: selectedOrder.status === 'pending' ? '#f59e0b' : selectedOrder.quotationStatus === 'pending_approval' ? '#f59e0b' : selectedOrder.quotationStatus === 'approved' ? '#22c55e' : selectedOrder.quotationStatus === 'rejected' ? '#ef4444' : selectedOrder.quotationFileUrl ? '#3b82f6' : '#8b5cf6', textTransform: 'uppercase' }}>{selectedOrder.status === 'pending' ? 'PENDING' : selectedOrder.quotationStatus === 'pending_approval' ? 'AWAITING CLIENT APPROVAL' : selectedOrder.quotationStatus === 'approved' ? 'CLIENT APPROVED' : selectedOrder.quotationStatus === 'rejected' ? 'CLIENT REJECTED' : selectedOrder.quotationFileUrl ? 'QUOTATION SENT' : 'IN PROGRESS'}</span>
                {isArchived && <span style={{ padding: '8px 16px', borderRadius: 100, fontSize: 14, fontWeight: 700, marginLeft: 8, background: selectedOrder.quotationStatus === 'approved' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: selectedOrder.quotationStatus === 'approved' ? '#22c55e' : '#ef4444' }}>Quote {selectedOrder.quotationStatus === 'approved' ? 'Approved' : 'Rejected'}</span>}
              </div>
              
              {/* Quotation Workflow - Only for approved orders that are NOT archived */}
              {selectedOrder.status === 'approved' && !isArchived && (
                <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 16 }}>QUOTATION WORKFLOW</div>
                  
                  {/* Progress Bar */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    {QUOTE_STAGES.map((stage, idx) => (
                      <div key={idx} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                        <div 
                          onClick={() => updateQuoteStage(selectedOrder.id, idx)}
                          style={{ 
                            width: 32, height: 32, borderRadius: '50%', 
                            background: (selectedOrder.quoteStage || 0) >= idx ? stage.color : 'rgba(255,255,255,0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: 14, transition: 'all 0.2s'
                          }}
                          title={stage.name}
                        >
                          {stage.icon}
                        </div>
                        {idx < QUOTE_STAGES.length - 1 && (
                          <div style={{ flex: 1, height: 3, background: (selectedOrder.quoteStage || 0) > idx ? stage.color : 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Current Stage Label */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 0', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
                    <span style={{ fontSize: 20 }}>{QUOTE_STAGES[selectedOrder.quoteStage || 0]?.icon}</span>
                    <span style={{ color: QUOTE_STAGES[selectedOrder.quoteStage || 0]?.color, fontSize: 15, fontWeight: 700 }}>{QUOTE_STAGES[selectedOrder.quoteStage || 0]?.name}</span>
                  </div>
                  
                  {/* Stage History */}
                  {selectedOrder.quoteHistory && selectedOrder.quoteHistory.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ color: GRAY, fontSize: 15, fontWeight: 600, marginBottom: 8 }}>HISTORY</div>
                      {selectedOrder.quoteHistory.slice().reverse().map((h, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < selectedOrder.quoteHistory.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                          <span style={{ fontSize: 14 }}>{QUOTE_STAGES[h.stage]?.icon}</span>
                          <span style={{ color: WHITE, fontSize: 14, flex: 1 }}>{h.note || QUOTE_STAGES[h.stage]?.name}</span>
                          <span style={{ color: GRAY, fontSize: 14 }}>{new Date(h.timestamp).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Quotation File Upload & Status - only for approved orders that are NOT archived */}
              {selectedOrder.status === 'approved' && !isArchived && (
                <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>QUOTATION DOCUMENT</div>
                  
                  {/* Show uploaded quotation */}
                  {selectedOrder.quotationFileUrl ? (
                    <div>
                      <a href={googleViewerUrl(selectedOrder.quotationFileUrl)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, textDecoration: 'none', marginBottom: 12 }}>
                        <span style={{ fontSize: 20 }}>📄</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.quotationFileName || 'Quotation'}</div>
                          <div style={{ color: '#3b82f6', fontSize: 13 }}>Click to view</div>
                          {selectedOrder.quotationAmount > 0 && <div style={{ color: O, fontSize: 14, fontWeight: 700, marginTop: 2 }}>SAR {selectedOrder.quotationAmount.toLocaleString()}</div>}
                        </div>
                        <span style={{ color: '#3b82f6', fontSize: 18 }}>↗</span>
                      </a>
                      
                      {/* Send quotation via WhatsApp / Email */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <button onClick={() => {
                          const phone = selectedOrder.clientPhone || '';
                          const formattedPhone = phone.replace(/^0/, '966').replace(/[^0-9]/g, '');
                          const msg = `Hello ${selectedOrder.userName || ''},\n\n📄 Your quotation for *${selectedOrder.category} - ${selectedOrder.type}* is ready!\n\n📎 View & download: ${selectedOrder.quotationFileUrl}\n\n✅ Please review and approve it in your portal:\n🔗 https://app.impressabuild.com\n\nBest regards,\nImpressaBuild Team`;
                          window.open(`https://wa.me/${formattedPhone}?text=${encodeURIComponent(msg)}`, '_blank');
                        }} style={{ flex: 1, padding: '10px 14px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 10, color: '#25D366', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          📱 WhatsApp
                        </button>
                        <button onClick={() => {
                          const subject = `ImpressaBuild - Quotation Ready: ${selectedOrder.category} - ${selectedOrder.type}`;
                          const body = `Dear ${selectedOrder.userName || 'Client'},\n\nYour quotation for ${selectedOrder.category} - ${selectedOrder.type} is ready for review.\n\nView & download: ${selectedOrder.quotationFileUrl}\n\nPlease log in to your portal to approve or reject:\nhttps://app.impressabuild.com\n\nBest regards,\nImpressaBuild Team`;
                          window.open(`mailto:${selectedOrder.userEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                        }} style={{ flex: 1, padding: '10px 14px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, color: '#3b82f6', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          ✉️ Email
                        </button>
                      </div>
                      
                      {/* Approval status */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: selectedOrder.quotationStatus === 'approved' ? 'rgba(34,197,94,0.1)' : selectedOrder.quotationStatus === 'rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)', border: '1px solid ' + (selectedOrder.quotationStatus === 'approved' ? 'rgba(34,197,94,0.3)' : selectedOrder.quotationStatus === 'rejected' ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)') }}>
                        <span style={{ fontSize: 16 }}>{selectedOrder.quotationStatus === 'approved' ? '✅' : selectedOrder.quotationStatus === 'rejected' ? '❌' : '⏳'}</span>
                        <span style={{ color: selectedOrder.quotationStatus === 'approved' ? '#22c55e' : selectedOrder.quotationStatus === 'rejected' ? '#ef4444' : '#f59e0b', fontSize: 14, fontWeight: 600 }}>
                          {selectedOrder.quotationStatus === 'approved' ? 'Client Approved' : selectedOrder.quotationStatus === 'rejected' ? 'Client Rejected' : 'Waiting for Client Approval'}
                        </span>
                      </div>
                      {selectedOrder.clientComment && (
                        <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)' }}>
                          <div style={{ color: GRAY, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>CLIENT COMMENT</div>
                          <div style={{ color: WHITE, fontSize: 14 }}>{selectedOrder.clientComment}</div>
                        </div>
                      )}
                      
                      {/* Re-upload option */}
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12, padding: '10px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, color: GRAY, fontSize: 13, cursor: 'pointer' }}>
                        <span>📎</span> Replace Quotation
                        <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" onChange={(e) => uploadOrderQuotation(selectedOrder.id, e)} style={{ display: 'none' }} disabled={uploadingQuote} />
                      </label>
                    </div>
                  ) : (
                    <div>
                      <p style={{ color: GRAY, fontSize: 14, marginBottom: 12 }}>Upload a quotation for the client to approve.</p>
                      <div style={{ marginBottom: 12 }}>
                        <input type="number" placeholder="Total Quotation Amount (SAR)" value={orderQuoteAmount} onChange={e => setOrderQuoteAmount(e.target.value)} style={{ ...s.input, fontSize: 14, padding: '10px 14px', marginBottom: 0 }} />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ color: GRAY, fontSize: 13, marginBottom: 6 }}>Down Payment % (of total amount)</div>
                        <input type="number" min="1" max="100" placeholder="e.g. 50" value={firstInvoicePercent} onChange={e => setFirstInvoicePercent(e.target.value)} style={{ ...s.input, fontSize: 14, padding: '10px 14px', marginBottom: 0 }} />
                        {orderQuoteAmount && firstInvoicePercent && (
                          <div style={{ color: O, fontSize: 13, fontWeight: 600, marginTop: 6 }}>Down Payment: SAR {((parseFloat(orderQuoteAmount) || 0) * (parseFloat(firstInvoicePercent) || 0) / 100).toLocaleString()}</div>
                        )}
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '16px 20px', background: OGLOW, border: '1px dashed ' + OBORDER, borderRadius: 12, cursor: 'pointer', color: O, fontWeight: 600, fontSize: 15 }}>
                        {uploadingQuote ? '⏳ Uploading...' : '📎 Upload Quotation File'}
                        <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" onChange={(e) => uploadOrderQuotation(selectedOrder.id, e)} style={{ display: 'none' }} disabled={uploadingQuote} />
                      </label>
                    </div>
                  )}
                </div>
              )}
              
              {/* Order Info */}
              <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>ORDER INFO</div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div><span style={{ color: GRAY, fontSize: 15 }}>Category: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.category}</span></div>
                  <div><span style={{ color: GRAY, fontSize: 15 }}>Type: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.type}</span></div>
                  <div><span style={{ color: GRAY, fontSize: 15 }}>Location: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.location}</span></div>
                  {selectedOrder.area && <div><span style={{ color: GRAY, fontSize: 15 }}>Area: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.area}</span></div>}
                  {selectedOrder.budget && <div><span style={{ color: GRAY, fontSize: 15 }}>Budget: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.budget}</span></div>}
                  {selectedOrder.timeline && <div><span style={{ color: GRAY, fontSize: 15 }}>Timeline: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.timeline}</span></div>}
                </div>
              </div>
              
              {/* Client Info */}
              <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>CLIENT INFO</div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div><span style={{ color: GRAY, fontSize: 15 }}>Name: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.userName}</span></div>
                  <div><span style={{ color: GRAY, fontSize: 15 }}>Company: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.company}</span></div>
                  <div><span style={{ color: GRAY, fontSize: 15 }}>Email: </span><span style={{ color: O, fontSize: 14, fontWeight: 600 }}>{selectedOrder.userEmail}</span></div>
                </div>
              </div>
              
              {/* Details */}
              {selectedOrder.details && (
                <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>PROJECT DETAILS</div>
                  <p style={{ color: WHITE, fontSize: 14, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{selectedOrder.details}</p>
                </div>
              )}
              
              {/* Attachments */}
              {(selectedOrder.attachmentFile || selectedOrder.attachmentLink) && (
                <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>ATTACHMENTS</div>
                  
                  {/* File Attachment */}
                  {selectedOrder.attachmentFile && selectedOrder.attachmentFileName && (
                    <a 
                      href={selectedOrder.attachmentFile} 
                      download={selectedOrder.attachmentFileName}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, textDecoration: 'none', marginBottom: selectedOrder.attachmentLink ? 12 : 0 }}
                    >
                      <span style={{ fontSize: 20 }}>📄</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.attachmentFileName}</div>
                        <div style={{ color: '#3b82f6', fontSize: 14, marginTop: 2 }}>Click to download</div>
                      </div>
                      <span style={{ color: '#3b82f6', fontSize: 18 }}>⬇</span>
                    </a>
                  )}
                  
                  {/* Link Attachment */}
                  {selectedOrder.attachmentLink && (
                    <a href={selectedOrder.attachmentLink} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 10, textDecoration: 'none' }}>
                      <span style={{ fontSize: 20 }}>🔗</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>External Link</div>
                        <div style={{ color: O, fontSize: 14, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedOrder.attachmentLink}</div>
                      </div>
                      <span style={{ color: O, fontSize: 18 }}>→</span>
                    </a>
                  )}
                </div>
              )}
              
              {/* Action Buttons */}
              {selectedOrder.status === 'pending' && (
                <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                  <button onClick={() => updateOrderStatus(selectedOrder.id, 'approved')} style={{ flex: 1, padding: '14px 20px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, color: '#22c55e', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: FONT }}>✓ Approve Order</button>
                  <button onClick={() => updateOrderStatus(selectedOrder.id, 'rejected')} style={{ flex: 1, padding: '14px 20px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, color: '#ef4444', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: FONT }}>✗ Reject</button>
                </div>
              )}
              
              {/* Archived quotation info */}
              {isArchived && selectedOrder.quotationFileUrl && (
                <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>QUOTATION</div>
                  <a href={selectedOrder.quotationFileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, textDecoration: 'none' }}>
                    <span style={{ fontSize: 20 }}>📄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedOrder.quotationFileName || 'Quotation'}</div>
                      {selectedOrder.quotationAmount > 0 && <div style={{ color: O, fontSize: 14, fontWeight: 700, marginTop: 2 }}>SAR {selectedOrder.quotationAmount.toLocaleString()}</div>}
                    </div>
                    <span style={{ color: '#3b82f6', fontSize: 18 }}>↗</span>
                  </a>
                </div>
              )}
              
              {/* Partner Tasks - available for any order */}
              <SubTaskManager projectId={selectedOrder.id} projectName={selectedOrder.projectName || selectedOrder.category + ' - ' + selectedOrder.type} notify={notify} />
              </>);
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminChangeRequests({ changeRequests, notify }) {
  const [uploadingCRQuote, setUploadingCRQuote] = useState(null);
  const [crAmount, setCRAmount] = useState('');
  
  const updateCRStatus = async (crId, status) => {
    try {
      await updateDoc(doc(db, 'changeRequests', crId), { status, updatedAt: serverTimestamp() });
      notify(`Change request ${status}`);
    } catch (err) { notify('Error updating'); }
  };

  const uploadCRQuotation = async (crId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCRQuote(crId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/cr-quotations');
      const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.secure_url) {
        const updateData = {
          quotationFileUrl: data.secure_url,
          quotationFileName: file.name,
          quotationStatus: 'pending_approval',
          updatedAt: serverTimestamp()
        };
        if (crAmount) updateData.quotationAmount = parseFloat(crAmount) || 0;
        await updateDoc(doc(db, 'changeRequests', crId), updateData);
        const cr = changeRequests.find(c => c.id === crId);
        if (cr) {
          createPushNotification(cr.userEmail, 'quotation', 'Change Request Quotation Ready', 'A quotation for your change request "' + cr.title + '" on "' + cr.projectName + '" is available on your portal for approval.', cr.projectName);
        }
        notify('Quotation uploaded & sent to client!');
        setCRAmount('');
      }
    } catch (err) { console.error(err); notify('Upload failed'); }
    setUploadingCRQuote(null);
  };

  return (
    <div>
      <h1 style={{ color: WHITE, fontSize: 28, fontWeight: 700, marginBottom: 32 }}>Change Requests</h1>
      {changeRequests.map(cr => (
        <div key={cr.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ color: WHITE, fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{cr.title}</h3>
              <p style={{ color: GRAY, fontSize: 14, margin: '0 0 8px' }}>{cr.description}</p>
              <div style={{ color: GRAY, fontSize: 15 }}>Project: {cr.projectName} • By: {cr.requestedBy}</div>
            </div>
            <span style={{ padding: '6px 14px', borderRadius: 100, fontSize: 14, fontWeight: 600, background: cr.quotationStatus === 'approved' ? 'rgba(34,197,94,0.1)' : cr.quotationStatus === 'pending_approval' ? 'rgba(245,158,11,0.1)' : cr.quotationStatus === 'rejected' ? 'rgba(239,68,68,0.1)' : cr.status === 'pending' ? 'rgba(139,92,246,0.1)' : cr.status === 'approved' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: cr.quotationStatus === 'approved' ? '#22c55e' : cr.quotationStatus === 'pending_approval' ? '#f59e0b' : cr.quotationStatus === 'rejected' ? '#ef4444' : cr.status === 'pending' ? '#8b5cf6' : cr.status === 'approved' ? '#22c55e' : '#ef4444', textTransform: 'capitalize', flexShrink: 0 }}>{cr.quotationStatus === 'approved' ? 'Quote Approved' : cr.quotationStatus === 'pending_approval' ? 'Awaiting Client' : cr.quotationStatus === 'rejected' ? 'Quote Rejected' : cr.status}</span>
          </div>
          
          {/* Client attachment */}
          {cr.attachmentUrl && (
            <div style={{ marginTop: 12 }}>
              <a href={cr.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 10, textDecoration: 'none' }}>
                <span style={{ fontSize: 16 }}>📎</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>{cr.attachmentName || 'Client Attachment'}</div>
                  <div style={{ color: '#8b5cf6', fontSize: 12 }}>From client • Tap to view</div>
                </div>
                <span style={{ color: '#8b5cf6', fontSize: 16 }}>↗</span>
              </a>
            </div>
          )}
          
          {/* Quotation upload section - optional */}
          {cr.status === 'pending' && !cr.quotationFileUrl && !cr.quotationStatus && (
            <div style={{ marginTop: 16, padding: 16, background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>SEND COST FOR CLIENT APPROVAL</div>
              <div style={{ color: GRAY, fontSize: 12, marginBottom: 12 }}>Enter amount and optionally attach a quotation document</div>
              <div style={{ marginBottom: 12 }}>
                <input type="number" placeholder="Amount (SAR)" value={crAmount} onChange={e => setCRAmount(e.target.value)} style={{ ...s.input, fontSize: 14, padding: '10px 14px', marginBottom: 0 }} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', background: OGLOW, border: '1px dashed ' + OBORDER, borderRadius: 10, cursor: 'pointer', color: O, fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
                {uploadingCRQuote === cr.id ? '⏳ Uploading...' : '📎 Attach Quotation (optional)'}
                <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" onChange={(e) => uploadCRQuotation(cr.id, e)} style={{ display: 'none' }} disabled={uploadingCRQuote === cr.id} />
              </label>
              {/* Send cost without file */}
              <button onClick={async () => {
                if (!crAmount) { notify('Please enter an amount'); return; }
                try {
                  await updateDoc(doc(db, 'changeRequests', cr.id), {
                    quotationAmount: parseFloat(crAmount) || 0,
                    quotationStatus: 'pending_approval',
                    updatedAt: serverTimestamp()
                  });
                  createPushNotification(cr.userEmail, 'quotation', 'Change Request Cost Sent', 'A cost of SAR ' + (parseFloat(crAmount) || 0).toLocaleString() + ' for your change request "' + cr.title + '" on "' + cr.projectName + '" is available on your portal for approval.', cr.projectName);
                  notify('Cost sent to client for approval!');
                  setCRAmount('');
                } catch (err) { console.error(err); notify('Error'); }
              }} style={{ width: '100%', padding: '14px 20px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, color: '#22c55e', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: FONT, opacity: crAmount ? 1 : 0.4, pointerEvents: crAmount ? 'auto' : 'none' }}>
                ✓ Send Cost for Approval
              </button>
            </div>
          )}
          
          {/* Show sent cost (no file) waiting for approval */}
          {cr.quotationStatus === 'pending_approval' && !cr.quotationFileUrl && (
            <div style={{ marginTop: 16, padding: 16, background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 18 }}>💰</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>Cost Sent to Client</div>
                  {cr.quotationAmount && <div style={{ color: O, fontSize: 16, fontWeight: 700 }}>SAR {cr.quotationAmount.toLocaleString()}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.1)' }}>
                <span style={{ fontSize: 14 }}>⏳</span>
                <span style={{ color: '#f59e0b', fontSize: 13, fontWeight: 600 }}>Waiting for Client Approval</span>
              </div>
            </div>
          )}
          
          {/* Show uploaded quotation */}
          {cr.quotationFileUrl && (
            <div style={{ marginTop: 16, padding: 16, background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
              <a href={googleViewerUrl(cr.quotationFileUrl)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, textDecoration: 'none', marginBottom: 10 }}>
                <span style={{ fontSize: 18 }}>📄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>{cr.quotationFileName}</div>
                  {cr.quotationAmount && <div style={{ color: O, fontSize: 13 }}>SAR {cr.quotationAmount.toLocaleString()}</div>}
                </div>
                <span style={{ color: '#3b82f6', fontSize: 16 }}>↗</span>
              </a>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: cr.quotationStatus === 'approved' ? 'rgba(34,197,94,0.1)' : cr.quotationStatus === 'rejected' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)' }}>
                <span style={{ fontSize: 14 }}>{cr.quotationStatus === 'approved' ? '✅' : cr.quotationStatus === 'rejected' ? '❌' : '⏳'}</span>
                <span style={{ color: cr.quotationStatus === 'approved' ? '#22c55e' : cr.quotationStatus === 'rejected' ? '#ef4444' : '#f59e0b', fontSize: 13, fontWeight: 600 }}>
                  {cr.quotationStatus === 'approved' ? 'Client Approved' : cr.quotationStatus === 'rejected' ? 'Client Rejected' : 'Waiting for Client Approval'}
                </span>
              </div>
              {cr.clientComment && (
                <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
                  <div style={{ color: GRAY, fontSize: 11, fontWeight: 600, marginBottom: 2 }}>CLIENT COMMENT</div>
                  <div style={{ color: WHITE, fontSize: 13 }}>{cr.clientComment}</div>
                </div>
              )}
              {/* Re-upload */}
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 10, padding: '8px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, color: GRAY, fontSize: 12, cursor: 'pointer' }}>
                📎 Replace Quotation
                <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" onChange={(e) => uploadCRQuotation(cr.id, e)} style={{ display: 'none' }} disabled={uploadingCRQuote === cr.id} />
              </label>
            </div>
          )}
          
          {/* Work Progress - only after client approves */}
          {cr.quotationStatus === 'approved' && (
            <div style={{ marginTop: 16, padding: 16, background: 'rgba(34,197,94,0.05)', borderRadius: 12, border: '1px solid rgba(34,197,94,0.2)' }}>
              <div style={{ color: '#22c55e', fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>WORK PROGRESS</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input type="range" min="0" max="100" value={cr.workProgress || 0} onChange={async (e) => {
                  const newProgress = parseInt(e.target.value);
                  try {
                    await updateDoc(doc(db, 'changeRequests', cr.id), { workProgress: newProgress, updatedAt: serverTimestamp() });
                  } catch (err) { console.error(err); }
                }} style={{ flex: 1, accentColor: '#22c55e' }} />
                <span style={{ color: '#22c55e', fontSize: 16, fontWeight: 700, minWidth: 45 }}>{cr.workProgress || 0}%</span>
              </div>
              <button onClick={async () => {
                try {
                  await updateDoc(doc(db, 'changeRequests', cr.id), { workProgress: cr.workProgress || 0, updatedAt: serverTimestamp() });
                  createPushNotification(cr.userEmail, 'cr_progress', 'Change Order Progress', 'Your change request "' + cr.title + '" for "' + cr.projectName + '" is now at ' + (cr.workProgress || 0) + '% progress.', cr.projectName);
                  notify('Progress updated & client notified');
                } catch (err) { notify('Error'); }
              }} style={{ marginTop: 10, padding: '8px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: '#22c55e', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                Save & Notify Client
              </button>
            </div>
          )}
          
          {/* Reject button - only if no quotation sent yet */}
          {cr.status === 'pending' && !cr.quotationFileUrl && (
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button onClick={() => updateCRStatus(cr.id, 'rejected')} style={{ padding: '10px 20px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>✗ Reject Request</button>
            </div>
          )}
        </div>
      ))}
      {changeRequests.length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No change requests</div>}
    </div>
  );
}

function AdminClients({ projects, notify, pmUser }) {
  const [clients, setClients] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', email: '', company: '', phone: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [whatsAppInvite, setWhatsAppInvite] = useState(null);

  // Get unique clients from projects
  useEffect(() => {
    const clientMap = {};
    projects.forEach(p => {
      if (p.clientEmail && !clientMap[p.clientEmail]) {
        clientMap[p.clientEmail] = {
          name: p.clientName || p.clientEmail.split('@')[0],
          email: p.clientEmail,
          company: p.company || '',
          phone: p.clientPhone || '',
          password: '',
          projectCount: 0,
          projects: []
        };
      }
      if (p.clientEmail && clientMap[p.clientEmail]) {
        clientMap[p.clientEmail].projectCount++;
        clientMap[p.clientEmail].projects.push(p);
      }
    });
    // Also fetch from Firebase Auth users collection if it exists
    const fetchUsers = async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        usersSnap.forEach(doc => {
          const u = doc.data();
          // PM scope: only include users assigned to this PM
          if (pmUser && u.pmEmail !== pmUser.email) {
            if (u.email && clientMap[u.email]) {
              // still merge password/phone for clients visible via projects
              if (u.password) clientMap[u.email].password = u.password;
              if (u.phone && !clientMap[u.email].phone) clientMap[u.email].phone = u.phone;
              if (u.company && !clientMap[u.email].company) clientMap[u.email].company = u.company;
            }
            return;
          }
          if (u.email && !clientMap[u.email]) {
            clientMap[u.email] = {
              name: u.name || u.email.split('@')[0],
              email: u.email,
              company: u.company || '',
              phone: u.phone || '',
              password: u.password || '',
              projectCount: 0,
              projects: []
            };
          } else if (u.email && clientMap[u.email]) {
            if (u.password) clientMap[u.email].password = u.password;
            if (u.phone && !clientMap[u.email].phone) clientMap[u.email].phone = u.phone;
            if (u.company && !clientMap[u.email].company) clientMap[u.email].company = u.company;
          }
        });
      } catch (e) { /* users collection might not exist */ }
      setClients(Object.values(clientMap));
    };
    fetchUsers();
  }, [projects, pmUser]);

  const createClient = async () => {
    if (!newClient.name || !newClient.email || !newClient.password) {
      notify('Name, email and password are required');
      return;
    }
    if (newClient.password.length < 6) {
      notify('Password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      const clientEmail = newClient.email.toLowerCase().trim();
      
      // Ensure we're authenticated before writing
      if (!auth.currentUser) {
        try {
          await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
        } catch (authErr) {
          notify('Auth error. Please try again.');
          setCreating(false);
          return;
        }
      }
      
      // Save to users collection in Firestore using addDoc (auto-generated ID)
      const clientData = {
        name: newClient.name,
        email: clientEmail,
        company: newClient.company,
        phone: newClient.phone,
        password: newClient.password,
        isFirstLogin: true,
        isCompanyAdmin: true,
        ...(pmUser ? { pmEmail: pmUser.email, pmName: pmUser.name || '' } : {}),
        createdAt: serverTimestamp()
      };
      
      // Try addDoc first (auto ID)
      const docRef = await addDoc(collection(db, 'users'), clientData);
      notify('Client created! Login: ' + clientEmail);
      // Save WhatsApp data for manual send
      const savedEmail = newClient.email;
      const savedPassword = newClient.password;
      const savedName = newClient.name;
      const savedPhone = (newClient.phone || '').trim().replace(/^0/, '966').replace(/\+/g, '').replace(/[^0-9]/g, '');
      const waMsg = 'Hello,\nWelcome to ImpressaBuild! Your client portal is ready.\n\n\uD83D\uDD17 Login: https://app.impressabuild.com\n\uD83D\uDCE7 Email: ' + savedEmail + '\n\uD83D\uDD11 Password: ' + savedPassword + '\n\nSelect "Client Portal" to access your project.\n\n\u26A0\uFE0F You will be asked to set a new password on your first login.\n\nFor any questions, contact us at contact@impressabuild.com\n\nBest regards,\nImpressaBuild Team';
      
      setShowAddClient(false);
      setNewClient({ name: '', email: '', company: '', phone: '', password: '' });
      setWhatsAppInvite({ phone: savedPhone, text: waMsg, name: savedName });
    } catch (err) {
      console.error('Create client error:', err);
      alert('CREATION ERROR: ' + (err.code || '') + ' - ' + err.message);
      notify('Error creating client');
    }
    setCreating(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, margin: 0 }}>Clients</h1>
        <button onClick={() => setShowAddClient(true)} style={{ ...s.cta, fontSize: 14, padding: '10px 16px' }}>+ New Client</button>
      </div>

      {clients.length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No clients yet. Create your first client above.</div>}
      
      {clients.map(client => (
        <div key={client.email} onClick={() => setSelectedClient(client)} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 18, marginBottom: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: OGLOW, border: '1px solid ' + OBORDER, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: O, fontWeight: 700 }}>
            {(client.company || client.name).charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: WHITE, fontSize: 17, fontWeight: 700 }}>{client.company || client.name}</div>
          </div>
          <div style={{ color: O, fontSize: 14, fontWeight: 600 }}>{client.projectCount} project(s)</div>
          <span style={{ color: GRAY, fontSize: 18 }}>▶</span>
        </div>
      ))}

      {/* Client Detail Modal */}
      {selectedClient && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }} onClick={() => setSelectedClient(null)}>
          <div style={{ minHeight: '100%', padding: '40px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 450, marginTop: 20, marginBottom: 20 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>Client Details</h2>
                <button onClick={() => setSelectedClient(null)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ padding: 20 }}>
                {/* Client Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: OGLOW, border: '2px solid ' + O, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: O, fontWeight: 700 }}>
                    {(selectedClient.company || selectedClient.name).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ color: WHITE, fontSize: 20, fontWeight: 700 }}>{selectedClient.company || selectedClient.name}</div>
                    <div style={{ color: GRAY, fontSize: 14, marginTop: 2 }}>{selectedClient.name}</div>
                  </div>
                </div>

                <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>CONTACT INFO</div>
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div><span style={{ color: GRAY, fontSize: 14 }}>Contact: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedClient.name}</span></div>
                    <div><span style={{ color: GRAY, fontSize: 14 }}>Email: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedClient.email}</span></div>
                    <div><span style={{ color: GRAY, fontSize: 14 }}>Phone: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedClient.phone || 'Not set'}</span></div>
                    {selectedClient.password && <div><span style={{ color: GRAY, fontSize: 14 }}>Password: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedClient.password}</span></div>}
                  </div>
                </div>

                {/* Projects */}
                <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>PROJECTS ({selectedClient.projects.length})</div>
                  {selectedClient.projects.length > 0 ? selectedClient.projects.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontSize: 16 }}>🏗</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                        <div style={{ color: GRAY, fontSize: 12 }}>{p.type} • {p.stage}</div>
                      </div>
                      <span style={{ color: O, fontSize: 13, fontWeight: 600 }}>{p.progress || 0}%</span>
                    </div>
                  )) : <div style={{ color: GRAY, fontSize: 14 }}>No projects yet</div>}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {selectedClient.phone && (
                    <button onClick={() => {
                      const phone = selectedClient.phone.replace(/^0/, '966').replace(/[^0-9]/g, '');
                      window.open(`https://wa.me/${phone}`, '_blank');
                    }} style={{ flex: 1, padding: '12px 16px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 10, color: '#25D366', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>📱 WhatsApp</button>
                  )}
                  <button onClick={() => {
                    window.open(`mailto:${selectedClient.email}`, '_blank');
                  }} style={{ flex: 1, padding: '12px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, color: '#3b82f6', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>✉️ Email</button>
                </div>

                <button onClick={() => setSelectedClient(null)} style={{ width: '100%', padding: '14px 20px', background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: WHITE, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, marginTop: 16 }}>Close</button>
                
                <button onClick={async () => {
                  if (!window.confirm('Delete client "' + (selectedClient.company || selectedClient.name) + '"? This will remove their login credentials. Projects and invoices will NOT be deleted.')) return;
                  try {
                    await deleteDoc(doc(db, 'users', selectedClient.email));
                    notify('Client deleted');
                    setSelectedClient(null);
                  } catch (err) { console.error(err); notify('Error deleting client'); }
                }} style={{ width: '100%', padding: '12px 20px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, marginTop: 8 }}>🗑 Delete Client</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddClient && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 450, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>New Client</h2>
              <button onClick={() => setShowAddClient(false)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={s.inputLabel}>Client Name *</div>
                <input value={newClient.name} onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))} placeholder="Full Name" style={{ ...s.input, marginBottom: 0 }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={s.inputLabel}>Email *</div>
                <input value={newClient.email} onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} placeholder="client@company.com" type="email" style={{ ...s.input, marginBottom: 0 }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={s.inputLabel}>Company</div>
                <input value={newClient.company} onChange={e => setNewClient(p => ({ ...p, company: e.target.value }))} placeholder="Company Name" style={{ ...s.input, marginBottom: 0 }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={s.inputLabel}>Phone (WhatsApp)</div>
                <input value={newClient.phone} onChange={e => setNewClient(p => ({ ...p, phone: e.target.value }))} placeholder="05XXXXXXXX" style={{ ...s.input, marginBottom: 0 }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={s.inputLabel}>Password *</div>
                <input value={newClient.password} onChange={e => setNewClient(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" type="text" style={{ ...s.input, marginBottom: 0 }} />
              </div>
              <button onClick={createClient} disabled={creating} style={{ ...s.cta, width: '100%', opacity: creating ? 0.6 : 1 }}>
                {creating ? 'Creating...' : 'Create Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Invite Popup */}
      {whatsAppInvite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 400, padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ color: WHITE, fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Client Created!</div>
            <div style={{ color: GRAY, fontSize: 14, marginBottom: 24 }}>Send the login details via WhatsApp</div>
            
            <a 
              href={'https://wa.me/' + whatsAppInvite.phone + '?text=' + encodeURIComponent(whatsAppInvite.text)} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '16px 20px', background: '#25D366', border: 'none', borderRadius: 12, color: WHITE, fontSize: 16, fontWeight: 700, textDecoration: 'none', marginBottom: 12, boxSizing: 'border-box' }}
            >
              📱 Send via WhatsApp
            </a>
            
            <button onClick={() => {
              try { navigator.clipboard.writeText(whatsAppInvite.text); notify('Message copied!'); } catch(e) {}
            }} style={{ width: '100%', padding: '12px 20px', background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: WHITE, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, marginBottom: 12 }}>
              📋 Copy Message
            </button>
            
            <button onClick={() => setWhatsAppInvite(null)} style={{ width: '100%', padding: '12px 20px', background: 'none', border: 'none', color: GRAY, fontSize: 14, cursor: 'pointer', fontFamily: FONT }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminCosts({ projects, notify, restrictIds }) {
  const [subTasks, setSubTasks] = useState([]);
  const [projectCosts, setProjectCosts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showAddCost, setShowAddCost] = useState(null);
  const [costForm, setCostForm] = useState({ description: '', amount: '', category: 'Material' });
  const [subsMap, setSubsMap] = useState({});

  useEffect(() => {
    const unsub1 = onSnapshot(collection(db, 'subTasks'), (snap) => {
      let items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (restrictIds) items = items.filter(t => restrictIds.includes(t.projectId));
      setSubTasks(items);
    });
    const unsub2 = onSnapshot(collection(db, 'projectCosts'), (snap) => {
      setProjectCosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsub3 = onSnapshot(collection(db, 'invoices'), (snap) => {
      setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsub1(); unsub2(); unsub3(); };
  }, [restrictIds ? restrictIds.join(',') : '']);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'subcontractors'));
        const map = {};
        snap.docs.forEach(d => { const data = d.data(); if (data.email) map[data.email] = data.company || data.name || data.email; });
        setSubsMap(map);
      } catch(e) {}
    })();
  }, []);

  const addProjectCost = async (projectId, projectName) => {
    if (!costForm.description || !costForm.amount) { notify('Fill description and amount'); return; }
    try {
      await addDoc(collection(db, 'projectCosts'), {
        projectId, projectName,
        description: costForm.description,
        amount: parseFloat(costForm.amount) || 0,
        category: costForm.category,
        createdAt: serverTimestamp()
      });
      notify('Cost added!');
      setShowAddCost(null);
      setCostForm({ description: '', amount: '', category: 'Material' });
    } catch(e) { notify('Error'); }
  };

  // Calculate per project
  const projectData = useMemo(() => {
    return projects.map(p => {
      // Revenue: max totalAmount from invoices for this project
      const projInvoices = invoices.filter(i => i.projectName === p.name && i.clientEmail === p.clientEmail);
      const revenue = projInvoices.reduce((max, inv) => Math.max(max, inv.totalAmount || 0), 0) || p.totalAmount || 0;

      // Partner costs
      const partnerTasks = subTasks.filter(t => t.projectId === p.id);
      const partnerCost = partnerTasks.reduce((s, t) => s + (t.amount || 0), 0);
      const partnerPaid = partnerTasks.filter(t => t.paid).reduce((s, t) => s + (t.amount || 0), 0);

      // Material + other costs
      const costs = projectCosts.filter(c => c.projectId === p.id);
      const materialCost = costs.filter(c => c.category === 'Material').reduce((s, c) => s + (c.amount || 0), 0);
      const otherCost = costs.filter(c => c.category !== 'Material').reduce((s, c) => s + (c.amount || 0), 0);

      const totalCost = partnerCost + materialCost + otherCost;
      const profit = revenue - totalCost;
      const margin = revenue > 0 ? Math.round((profit / revenue) * 100) : 0;

      return { ...p, revenue, partnerCost, partnerPaid, materialCost, otherCost, totalCost, profit, margin, partnerTasks, costs };
    }).filter(p => p.revenue > 0 || p.totalCost > 0);
  }, [projects, subTasks, projectCosts, invoices]);

  // Grand totals
  const grandRevenue = projectData.reduce((s, p) => s + p.revenue, 0);
  const grandCost = projectData.reduce((s, p) => s + p.totalCost, 0);
  const grandProfit = grandRevenue - grandCost;
  const grandMargin = grandRevenue > 0 ? Math.round((grandProfit / grandRevenue) * 100) : 0;

  return (
    <div>
      <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Project Costs & Profit</h1>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={{ background: CARD, borderRadius: 14, padding: 16, textAlign: 'center' }}>
          <div style={{ color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>REVENUE</div>
          <div style={{ color: O, fontSize: 20, fontWeight: 700, marginTop: 4 }}>SAR {grandRevenue.toLocaleString()}</div>
        </div>
        <div style={{ background: CARD, borderRadius: 14, padding: 16, textAlign: 'center' }}>
          <div style={{ color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>COSTS</div>
          <div style={{ color: '#ef4444', fontSize: 20, fontWeight: 700, marginTop: 4 }}>SAR {grandCost.toLocaleString()}</div>
        </div>
        <div style={{ background: CARD, borderRadius: 14, padding: 16, textAlign: 'center' }}>
          <div style={{ color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>PROFIT</div>
          <div style={{ color: grandProfit >= 0 ? '#22c55e' : '#ef4444', fontSize: 20, fontWeight: 700, marginTop: 4 }}>SAR {grandProfit.toLocaleString()}</div>
        </div>
        <div style={{ background: CARD, borderRadius: 14, padding: 16, textAlign: 'center' }}>
          <div style={{ color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>MARGIN</div>
          <div style={{ color: grandMargin >= 20 ? '#22c55e' : '#f59e0b', fontSize: 20, fontWeight: 700, marginTop: 4 }}>{grandMargin}%</div>
        </div>
      </div>

      {/* Profit Table */}
      <div style={{ background: CARD, borderRadius: 16, overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ padding: '14px 18px', background: OGLOW, borderBottom: '1px solid ' + OBORDER }}>
          <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1.5 }}>PROFIT SUMMARY</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: GRAY, fontSize: 11, fontWeight: 700 }}>PROJECT</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700 }}>REVENUE</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700 }}>PARTNERS</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700 }}>MATERIAL</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700 }}>OTHER</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700 }}>PROFIT</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700 }}>%</th>
              </tr>
            </thead>
            <tbody>
              {projectData.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '10px 12px', color: WHITE, fontSize: 12, fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: O, fontSize: 12, fontWeight: 700 }}>{p.revenue.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', fontSize: 12 }}>{p.partnerCost.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', fontSize: 12 }}>{p.materialCost.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', fontSize: 12 }}>{p.otherCost.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: p.profit >= 0 ? '#22c55e' : '#ef4444', fontSize: 12, fontWeight: 700 }}>{p.profit.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: p.margin >= 20 ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: p.margin >= 20 ? '#22c55e' : '#f59e0b' }}>{p.margin}%</span>
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid rgba(255,107,0,0.3)' }}>
                <td style={{ padding: '12px', color: O, fontSize: 12, fontWeight: 700 }}>TOTAL</td>
                <td style={{ padding: '12px', textAlign: 'right', color: O, fontSize: 12, fontWeight: 700 }}>{grandRevenue.toLocaleString()}</td>
                <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444', fontSize: 12, fontWeight: 700 }}>{projectData.reduce((s,p) => s+p.partnerCost, 0).toLocaleString()}</td>
                <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444', fontSize: 12, fontWeight: 700 }}>{projectData.reduce((s,p) => s+p.materialCost, 0).toLocaleString()}</td>
                <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444', fontSize: 12, fontWeight: 700 }}>{projectData.reduce((s,p) => s+p.otherCost, 0).toLocaleString()}</td>
                <td style={{ padding: '12px', textAlign: 'right', color: grandProfit >= 0 ? '#22c55e' : '#ef4444', fontSize: 12, fontWeight: 700 }}>{grandProfit.toLocaleString()}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}><span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: grandMargin >= 20 ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: grandMargin >= 20 ? '#22c55e' : '#f59e0b' }}>{grandMargin}%</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Per Project Cost Breakdown */}
      {projectData.map(p => (
        <div key={p.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 18, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{p.name}</div>
              <div style={{ color: GRAY, fontSize: 12 }}>{p.clientName} • Revenue: SAR {p.revenue.toLocaleString()}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: p.profit >= 0 ? '#22c55e' : '#ef4444', fontSize: 16, fontWeight: 700 }}>SAR {p.profit.toLocaleString()}</div>
              <div style={{ color: GRAY, fontSize: 11 }}>Profit ({p.margin}%)</div>
            </div>
          </div>

          {/* Partner costs */}
          {p.partnerTasks.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: '#8b5cf6', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>PARTNER COSTS</div>
              {p.partnerTasks.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <span style={{ color: GRAY, fontSize: 12 }}>{t.subName} - {t.title}</span>
                  <span style={{ color: t.paid ? '#22c55e' : '#f59e0b', fontSize: 12, fontWeight: 600 }}>SAR {(t.amount || 0).toLocaleString()} {t.paid ? '✅' : ''}</span>
                </div>
              ))}
            </div>
          )}

          {/* Material + Other costs */}
          {p.costs.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: O, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>OTHER COSTS</div>
              {p.costs.map(c => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <span style={{ color: GRAY, fontSize: 12 }}>{c.category}: {c.description}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 600 }}>SAR {(c.amount || 0).toLocaleString()}</span>
                    <button onClick={async () => { if (window.confirm('Delete?')) { await deleteDoc(doc(db, 'projectCosts', c.id)); notify('Deleted'); } }} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 10, cursor: 'pointer' }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Cost Button */}
          {showAddCost === p.id ? (
            <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 8, padding: 10, marginTop: 6 }}>
              <select value={costForm.category} onChange={e => setCostForm(pr => ({ ...pr, category: e.target.value }))} style={{ ...s.input, marginBottom: 6, fontSize: 12 }}>
                <option value="Material">Material</option>
                <option value="Equipment">Equipment</option>
                <option value="Transport">Transport</option>
                <option value="Printing">Printing</option>
                <option value="Rental">Rental</option>
                <option value="Other">Other</option>
              </select>
              <input value={costForm.description} onChange={e => setCostForm(pr => ({ ...pr, description: e.target.value }))} placeholder="Description" style={{ ...s.input, marginBottom: 6, fontSize: 12 }} />
              <input type="number" value={costForm.amount} onChange={e => setCostForm(pr => ({ ...pr, amount: e.target.value }))} placeholder="Amount (SAR)" style={{ ...s.input, marginBottom: 6, fontSize: 12 }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => addProjectCost(p.id, p.name)} style={{ flex: 1, padding: '8px', background: O, border: 'none', borderRadius: 6, color: BG, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Add Cost</button>
                <button onClick={() => setShowAddCost(null)} style={{ padding: '8px 12px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: GRAY, fontSize: 12, cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddCost(p.id)} style={{ marginTop: 6, padding: '6px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: GRAY, fontSize: 11, cursor: 'pointer', fontFamily: FONT }}>+ Add Cost</button>
          )}
        </div>
      ))}

      {/* Partner Folders */}
      {subTasks.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ color: '#8b5cf6', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Partner Breakdown</h2>
          {(() => {
            const partnerGroups = {};
            subTasks.forEach(t => {
              const k = subsMap[t.subEmail] || t.subName || t.subEmail || 'Unknown';
              if (!partnerGroups[k]) partnerGroups[k] = { category: t.subCategory || '', tasks: [] };
              partnerGroups[k].tasks.push(t);
            });
            return Object.entries(partnerGroups).map(([name, data]) => {
              const total = data.tasks.reduce((s, t) => s + (t.amount || 0), 0);
              const paid = data.tasks.reduce((s, t) => s + (t.paidAmount || 0), 0);
              const pending = total - paid;
              return (
                <div key={name} style={{ background: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 14, marginBottom: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(139,92,246,0.06)' }}>
                    <span style={{ fontSize: 20 }}>🔧</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: WHITE, fontSize: 15, fontWeight: 700 }}>{name}</div>
                      <div style={{ color: GRAY, fontSize: 12 }}>{data.category} • {data.tasks.length} task(s)</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#8b5cf6', fontSize: 14, fontWeight: 700 }}>SAR {total.toLocaleString()}</div>
                      <div style={{ color: pending > 0 ? '#f59e0b' : '#22c55e', fontSize: 11 }}>{pending > 0 ? 'Due: SAR ' + pending.toLocaleString() : '✅ Paid'}</div>
                    </div>
                  </div>
                  {data.tasks.map(t => (
                    <div key={t.id} style={{ padding: '8px 16px', borderTop: '1px solid rgba(139,92,246,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>{t.title}</div>
                        <div style={{ color: GRAY, fontSize: 11 }}>{t.projectName}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#8b5cf6', fontSize: 13, fontWeight: 600 }}>SAR {(t.amount || 0).toLocaleString()}</div>
                        {t.paidAmount > 0 && <div style={{ color: '#22c55e', fontSize: 10 }}>Paid: SAR {t.paidAmount.toLocaleString()}</div>}
                        {!t.paid && (t.amount || 0) - (t.paidAmount || 0) > 0 && <div style={{ color: '#f59e0b', fontSize: 10 }}>Due: SAR {((t.amount || 0) - (t.paidAmount || 0)).toLocaleString()}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}

function AdminTeam({ notify }) {
  const [pms, setPms] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [selectedPm, setSelectedPm] = useState(null);
  const [pmProjects, setPmProjects] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'projectManagers'), (snap) => {
      setPms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'projects'), (snap) => {
      setPmProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const createPm = async () => {
    if (!form.name || !form.email || !form.password) { notify('Name, email and password required'); return; }
    setCreating(true);
    try {
      if (!auth.currentUser) await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
      await addDoc(collection(db, 'projectManagers'), { ...form, email: form.email.toLowerCase().trim(), isFirstLogin: true, createdAt: serverTimestamp() });
      notify('Project Manager created!');
      setShowAdd(false);
      setSelectedPm({ name: form.name, email: form.email.toLowerCase().trim(), password: form.password, phone: form.phone, _justCreated: true });
      setForm({ name: '', email: '', phone: '', password: '' });
    } catch(e) { notify('Error: ' + e.message); }
    setCreating(false);
  };

  const sendPmInvite = (pm, method) => {
    const msg = `Hello ${pm.name},\n\nYou've been invited to join ImpressaBuild as a Project Manager!\n\n🔗 Portal: https://app.impressabuild.com\n📧 Email: ${pm.email}\n🔑 Password: ${pm.password}\n\nOpen the app, tap "ImpressaBuild" then "Project Manager" and log in with your email and password. You'll be asked to set a new password on first login.\n\nBest regards,\nImpressaBuild Team`;
    if (method === 'whatsapp') {
      const phone = pm.phone ? pm.phone.replace(/\s/g, '').replace(/^0/, '966') : '';
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else {
      window.open(`mailto:${pm.email}?subject=${encodeURIComponent('ImpressaBuild - Project Manager Access')}&body=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, margin: 0 }}>Team</h1>
        <button onClick={() => setShowAdd(true)} style={{ ...s.cta, fontSize: 14, padding: '10px 16px' }}>+ New PM</button>
      </div>

      {showAdd && (
        <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 18, marginBottom: 20 }}>
          <div style={{ color: '#3b82f6', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>NEW PROJECT MANAGER</div>
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full Name *" style={{ ...s.input, marginBottom: 8 }} />
          <input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email *" style={{ ...s.input, marginBottom: 8 }} />
          <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone (05xxxxxxxx)" style={{ ...s.input, marginBottom: 8 }} />
          <input value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Temporary Password *" style={{ ...s.input, marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={createPm} disabled={creating} style={{ ...s.cta, flex: 1, opacity: creating ? 0.6 : 1 }}>{creating ? 'Creating...' : 'Create PM'}</button>
            <button onClick={() => setShowAdd(false)} style={{ ...s.ctaOutline, flex: 1 }}>Cancel</button>
          </div>
        </div>
      )}

      {pms.length === 0 && !showAdd && (
        <div style={{ textAlign: 'center', padding: 40, color: GRAY }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👔</div>
          <div>No project managers yet</div>
        </div>
      )}

      {pms.map(pm => {
        const assigned = pmProjects.filter(p => p.pmEmail === pm.email && !p.archived);
        return (
        <div key={pm.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 16, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', fontSize: 17, fontWeight: 700 }}>{(pm.name || '?')[0].toUpperCase()}</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{pm.name}</div>
              <div style={{ color: GRAY, fontSize: 13 }}>{pm.email}{pm.phone ? ' • ' + pm.phone : ''}</div>
              <div style={{ color: '#3b82f6', fontSize: 13, marginTop: 2 }}>{assigned.length} active project(s)</div>
            </div>
          </div>
          {assigned.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {assigned.map(p => <span key={p.id} style={{ padding: '4px 10px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, color: '#3b82f6', fontSize: 12 }}>{p.name}</span>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={() => sendPmInvite(pm, 'whatsapp')} style={{ flex: 1, padding: '10px', background: '#25D366', border: 'none', borderRadius: 8, color: WHITE, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>📱 WhatsApp Invite</button>
            <button onClick={() => sendPmInvite(pm, 'email')} style={{ flex: 1, padding: '10px', background: '#3b82f6', border: 'none', borderRadius: 8, color: WHITE, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>✉️ Email Invite</button>
            <button onClick={async () => {
              if (!window.confirm('Delete PM "' + pm.name + '"? Their projects will remain but unassigned.')) return;
              try { await deleteDoc(doc(db, 'projectManagers', pm.id)); notify('Deleted'); } catch(e) { notify('Error'); }
            }} style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#ef4444', fontSize: 13, cursor: 'pointer', fontFamily: FONT }}>🗑</button>
          </div>
        </div>
        );
      })}

      {/* Just created popup */}
      {selectedPm && selectedPm._justCreated && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 420, padding: 24 }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>PM Created!</h2>
              <p style={{ color: GRAY, fontSize: 14, marginTop: 6 }}>Send the login invitation</p>
            </div>
            <div style={{ background: CARD, borderRadius: 12, padding: 14, marginBottom: 16 }}>
              <div style={{ color: WHITE, fontWeight: 600 }}>{selectedPm.name}</div>
              <div style={{ color: O, fontSize: 14, marginTop: 4 }}>{selectedPm.email}</div>
              <div style={{ color: GRAY, fontSize: 14, marginTop: 4 }}>Password: {selectedPm.password}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <button onClick={() => sendPmInvite(selectedPm, 'whatsapp')} style={{ flex: 1, padding: '12px', background: '#25D366', border: 'none', borderRadius: 10, color: WHITE, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>📱 WhatsApp</button>
              <button onClick={() => sendPmInvite(selectedPm, 'email')} style={{ flex: 1, padding: '12px', background: '#3b82f6', border: 'none', borderRadius: 10, color: WHITE, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>✉️ Email</button>
            </div>
            <button onClick={() => setSelectedPm(null)} style={{ ...s.ctaOutline, width: '100%' }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminSubcontractors({ projects, notify }) {
  const [subs, setSubs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', category: 'Carpenter', password: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'subcontractors'), (snap) => {
      setSubs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const [createdPartner, setCreatedPartner] = useState(null);
  const [showInvite, setShowInvite] = useState(false);

  const createSub = async () => {
    if (!form.name || !form.email || !form.password) { notify('Name, email and password required'); return; }
    setCreating(true);
    try {
      if (!auth.currentUser) await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
      await addDoc(collection(db, 'subcontractors'), { ...form, email: form.email.toLowerCase().trim(), isFirstLogin: true, createdAt: serverTimestamp() });
      notify('Partner created!');
      setCreatedPartner({ name: form.name, email: form.email.toLowerCase().trim(), password: form.password, phone: form.phone });
      setShowAdd(false);
      setShowInvite(true);
      setForm({ name: '', email: '', phone: '', company: '', category: 'Carpenter', password: '' });
    } catch (e) { notify('Error: ' + e.message); }
    setCreating(false);
  };

  const sendPartnerInvite = (method) => {
    if (!createdPartner) return;
    const msg = `Hello ${createdPartner.name},\n\nYou've been invited to join ImpressaBuild Partners Portal!\n\n🔗 Portal: https://app.impressabuild.com\n📧 Email: ${createdPartner.email}\n🔑 Password: ${createdPartner.password}\n\nPlease select "Partners Portal" to log in. You'll be asked to set a new password on first login.\n\nBest regards,\nImpressaBuild Team`;
    if (method === 'whatsapp') {
      const phone = createdPartner.phone ? createdPartner.phone.replace(/\s/g, '').replace(/^0/, '966') : '';
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else {
      window.open(`mailto:${createdPartner.email}?subject=${encodeURIComponent('ImpressaBuild Partners Portal - Your Login')}&body=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  return (
    <div>
      {/* Invite Popup */}
      {showInvite && createdPartner && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 420, overflow: 'hidden' }}>
            <div style={{ background: OGLOW, borderBottom: '1px solid ' + OBORDER, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h2 style={{ color: WHITE, fontSize: 22, fontWeight: 700, margin: 0 }}>Partner Created!</h2>
              <p style={{ color: GRAY, fontSize: 14, marginTop: 8 }}>Send the login details via WhatsApp or Email</p>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <div style={{ color: GRAY, fontSize: 14, marginBottom: 4 }}>Partner</div>
                <div style={{ color: WHITE, fontWeight: 600 }}>{createdPartner.name}</div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 12, marginBottom: 4 }}>Email</div>
                <div style={{ color: O, fontWeight: 600 }}>{createdPartner.email}</div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 12, marginBottom: 4 }}>Password</div>
                <div style={{ color: WHITE, fontWeight: 600 }}>{createdPartner.password}</div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <button onClick={() => sendPartnerInvite('whatsapp')} style={{ flex: 1, background: '#25D366', border: 'none', borderRadius: 12, padding: '14px 16px', color: WHITE, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>📱 WhatsApp</button>
                <button onClick={() => sendPartnerInvite('email')} style={{ flex: 1, background: '#3b82f6', border: 'none', borderRadius: 12, padding: '14px 16px', color: WHITE, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>✉️ Email</button>
              </div>
              <button onClick={() => { setShowInvite(false); setCreatedPartner(null); }} style={{ ...s.ctaOutline, width: '100%' }}>Skip</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, margin: 0 }}>Partners</h1>
        <button onClick={() => setShowAdd(true)} style={{ ...s.cta, fontSize: 14, padding: '10px 16px' }}>+ New Partner</button>
      </div>

      {subs.length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No partners yet.</div>}
      
      {subs.map(sub => (
        <div key={sub.id} onClick={() => setSelectedSub(sub)} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 18, marginBottom: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🔧</div>
          <div style={{ flex: 1 }}>
            <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{sub.company || sub.name}</div>
            <div style={{ color: GRAY, fontSize: 13 }}>{sub.company ? sub.name + ' • ' : ''}{sub.category} • {sub.email}</div>
          </div>
          <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: 'rgba(139,92,246,0.15)', color: '#8b5cf6' }}>{sub.category}</span>
        </div>
      ))}

      {/* Selected Sub Detail */}
      {selectedSub && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setSelectedSub(null)}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 450, maxHeight: '90vh', overflow: 'auto', padding: 24 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>Partner Details</h2>
              <button onClick={() => setSelectedSub(null)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>INFO</div>
              <div style={{ display: 'grid', gap: 8 }}>
                <div><span style={{ color: GRAY, fontSize: 14 }}>Name: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedSub.name}</span></div>
                <div><span style={{ color: GRAY, fontSize: 14 }}>Category: </span><span style={{ color: '#8b5cf6', fontSize: 14, fontWeight: 600 }}>{selectedSub.category}</span></div>
                <div><span style={{ color: GRAY, fontSize: 14 }}>Email: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedSub.email}</span></div>
                <div><span style={{ color: GRAY, fontSize: 14 }}>Phone: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedSub.phone || 'Not set'}</span></div>
                <div><span style={{ color: GRAY, fontSize: 14 }}>Company: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedSub.company || 'Independent'}</span></div>
                <div><span style={{ color: GRAY, fontSize: 14 }}>Password: </span><span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{selectedSub.password}</span></div>
              </div>
            </div>
            <button onClick={async () => {
              if (!window.confirm('Delete this partner?')) return;
              try { await deleteDoc(doc(db, 'subcontractors', selectedSub.id)); notify('Deleted'); setSelectedSub(null); } catch(e) { notify('Error'); }
            }} style={{ width: '100%', padding: '12px 20px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, marginBottom: 12 }}>🗑 Delete Partner</button>
            
            {/* Send Invite */}
            <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>SEND INVITATION</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => {
                const msg = `Hello ${selectedSub.name},\n\nYou've been invited to join ImpressaBuild Partners Portal!\n\n🔗 Portal: https://app.impressabuild.com\n📧 Email: ${selectedSub.email}\n🔑 Password: ${selectedSub.password}\n\nPlease select "Partners Portal" to log in. You'll be asked to set a new password on first login.\n\nBest regards,\nImpressaBuild Team`;
                const phone = selectedSub.phone ? selectedSub.phone.replace(/\s/g, '').replace(/^0/, '966') : '';
                window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
              }} style={{ flex: 1, padding: '12px', background: '#25D366', border: 'none', borderRadius: 10, color: WHITE, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>📱 WhatsApp</button>
              <button onClick={() => {
                const msg = `Hello ${selectedSub.name},\n\nYou've been invited to join ImpressaBuild Partners Portal!\n\nPortal: https://app.impressabuild.com\nEmail: ${selectedSub.email}\nPassword: ${selectedSub.password}\n\nPlease select "Partners Portal" to log in.\n\nBest regards,\nImpressaBuild Team`;
                window.open(`mailto:${selectedSub.email}?subject=${encodeURIComponent('ImpressaBuild Partners Portal - Your Login')}&body=${encodeURIComponent(msg)}`, '_blank');
              }} style={{ flex: 1, padding: '12px', background: '#3b82f6', border: 'none', borderRadius: 10, color: WHITE, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>✉️ Email</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Sub Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 450, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>New Partner</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 14 }}><div style={s.inputLabel}>Name *</div><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full Name" style={{ ...s.input, marginBottom: 0 }} /></div>
              <div style={{ marginBottom: 14 }}><div style={s.inputLabel}>Email *</div><input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="sub@email.com" style={{ ...s.input, marginBottom: 0 }} /></div>
              <div style={{ marginBottom: 14 }}><div style={s.inputLabel}>Phone</div><input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="05XXXXXXXX" style={{ ...s.input, marginBottom: 0 }} /></div>
              <div style={{ marginBottom: 14 }}><div style={s.inputLabel}>Company</div><input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Company or Independent" style={{ ...s.input, marginBottom: 0 }} /></div>
              <div style={{ marginBottom: 14 }}><div style={s.inputLabel}>Category *</div>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={{ ...s.input, marginBottom: 0, cursor: 'pointer' }}>
                  {SUB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 20 }}><div style={s.inputLabel}>Password *</div><input value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" style={{ ...s.input, marginBottom: 0 }} /></div>
              <button onClick={createSub} disabled={creating} style={{ ...s.cta, width: '100%', opacity: creating ? 0.6 : 1 }}>{creating ? 'Creating...' : 'Create Partner'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Project Manager Portal
function ProjectManagerPortal({ user, logout, notify }) {
  const [tab, setTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subTasks, setSubTasks] = useState([]);
  const [projectCosts, setProjectCosts] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'projects'), where('pmEmail', '==', user.email));
    const unsub = onSnapshot(q, (snap) => {
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user.email]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'changeRequests'), (snap) => {
      setChangeRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsub2 = onSnapshot(collection(db, 'invoices'), (snap) => {
      setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsub3 = onSnapshot(collection(db, 'subTasks'), (snap) => {
      setSubTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const unsub4 = onSnapshot(collection(db, 'projectCosts'), (snap) => {
      setProjectCosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsub(); unsub2(); unsub3(); unsub4(); };
  }, []);

  const activeProjects = projects.filter(p => !p.archived);
  const completedProjects = projects.filter(p => p.archived);
  const myProjectIds = projects.map(p => p.id);
  const myProjectNames = projects.map(p => p.name);
  const myInvoices = invoices.filter(i => myProjectNames.includes(i.projectName));
  const mySubTasks = subTasks.filter(t => myProjectIds.includes(t.projectId));
  const myCosts = projectCosts.filter(c => myProjectIds.includes(c.projectId));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <div style={{ ...s.header, flexShrink: 0, background: BG }}>
        <HeaderLogo />
        <button onClick={logout} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>Sign Out</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 100px' }}>
        {tab === 'projects' && (
          <div>
            <div style={{ color: '#3b82f6', fontSize: 13, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>PROJECT MANAGER</div>
            <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 4 }}>My Projects</h1>
            <div style={{ color: GRAY, fontSize: 16, marginBottom: 20 }}>{user.name}</div>

            {activeProjects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: GRAY }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
                <div style={{ fontSize: 16 }}>No projects assigned yet</div>
                <div style={{ fontSize: 14, marginTop: 8 }}>Projects assigned to you will appear here</div>
              </div>
            ) : activeProjects.map(p => (
              <div key={p.id} onClick={() => setSelectedProject(p)} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 18, marginBottom: 12, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ color: WHITE, fontSize: 17, fontWeight: 700 }}>{p.name}</div>
                  <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: OGLOW, color: O }}>{p.progress || 0}%</span>
                </div>
                <div style={{ color: GRAY, fontSize: 14, marginBottom: 4 }}>{p.company || p.clientName} • {p.type}</div>
                <div style={{ color: '#8b5cf6', fontSize: 13, marginBottom: 8, textTransform: 'capitalize' }}>Stage: {p.stage || 'design'}</div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3 }}>
                  <div style={{ height: 6, width: (p.progress || 0) + '%', background: O, borderRadius: 3 }} />
                </div>
              </div>
            ))}

            {completedProjects.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ color: GRAY, fontSize: 14, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>COMPLETED ({completedProjects.length})</div>
                {completedProjects.map(p => (
                  <div key={p.id} onClick={() => setSelectedProject(p)} style={{ background: CARD, border: '1px solid rgba(34,197,94,0.15)', borderRadius: 14, padding: 14, marginBottom: 8, cursor: 'pointer', opacity: 0.7 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: WHITE, fontSize: 15, fontWeight: 600 }}>{p.name}</div>
                      <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 700 }}>✅ Done</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'financials' && (
          <div>
            <div style={{ color: '#3b82f6', fontSize: 13, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>MY PROJECTS ONLY</div>
            <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Financials</h1>

            {activeProjects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: GRAY }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
                <div style={{ fontSize: 16 }}>No projects assigned</div>
              </div>
            ) : activeProjects.map(p => {
              const pInvoices = myInvoices.filter(i => i.projectName === p.name);
              const invTotal = pInvoices.reduce((s, i) => s + (i.amount || 0), 0);
              const invPaid = pInvoices.reduce((s, i) => s + (i.paid || 0), 0);
              const pTasks = mySubTasks.filter(t => t.projectId === p.id);
              const partnerCost = pTasks.reduce((s, t) => s + (t.amount || 0), 0);
              const partnerPaid = pTasks.reduce((s, t) => s + (t.paidAmount || 0), 0);
              const pCosts = myCosts.filter(c => c.projectId === p.id);
              const otherCost = pCosts.reduce((s, c) => s + (c.amount || 0), 0);
              const revenue = p.totalAmount || invTotal;
              return (
                <div key={p.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 18, marginBottom: 14 }}>
                  <div style={{ color: WHITE, fontSize: 17, fontWeight: 700, marginBottom: 12 }}>{p.name}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <div style={{ background: 'rgba(255,107,0,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ color: GRAY, fontSize: 12 }}>Project Value</div>
                      <div style={{ color: O, fontSize: 17, fontWeight: 700 }}>SAR {revenue.toLocaleString()}</div>
                    </div>
                    <div style={{ background: 'rgba(34,197,94,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ color: GRAY, fontSize: 12 }}>Client Paid</div>
                      <div style={{ color: '#22c55e', fontSize: 17, fontWeight: 700 }}>SAR {invPaid.toLocaleString()}</div>
                    </div>
                    <div style={{ background: 'rgba(139,92,246,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ color: GRAY, fontSize: 12 }}>Partner Costs</div>
                      <div style={{ color: '#8b5cf6', fontSize: 17, fontWeight: 700 }}>SAR {partnerCost.toLocaleString()}</div>
                      {partnerCost - partnerPaid > 0 && <div style={{ color: '#f59e0b', fontSize: 11 }}>Due: SAR {(partnerCost - partnerPaid).toLocaleString()}</div>}
                    </div>
                    <div style={{ background: 'rgba(59,130,246,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ color: GRAY, fontSize: 12 }}>Other Costs</div>
                      <div style={{ color: '#3b82f6', fontSize: 17, fontWeight: 700 }}>SAR {otherCost.toLocaleString()}</div>
                    </div>
                  </div>
                  {pInvoices.length > 0 && (
                    <div>
                      <div style={{ color: GRAY, fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>INVOICES</div>
                      {pInvoices.map(inv => (
                        <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                          <div style={{ color: WHITE, fontSize: 13 }}>{inv.description || 'Invoice'}</div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>SAR {(inv.amount || 0).toLocaleString()}</div>
                            <div style={{ color: inv.status === 'paid' ? '#22c55e' : '#f59e0b', fontSize: 11, fontWeight: 600 }}>{inv.status === 'paid' ? '✅ Paid' : (inv.paid || 0) > 0 ? 'Partial: SAR ' + inv.paid.toLocaleString() : 'Unpaid'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'account' && (
          <div>
            <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Account</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 20, padding: '20px 18px', marginBottom: 20 }}>
              <IconMark size={54} />
              <div>
                <div style={{ color: WHITE, fontSize: 20, fontWeight: 700 }}>{user.name}</div>
                <div style={{ color: '#3b82f6', fontSize: 14, marginTop: 4, fontWeight: 600 }}>Project Manager</div>
                <div style={{ color: O, fontSize: 15, marginTop: 4 }}>{user.email}</div>
              </div>
            </div>
            <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 18, padding: '16px 20px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: WHITE, fontSize: 16 }}>📊 Assigned Projects</span>
              <span style={{ color: O, fontSize: 20, fontWeight: 700 }}>{activeProjects.length}</span>
            </div>
            <button style={{ ...s.ctaOutline, width: '100%', textAlign: 'center', color: '#E85454', borderColor: 'rgba(232,84,84,0.2)' }} onClick={logout}>Sign Out</button>
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', background: 'rgba(10,10,11,0.95)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '10px 0 24px', zIndex: 100 }}>
        {[{ id: 'projects', icon: '📊', label: 'Projects' }, { id: 'financials', icon: '💰', label: 'Financials' }, { id: 'account', icon: '👤', label: 'Account' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, textAlign: 'center', padding: '6px 0' }}>
            <div style={{ fontSize: 22, marginBottom: 2, opacity: tab === t.id ? 1 : 0.4 }}>{t.icon}</div>
            <div style={{ color: tab === t.id ? O : GRAY, fontSize: 12, fontWeight: 600 }}>{t.label}</div>
          </button>
        ))}
      </div>

      {/* Project Detail - reuse admin modal (full project controls) */}
      {selectedProject && createPortal(<ProjectDetailModal project={selectedProject} changeRequests={changeRequests} onClose={() => setSelectedProject(null)} notify={notify} />, document.body)}
    </div>
  );
}

// Partners Portal
function PartnerTaskCard({ task, notify, updateTaskProgress, uploadTaskPhoto }) {
  const [quoteAmount, setQuoteAmount] = useState('');
  const [uploading, setUploading] = useState(false);
  const [quoteFileUrl, setQuoteFileUrl] = useState('');

  const uploadQuoteFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/partner-quotes');
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.secure_url) { setQuoteFileUrl(data.secure_url); notify('Quote uploaded!'); }
    } catch(err) { notify('Upload failed'); }
    setUploading(false);
  };

  const status = task.orderStatus || 'sent';
  const statusLabel = status === 'sent' ? 'New Work Order' : status === 'accepted' ? 'Preparing Quote' : status === 'quoted' ? 'Quote Sent' : status === 'approved' ? 'Approved - In Progress' : status === 'in_progress' ? 'In Progress' : status === 'complete' ? 'Completed' : status === 'rejected_by_partner' ? 'Declined' : status === 'quote_rejected' ? 'Quote Rejected' : status;
  const statusColor = status === 'sent' ? '#3b82f6' : status === 'accepted' ? '#f59e0b' : status === 'quoted' ? '#8b5cf6' : (status === 'approved' || status === 'in_progress' || status === 'complete') ? '#22c55e' : '#ef4444';

  return (
    <div style={{ background: CARD, border: '1px solid ' + (status === 'sent' ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)'), borderRadius: 16, padding: 18, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{task.title}</div>
        <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 14, fontWeight: 700, background: statusColor + '22', color: statusColor }}>{statusLabel}</span>
      </div>
      <div style={{ color: GRAY, fontSize: 15, marginBottom: 4 }}>Project: {task.projectName}</div>
      {task.description && <div style={{ color: GRAY, fontSize: 15, marginBottom: 8, lineHeight: 1.5 }}>{task.description}</div>}
      {task.deadline && <div style={{ color: '#f59e0b', fontSize: 15, marginBottom: 8 }}>Deadline: {task.deadline}</div>}
      {task.amount > 0 && <div style={{ color: GRAY, fontSize: 15, marginBottom: 8 }}>Budget: SAR {task.amount.toLocaleString()}</div>}
      
      {/* Brief files from admin */}
      {task.briefFiles && task.briefFiles.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: O, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>ATTACHMENTS</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {task.briefFiles.map((f, i) => (
              <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" style={{ padding: '4px 10px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 6, color: O, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>📎 {f.name.substring(0, 20)}</a>
            ))}
          </div>
        </div>
      )}
      
      {/* Step 1: Accept or Reject work order */}
      {status === 'sent' && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: WHITE, fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Do you want to quote for this work?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={async () => {
              await updateDoc(doc(db, 'subTasks', task.id), { orderStatus: 'accepted', updatedAt: serverTimestamp() });
              notify('Accepted! Now submit your quote.');
            }} style={{ flex: 1, padding: '12px', background: '#22c55e', border: 'none', borderRadius: 10, color: WHITE, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>✓ Accept</button>
            <button onClick={async () => {
              await updateDoc(doc(db, 'subTasks', task.id), { orderStatus: 'rejected_by_partner', updatedAt: serverTimestamp() });
              notify('Declined');
            }} style={{ flex: 1, padding: '12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>✗ Decline</button>
          </div>
        </div>
      )}
      
      {/* Step 2: Submit quotation */}
      {status === 'accepted' && (
        <div style={{ marginTop: 10, background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 10, padding: 12 }}>
          <div style={{ color: '#8b5cf6', fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Submit Your Quote</div>
          <input type="number" value={quoteAmount} onChange={e => setQuoteAmount(e.target.value)} placeholder="Your quote amount (SAR) *" style={{ ...s.input, marginBottom: 8, fontSize: 13 }} />
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', background: CARD, border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', marginBottom: 8, opacity: uploading ? 0.6 : 1 }}>
            <span style={{ fontSize: 14 }}>{uploading ? '⏳' : quoteFileUrl ? '✅' : '📎'}</span>
            <span style={{ color: GRAY, fontSize: 12 }}>{uploading ? 'Uploading...' : quoteFileUrl ? 'Quote attached' : 'Attach quotation PDF (optional)'}</span>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadQuoteFile} style={{ display: 'none' }} disabled={uploading} />
          </label>
          <button onClick={async () => {
            const amt = parseFloat(quoteAmount);
            if (!amt) { notify('Enter your quote amount'); return; }
            await updateDoc(doc(db, 'subTasks', task.id), { orderStatus: 'quoted', partnerQuoteAmount: amt, partnerQuoteUrl: quoteFileUrl || null, updatedAt: serverTimestamp() });
            createPushNotification(ADMIN_EMAIL, 'partner_quote', 'Partner Quote Received', task.subName + ' submitted a quote of SAR ' + amt.toLocaleString() + ' for "' + task.title + '".', task.projectName);
            notify('Quote submitted! Waiting for approval.');
          }} style={{ width: '100%', padding: '12px', background: '#8b5cf6', border: 'none', borderRadius: 10, color: WHITE, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Submit Quote</button>
        </div>
      )}
      
      {/* Waiting for admin approval */}
      {status === 'quoted' && (
        <div style={{ marginTop: 10, padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, textAlign: 'center' }}>
          <span style={{ color: '#f59e0b', fontSize: 15, fontWeight: 600 }}>⏳ Your quote of SAR {(task.partnerQuoteAmount || 0).toLocaleString()} is under review</span>
        </div>
      )}
      
      {/* Quote rejected */}
      {status === 'quote_rejected' && (
        <div style={{ marginTop: 10, padding: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, textAlign: 'center' }}>
          <span style={{ color: '#ef4444', fontSize: 15, fontWeight: 600 }}>Quote was rejected by admin</span>
        </div>
      )}
      
      {/* Approved - work in progress */}
      {(status === 'approved' || status === 'in_progress') && (
        <div style={{ marginTop: 10 }}>
          {task.partnerQuoteAmount > 0 && <div style={{ color: '#22c55e', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>✅ Approved: SAR {task.partnerQuoteAmount.toLocaleString()}</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: GRAY, fontSize: 12 }}>Progress</span>
            <span style={{ color: O, fontSize: 15, fontWeight: 700 }}>{task.progress || 0}%</span>
          </div>
          <input type="range" min="0" max="100" defaultValue={task.progress || 0} onMouseUp={e => updateTaskProgress(task.id, parseInt(e.target.value))} onTouchEnd={e => updateTaskProgress(task.id, parseInt(e.target.value))} style={{ width: '100%', accentColor: O, marginBottom: 8 }} />
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 8, cursor: 'pointer', color: O, fontSize: 15, fontWeight: 600 }}>
            📷 Upload Photo
            <input type="file" accept="image/*" onChange={e => uploadTaskPhoto(task.id, e)} style={{ display: 'none' }} />
          </label>
        </div>
      )}
      
      {/* Payment status */}
      {task.paid && <div style={{ color: '#22c55e', fontSize: 15, fontWeight: 600, marginTop: 10, padding: 10, background: 'rgba(34,197,94,0.08)', borderRadius: 8, textAlign: 'center' }}>💰 Payment received: SAR {(task.paidAmount || task.amount || 0).toLocaleString()}</div>}
      
      {/* Photos */}
      {task.photos && task.photos.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {task.photos.map((p, i) => (
            <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" style={{ width: 60, height: 60, borderRadius: 8, overflow: 'hidden' }}>
              <img src={p.url.replace('/upload/', '/upload/w_120,h_120,c_fill/')} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function SubcontractorPortal({ user, logout, notify }) {
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState('tasks');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'subTasks'), where('subEmail', '==', user.email));
    const unsub = onSnapshot(q, (snap) => {
      const t = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      t.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setTasks(t);
    });
    return () => unsub();
  }, [user.email]);

  const updateTaskProgress = async (taskId, progress) => {
    try {
      await updateDoc(doc(db, 'subTasks', taskId), { progress, updatedAt: serverTimestamp() });
      notify('Progress updated!');
    } catch (e) { notify('Error'); }
  };

  const uploadTaskPhoto = async (taskId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/subtasks');
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.secure_url) {
        const task = tasks.find(t => t.id === taskId);
        const photos = [...(task?.photos || []), { url: data.secure_url, name: file.name, uploadedAt: new Date().toISOString() }];
        await updateDoc(doc(db, 'subTasks', taskId), { photos, updatedAt: serverTimestamp() });
        notify('Photo uploaded!');
      }
    } catch (err) { notify('Upload failed'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: BG }}>
      <div style={{ ...s.header, flexShrink: 0, background: BG }}>
        <HeaderLogo />
        <button style={s.burger} onClick={() => setMenuOpen(!menuOpen)}>
          <div style={{ ...s.bLine, transform: menuOpen ? "rotate(45deg) translate(3px,3px)" : "none" }} />
          <div style={{ ...s.bLine, opacity: menuOpen ? 0 : 1 }} />
          <div style={{ ...s.bLine, transform: menuOpen ? "rotate(-45deg) translate(3px,-3px)" : "none" }} />
        </button>
      </div>
      
      <div style={{ overflow: "hidden", maxHeight: menuOpen ? 300 : 0, opacity: menuOpen ? 1 : 0, background: CARD, borderBottom: "1px solid " + (menuOpen ? OBORDER : "transparent"), transition: "all .35s", flexShrink: 0 }}>
        {["tasks", "payments", "account"].map(t => (
          <button key={t} onClick={() => { setTab(t); setMenuOpen(false); }} style={{ display: "block", width: "100%", background: "none", border: "none", padding: "16px 24px", textAlign: "left", fontSize: 18, fontWeight: tab === t ? 700 : 400, color: tab === t ? O : WHITE, cursor: "pointer", fontFamily: FONT, textTransform: 'capitalize' }}>{t === 'tasks' ? 'My Tasks' : t === 'payments' ? 'My Payments' : 'Account'}</button>
        ))}
      </div>
      
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 20, paddingBottom: 100 }}>
        {tab === 'tasks' && (
          <div>
            <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>PARTNERS PORTAL</div>
            <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 4 }}>My Tasks</h1>
            <div style={{ color: GRAY, fontSize: 16, marginBottom: 20 }}>{user.name} • {user.category || 'Subcontractor'}</div>
            
            {tasks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: GRAY }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 16 }}>No work orders yet</div>
              </div>
            ) : (() => {
              const grouped = {};
              tasks.forEach(t => { const k = t.projectName || 'General'; if (!grouped[k]) grouped[k] = []; grouped[k].push(t); });
              return Object.entries(grouped).map(([projName, projTasks]) => (
                <div key={projName} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>📁</span>
                    <div>
                      <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{projName}</div>
                      <div style={{ color: GRAY, fontSize: 13 }}>{projTasks.length} task(s)</div>
                    </div>
                  </div>
                  {projTasks.map(task => (
                    <PartnerTaskCard key={task.id} task={task} notify={notify} updateTaskProgress={updateTaskProgress} uploadTaskPhoto={uploadTaskPhoto} />
                  ))}
                </div>
              ));
            })()}
          </div>
        )}
        
        {tab === 'payments' && (
          <div>
            <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>PAYMENTS</div>
            <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 20 }}>My Payments</h1>
            
            {(() => {
              const totalAgreed = tasks.reduce((s, t) => s + (t.amount || 0), 0);
              const totalPaid = tasks.filter(t => t.paid).reduce((s, t) => s + (t.amount || 0), 0);
              const totalPending = totalAgreed - totalPaid;
              return (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
                    <div style={{ background: CARD, borderRadius: 12, padding: 14, textAlign: 'center' }}>
                      <div style={{ color: GRAY, fontSize: 13, fontWeight: 700 }}>TOTAL</div>
                      <div style={{ color: O, fontSize: 16, fontWeight: 700, marginTop: 4 }}>SAR {totalAgreed.toLocaleString()}</div>
                    </div>
                    <div style={{ background: CARD, borderRadius: 12, padding: 14, textAlign: 'center' }}>
                      <div style={{ color: GRAY, fontSize: 13, fontWeight: 700 }}>PAID</div>
                      <div style={{ color: '#22c55e', fontSize: 16, fontWeight: 700, marginTop: 4 }}>SAR {totalPaid.toLocaleString()}</div>
                    </div>
                    <div style={{ background: CARD, borderRadius: 12, padding: 14, textAlign: 'center' }}>
                      <div style={{ color: GRAY, fontSize: 10, fontWeight: 700 }}>PENDING</div>
                      <div style={{ color: '#f59e0b', fontSize: 16, fontWeight: 700, marginTop: 4 }}>SAR {totalPending.toLocaleString()}</div>
                    </div>
                  </div>
                  {tasks.filter(t => t.amount > 0).map(task => {
                    const taskDue = (task.amount || 0) - (task.paidAmount || 0);
                    return (
                    <div key={task.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16, marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                          <div style={{ color: WHITE, fontSize: 16, fontWeight: 600 }}>{task.title}</div>
                          <div style={{ color: GRAY, fontSize: 14 }}>{task.projectName}</div>
                        </div>
                        <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: task.paid ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: task.paid ? '#22c55e' : '#f59e0b' }}>{task.paid ? 'Paid' : 'Pending'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                        <div><div style={{ color: GRAY, fontSize: 12 }}>Total</div><div style={{ color: O, fontSize: 16, fontWeight: 700 }}>SAR {task.amount.toLocaleString()}</div></div>
                        {task.paidAmount > 0 && <div><div style={{ color: GRAY, fontSize: 12 }}>Paid</div><div style={{ color: '#22c55e', fontSize: 16, fontWeight: 700 }}>SAR {task.paidAmount.toLocaleString()}</div></div>}
                        {!task.paid && taskDue > 0 && <div><div style={{ color: GRAY, fontSize: 12 }}>Due</div><div style={{ color: '#f59e0b', fontSize: 16, fontWeight: 700 }}>SAR {taskDue.toLocaleString()}</div></div>}
                      </div>
                      {!task.paid && taskDue > 0 && (
                        <button onClick={async () => {
                          createPushNotification(ADMIN_EMAIL, 'payment_reminder_partner', 'Payment Reminder from Partner', (task.subName || 'Partner') + ' is requesting payment of SAR ' + taskDue.toLocaleString() + ' for "' + task.title + '" on project "' + task.projectName + '".', task.projectName);
                          notify('Payment reminder sent to admin!');
                        }} style={{ width: '100%', padding: '12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, color: '#f59e0b', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>🔔 Send Payment Reminder</button>
                      )}
                    </div>
                    );
                  })}
                  {tasks.filter(t => t.amount > 0).length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 30 }}>No payment records yet</div>}
                </>
              );
            })()}
          </div>
        )}
        
        {tab === 'account' && (
          <div>
            <div style={{ color: O, fontSize: 13, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>ACCOUNT</div>
            <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Profile</h1>
            <div style={{ background: CARD, borderRadius: 16, padding: 18, marginBottom: 16 }}>
              <div style={{ display: 'grid', gap: 10 }}>
                <div><span style={{ color: GRAY }}>Name: </span><span style={{ color: WHITE, fontWeight: 600 }}>{user.name}</span></div>
                <div><span style={{ color: GRAY }}>Email: </span><span style={{ color: WHITE, fontWeight: 600 }}>{user.email}</span></div>
                <div><span style={{ color: GRAY }}>Category: </span><span style={{ color: '#8b5cf6', fontWeight: 600 }}>{user.category}</span></div>
              </div>
            </div>
            <button onClick={logout} style={{ width: '100%', padding: '14px 20px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, color: '#ef4444', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>Log Out</button>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminFinancials({ projects, allProjects, notify, pmScope }) {
  const [invoices, setInvoices] = useState([]);
  const [extraOrders, setExtraOrders] = useState([]);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [showAddExtra, setShowAddExtra] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ clientEmail: '', projectName: '', description: '', totalAmount: '', amount: '', paid: '0', status: 'pending', fileUrl: '' });
  const [extraForm, setExtraForm] = useState({ clientEmail: '', projectName: '', description: '', type: 'Addition', amount: '', paid: '0', status: 'pending', fileUrl: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedProject, setSelectedProject] = useState('all');
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [createdExtra, setCreatedExtra] = useState(null);

  useEffect(() => {
    const inScope = (item) => {
      if (!pmScope) return true;
      const email = (item.clientEmail || '').toLowerCase();
      return pmScope.clientEmails.includes(email) || (item.projectName && pmScope.projectNames.includes(item.projectName));
    };
    const unsub1 = onSnapshot(collection(db, 'invoices'), (snap) => {
      const invs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(inScope);
      invs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setInvoices(invs);
    });
    const unsub2 = onSnapshot(collection(db, 'extraOrders'), (snap) => {
      const extras = snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(inScope);
      extras.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setExtraOrders(extras);
    });
    return () => { unsub1(); unsub2(); };
  }, [pmScope ? pmScope.clientEmails.join(',') + '|' + pmScope.projectNames.join(',') : '']);

  // Group data by client
  const clientGroups = useMemo(() => {
    const groups = {};
    const allItems = [...invoices, ...extraOrders];
    allItems.forEach(item => {
      const client = item.clientEmail || 'unknown';
      if (!groups[client]) {
        groups[client] = { email: client, projects: {} };
      }
      const projName = item.projectName || 'General';
      if (!groups[client].projects[projName]) {
        groups[client].projects[projName] = { invoices: [], extras: [] };
      }
    });
    invoices.forEach(inv => {
      const client = inv.clientEmail || 'unknown';
      const projName = inv.projectName || 'General';
      groups[client].projects[projName].invoices.push(inv);
    });
    extraOrders.forEach(eo => {
      const client = eo.clientEmail || 'unknown';
      const projName = eo.projectName || 'General';
      groups[client].projects[projName].extras.push(eo);
    });
    return groups;
  }, [invoices, extraOrders]);

  const [expandedClient, setExpandedClient] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);

  // Get client company name from projects
  const [clientCompanyMap, setClientCompanyMap] = useState({});
  
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const map = {};
        usersSnap.forEach(d => {
          const u = d.data();
          if (u.email && u.company) map[u.email.toLowerCase()] = u.company;
        });
        setClientCompanyMap(map);
      } catch (e) {}
    };
    fetchCompanies();
  }, []);

  const getClientName = (email) => {
    const emailLower = (email || '').toLowerCase();
    // Check users collection first for company name
    if (clientCompanyMap[emailLower]) return clientCompanyMap[emailLower];
    const allProjs = allProjects || projects;
    const proj = allProjs.find(p => p.clientEmail === email);
    return proj?.company || proj?.clientName || email.split('@')[0];
  };
  
  const getClientContactName = (email) => {
    const allProjs = allProjects || projects;
    const proj = allProjs.find(p => p.clientEmail === email);
    return proj?.clientName || email.split('@')[0];
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify('File too large. Max 5MB');
      return;
    }
    
    setUploading(true);
    setUploadProgress('Uploading...');
    
    try {
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', `impressabuild/${type}`);
      
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
        { method: 'POST', body: formData }
      );
      
      const data = await response.json();
      
      if (data.secure_url) {
        if (type === 'invoices') {
          setInvoiceForm(prev => ({ ...prev, fileUrl: data.secure_url }));
        } else {
          setExtraForm(prev => ({ ...prev, fileUrl: data.secure_url }));
        }
        notify('File uploaded!');
      } else {
        throw new Error('Upload failed');
      }
      setUploadProgress('');
    } catch (err) {
      console.error('Upload error:', err);
      notify('Upload failed');
      setUploadProgress('');
    }
    setUploading(false);
  };

  const createInvoice = async () => {
    if (!invoiceForm.clientEmail || !invoiceForm.totalAmount) {
      notify('Fill client and total project amount');
      return;
    }
    try {
      // Get client phone from project
      const clientProject = projects.find(p => p.clientEmail === invoiceForm.clientEmail);
      const clientPhone = clientProject?.clientPhone || '';
      
      await addDoc(collection(db, 'invoices'), {
        ...invoiceForm,
        clientEmail: invoiceForm.clientEmail.toLowerCase().trim(),
        totalAmount: parseFloat(invoiceForm.totalAmount) || 0,
        amount: parseFloat(invoiceForm.amount) || 0,
        paid: parseFloat(invoiceForm.paid) || 0,
        createdAt: serverTimestamp()
      });
      notify('Invoice created');
      // Push notification to client
      const clientEmailClean = invoiceForm.clientEmail.toLowerCase().trim();
      createPushNotification(
        clientEmailClean,
        'invoice',
        'New Invoice Created',
        'A new invoice of SAR ' + (parseFloat(invoiceForm.amount) || 0).toLocaleString() + ' has been created for "' + invoiceForm.projectName + '". Please check your portal under Financials.',
        invoiceForm.projectName
      );
      setShowAddInvoice(false);
      setCreatedInvoice({ ...invoiceForm, clientPhone });
      setInvoiceForm({ clientEmail: '', projectName: '', description: '', totalAmount: '', amount: '', paid: '0', status: 'pending', fileUrl: '' });
    } catch (err) {
      notify('Error creating invoice');
    }
  };

  const createExtraOrder = async () => {
    if (!extraForm.clientEmail || !extraForm.amount) {
      notify('Fill required fields');
      return;
    }
    try {
      // Get client phone from project
      const clientProject = projects.find(p => p.clientEmail === extraForm.clientEmail);
      const clientPhone = clientProject?.clientPhone || '';
      
      await addDoc(collection(db, 'extraOrders'), {
        ...extraForm,
        clientEmail: extraForm.clientEmail.toLowerCase().trim(),
        amount: parseFloat(extraForm.amount) || 0,
        paid: parseFloat(extraForm.paid) || 0,
        createdAt: serverTimestamp()
      });
      notify('Extra order created');
      setShowAddExtra(false);
      setCreatedExtra({ ...extraForm, clientPhone });
      setExtraForm({ clientEmail: '', projectName: '', description: '', type: 'Addition', amount: '', paid: '0', status: 'pending', fileUrl: '' });
    } catch (err) {
      notify('Error creating extra order');
    }
  };

  const updateInvoice = async (id, updates) => {
    try {
      await updateDoc(doc(db, 'invoices', id), { ...updates, updatedAt: serverTimestamp() });
      notify('Updated');
    } catch (err) { notify('Error'); }
  };

  const updateExtraOrder = async (id, updates) => {
    try {
      await updateDoc(doc(db, 'extraOrders', id), { ...updates, updatedAt: serverTimestamp() });
      notify('Updated');
    } catch (err) { notify('Error'); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await deleteDoc(doc(db, 'invoices', id));
      notify('Invoice deleted');
    } catch (err) { notify('Error deleting'); }
  };

  const deleteExtraOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this extra order?')) return;
    try {
      await deleteDoc(doc(db, 'extraOrders', id));
      notify('Extra order deleted');
    } catch (err) { notify('Error deleting'); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-SA', { style: 'currency', currency: 'SAR' }).format(amount);

  // Get unique clients from projects
  const clientEmails = [...new Set(projects.map(p => p.clientEmail).filter(Boolean))];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ color: WHITE, fontSize: 24, fontWeight: 700, margin: 0 }}>Financials</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={() => setShowAddInvoice(true)} style={{ ...s.cta, fontSize: 14, padding: '10px 16px' }}>+ Invoice</button>
          <button onClick={() => setShowAddExtra(true)} style={{ ...s.ctaOutline, fontSize: 14, padding: '10px 16px' }}>+ Extra Order</button>
        </div>
      </div>

      {/* Financial Dashboard */}
      {invoices.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: OGLOW, borderBottom: '1px solid ' + OBORDER }}>
              <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5 }}>FINANCIAL SUMMARY</div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>COMPANY</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>TOTAL</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>PAID</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>REMAINING</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', color: GRAY, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>OVERDUE</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let grandTotal = 0, grandPaid = 0, grandRemaining = 0;
                    const rows = Object.entries(clientGroups).map(([clientEmail, clientData]) => {
                      const companyName = getClientName(clientEmail);
                      const clientInvoices = Object.values(clientData.projects).flatMap(p => p.invoices);
                      const clientExtras = Object.values(clientData.projects).flatMap(p => p.extras);
                      // Total value from max totalAmount per project
                      const projTotals = {};
                      clientInvoices.forEach(inv => {
                        const k = inv.projectName || 'General';
                        if (!projTotals[k] || (inv.totalAmount || 0) > projTotals[k]) projTotals[k] = inv.totalAmount || 0;
                      });
                      const totalValue = Object.values(projTotals).reduce((s, v) => s + v, 0);
                      const paid = clientInvoices.reduce((s, i) => s + (i.paid || 0), 0) + clientExtras.reduce((s, e) => s + (e.paid || 0), 0);
                      const remaining = totalValue - paid;
                      // Calculate overdue days - oldest unpaid invoice
                      let overdueDays = 0;
                      clientInvoices.forEach(inv => {
                        if (inv.status !== 'paid' && (inv.amount || 0) - (inv.paid || 0) > 0 && inv.createdAt) {
                          const created = inv.createdAt.toDate ? inv.createdAt.toDate() : new Date(inv.createdAt);
                          const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
                          if (days > overdueDays) overdueDays = days;
                        }
                      });
                      grandTotal += totalValue;
                      grandPaid += paid;
                      grandRemaining += remaining;
                      return { companyName, totalValue, paid, remaining, overdueDays };
                    });
                    return (<>
                      {rows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '10px 14px', color: WHITE, fontSize: 13, fontWeight: 600 }}>{r.companyName}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: O, fontSize: 13, fontWeight: 700 }}>SAR {r.totalValue.toLocaleString()}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: '#22c55e', fontSize: 13, fontWeight: 600 }}>SAR {r.paid.toLocaleString()}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: r.remaining > 0 ? '#ef4444' : '#22c55e', fontSize: 13, fontWeight: 700 }}>{r.remaining > 0 ? 'SAR ' + r.remaining.toLocaleString() : '✅'}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                            {r.overdueDays > 0 ? (
                              <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: r.overdueDays > 30 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)', color: r.overdueDays > 30 ? '#ef4444' : '#f59e0b' }}>{r.overdueDays} days</span>
                            ) : <span style={{ color: '#22c55e', fontSize: 12 }}>—</span>}
                          </td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid rgba(255,107,0,0.3)' }}>
                        <td style={{ padding: '12px 14px', color: O, fontSize: 13, fontWeight: 700 }}>TOTAL</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', color: O, fontSize: 13, fontWeight: 700 }}>SAR {grandTotal.toLocaleString()}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', color: '#22c55e', fontSize: 13, fontWeight: 700 }}>SAR {grandPaid.toLocaleString()}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', color: grandRemaining > 0 ? '#ef4444' : '#22c55e', fontSize: 13, fontWeight: 700 }}>{grandRemaining > 0 ? 'SAR ' + grandRemaining.toLocaleString() : '✅'}</td>
                        <td style={{ padding: '12px 14px' }}></td>
                      </tr>
                    </>);
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Client Folders */}
      {Object.keys(clientGroups).length > 0 ? Object.entries(clientGroups).map(([clientEmail, clientData]) => {
        const clientName = getClientName(clientEmail);
        const isExpanded = expandedClient === clientEmail;
        const clientInvoices = Object.values(clientData.projects).flatMap(p => p.invoices);
        const clientExtras = Object.values(clientData.projects).flatMap(p => p.extras);
        // Get total project value (max totalAmount per project)
        const projTotals = {};
        clientInvoices.forEach(inv => {
          const k = inv.projectName || 'General';
          if (!projTotals[k] || (inv.totalAmount || 0) > projTotals[k]) projTotals[k] = inv.totalAmount || 0;
        });
        const clientTotalValue = Object.values(projTotals).reduce((s, v) => s + v, 0);
        const clientPaid = clientInvoices.reduce((s, i) => s + (i.paid || 0), 0) + clientExtras.reduce((s, e) => s + (e.paid || 0), 0);
        const clientRemaining = clientTotalValue - clientPaid;
        
        return (
          <div key={clientEmail} style={{ marginBottom: 12 }}>
            <button onClick={() => setExpandedClient(isExpanded ? null : clientEmail)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: CARD, border: '1px solid ' + (isExpanded ? O : 'rgba(255,255,255,0.06)'), borderRadius: 16, cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
              <span style={{ fontSize: 24 }}>{isExpanded ? '📂' : '📁'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{clientName}</div>
                <div style={{ color: GRAY, fontSize: 13 }}>{Object.keys(clientData.projects).length} project(s)</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: O, fontSize: 15, fontWeight: 700 }}>SAR {clientTotalValue.toLocaleString()}</div>
                <div style={{ color: clientRemaining > 0 ? '#ef4444' : '#22c55e', fontSize: 12 }}>{clientRemaining > 0 ? 'Due: SAR ' + clientRemaining.toLocaleString() : '✅ Paid'}</div>
              </div>
              <span style={{ color: GRAY, fontSize: 18, marginLeft: 8 }}>{isExpanded ? '▼' : '▶'}</span>
            </button>
            
            {isExpanded && (
              <div style={{ marginLeft: 20, marginTop: 8 }}>
                {Object.entries(clientData.projects).map(([projName, projData]) => {
                  const isProjExpanded = expandedProject === clientEmail + ':' + projName;
                  // Get total project value from the max totalAmount in invoices
                  const projTotalValue = projData.invoices.reduce((max, inv) => Math.max(max, inv.totalAmount || 0), 0);
                  const projInvoiced = projData.invoices.reduce((s, i) => s + (i.amount || 0), 0) + projData.extras.reduce((s, e) => s + (e.amount || 0), 0);
                  const projPaid = projData.invoices.reduce((s, i) => s + (i.paid || 0), 0) + projData.extras.reduce((s, e) => s + (e.paid || 0), 0);
                  const projRemaining = (projTotalValue || projInvoiced) - projPaid;
                  
                  return (
                    <div key={projName} style={{ marginBottom: 8 }}>
                      <button onClick={() => setExpandedProject(isProjExpanded ? null : clientEmail + ':' + projName)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid ' + (isProjExpanded ? 'rgba(255,107,0,0.3)' : 'rgba(255,255,255,0.04)'), borderRadius: 12, cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
                        <span style={{ fontSize: 18 }}>{isProjExpanded ? '📂' : '📁'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{projName}</div>
                          <div style={{ color: GRAY, fontSize: 12 }}>{projData.invoices.length} invoice(s) • {projData.extras.length} extra(s)</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: O, fontSize: 14, fontWeight: 600 }}>SAR {(projTotalValue || projInvoiced).toLocaleString()}</div>
                          <div style={{ color: projRemaining > 0 ? '#ef4444' : '#22c55e', fontSize: 11 }}>{projRemaining > 0 ? 'Due: SAR ' + projRemaining.toLocaleString() : '✅ Paid'}</div>
                        </div>
                        <span style={{ color: GRAY, fontSize: 16 }}>{isProjExpanded ? '▼' : '▶'}</span>
                      </button>
                      
                      {isProjExpanded && (
                        <div style={{ marginLeft: 16, marginTop: 6 }}>
                          {projData.invoices.map(inv => {
                            const invDue = (inv.amount || 0) - (inv.paid || 0);
                            const isUnpaid = inv.status !== 'paid' && invDue > 0;
                            return (
                            <div key={inv.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.04)', borderRadius: 10, marginBottom: 6, overflow: 'hidden' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
                                <span style={{ fontSize: 16 }}>📄</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>{inv.description || 'Invoice'}</div>
                                  <div style={{ color: GRAY, fontSize: 12 }}>Amount: SAR {(inv.amount || 0).toLocaleString()} • Paid: SAR {(inv.paid || 0).toLocaleString()}</div>
                                </div>
                                <span style={{ padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: inv.status === 'paid' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: inv.status === 'paid' ? '#22c55e' : '#f59e0b' }}>{inv.status || 'pending'}</span>
                              </div>
                              {isUnpaid && (
                                <div style={{ display: 'flex', gap: 6, padding: '0 14px 10px' }}>
                                  <button onClick={async () => {
                                    try {
                                      await updateDoc(doc(db, 'invoices', inv.id), { paid: inv.amount || 0, status: 'paid', paidAt: serverTimestamp() });
                                      createPushNotification(inv.clientEmail || clientEmail, 'payment_thanks', 'Payment Received! Thank You', 'We have received your payment of SAR ' + (inv.amount || 0).toLocaleString() + ' for "' + (inv.projectName || projName) + '". Thank you for your prompt payment!', inv.projectName || projName);
                                      notify('Marked as paid & client notified!');
                                    } catch (err) { notify('Error: ' + err.message); }
                                  }} style={{ flex: 1, padding: '6px 10px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 6, color: '#22c55e', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>✅ Mark as Paid</button>
                                  <button onClick={() => {
                                    createPushNotification(inv.clientEmail || clientEmail, 'payment_reminder', 'Payment Reminder', 'A payment of SAR ' + invDue.toLocaleString() + ' is due for "' + (inv.projectName || projName) + '". Please check your Financials on the portal.', inv.projectName || projName);
                                    notify('Reminder sent!');
                                  }} style={{ padding: '6px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>🔔 Remind</button>
                                  <button onClick={() => {
                                    const msg = 'Hi,\n\nThis is a reminder that a payment of SAR ' + invDue.toLocaleString() + ' is due for "' + (inv.projectName || projName) + '".\n\nPlease log in to your portal:\nhttps://app.impressabuild.com\n\nBest regards,\nImpressaBuild Team';
                                    window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank');
                                  }} style={{ padding: '6px 10px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.2)', borderRadius: 6, color: '#25D366', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>📱</button>
                                </div>
                              )}
                            </div>
                            );
                          })}
                          {projData.extras.map(eo => (
                            <div key={eo.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: CARD, border: '1px solid rgba(139,92,246,0.15)', borderRadius: 10, marginBottom: 6 }}>
                              <span style={{ fontSize: 16 }}>🔧</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: WHITE, fontSize: 13, fontWeight: 600 }}>{eo.description || 'Extra Order'}</div>
                                <div style={{ color: GRAY, fontSize: 12 }}>Amount: SAR {(eo.amount || 0).toLocaleString()} • Paid: SAR {(eo.paid || 0).toLocaleString()}</div>
                              </div>
                              <span style={{ padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: eo.status === 'paid' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: eo.status === 'paid' ? '#22c55e' : '#f59e0b' }}>{eo.status || 'pending'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }) : <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No financial records yet</div>}

      {/* Add Invoice Modal */}
      {showAddInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>New Invoice</h2>
              <button onClick={() => setShowAddInvoice(false)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Client Email *</div>
                <select value={invoiceForm.clientEmail} onChange={e => setInvoiceForm(p => ({ ...p, clientEmail: e.target.value }))} style={{ ...s.input, cursor: 'pointer' }}>
                  <option value="">Select client...</option>
                  {clientEmails.map(email => <option key={email} value={email}>{email}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Project Name</div>
                <select value={invoiceForm.projectName} onChange={e => setInvoiceForm(p => ({ ...p, projectName: e.target.value }))} style={{ ...s.input, cursor: 'pointer' }}>
                  <option value="">Select project...</option>
                  {projects.filter(p => p.clientEmail === invoiceForm.clientEmail).map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>
              <FInput label="Description" value={invoiceForm.description} onChange={v => setInvoiceForm(p => ({ ...p, description: v }))} placeholder="Initial Quotation, Milestone 2, etc." />
              
              {/* Total Project Amount - separate from invoice amount */}
              <div style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <FInput label="Total Project Amount (SAR) *" value={invoiceForm.totalAmount} onChange={v => setInvoiceForm(p => ({ ...p, totalAmount: v }))} placeholder="150000" type="number" />
                <div style={{ color: GRAY, fontSize: 14, marginTop: -8 }}>Full quoted price for the entire project</div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FInput label="Invoice Amount (SAR)" value={invoiceForm.amount} onChange={v => setInvoiceForm(p => ({ ...p, amount: v }))} placeholder="50000" type="number" />
                <FInput label="Amount Paid (SAR)" value={invoiceForm.paid} onChange={v => setInvoiceForm(p => ({ ...p, paid: v }))} placeholder="0" type="number" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Status</div>
                <select value={invoiceForm.status} onChange={e => setInvoiceForm(p => ({ ...p, status: e.target.value }))} style={{ ...s.input, cursor: 'pointer' }}>
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Upload Invoice/Quotation (PDF) - Max 5MB</div>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(e, 'invoices')} disabled={uploading} style={{ color: GRAY }} />
                {uploading && <div style={{ color: O, fontSize: 15, marginTop: 8 }}>⏳ {uploadProgress || 'Uploading...'}</div>}
                {invoiceForm.fileUrl && !uploading && <div style={{ color: '#22c55e', fontSize: 15, marginTop: 8 }}>✓ File uploaded</div>}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setShowAddInvoice(false)} style={{ ...s.ctaOutline, flex: 1 }}>Cancel</button>
                <button onClick={createInvoice} disabled={uploading} style={{ ...s.cta, flex: 1, opacity: uploading ? 0.6 : 1 }}>{uploading ? 'Uploading...' : 'Create'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Extra Order Modal */}
      {showAddExtra && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>New Extra Order</h2>
              <button onClick={() => setShowAddExtra(false)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Client Email *</div>
                <select value={extraForm.clientEmail} onChange={e => setExtraForm(p => ({ ...p, clientEmail: e.target.value }))} style={{ ...s.input, cursor: 'pointer' }}>
                  <option value="">Select client...</option>
                  {clientEmails.map(email => <option key={email} value={email}>{email}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Project Name</div>
                <select value={extraForm.projectName} onChange={e => setExtraForm(p => ({ ...p, projectName: e.target.value }))} style={{ ...s.input, cursor: 'pointer' }}>
                  <option value="">Select project...</option>
                  {projects.filter(p => p.clientEmail === extraForm.clientEmail).map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>
              <FInput label="Description" value={extraForm.description} onChange={v => setExtraForm(p => ({ ...p, description: v }))} placeholder="Additional LED Signage, Rush Fee, etc." />
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Type</div>
                <select value={extraForm.type} onChange={e => setExtraForm(p => ({ ...p, type: e.target.value }))} style={{ ...s.input, cursor: 'pointer' }}>
                  <option value="Addition">Addition</option>
                  <option value="Change Order">Change Order</option>
                  <option value="Rush Fee">Rush Fee</option>
                  <option value="Material Upgrade">Material Upgrade</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FInput label="Amount (SAR) *" value={extraForm.amount} onChange={v => setExtraForm(p => ({ ...p, amount: v }))} placeholder="5000" type="number" />
                <FInput label="Paid (SAR)" value={extraForm.paid} onChange={v => setExtraForm(p => ({ ...p, paid: v }))} placeholder="0" type="number" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={s.inputLabel}>Upload Document (PDF)</div>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(e, 'extraOrders')} style={{ color: GRAY }} />
                {extraForm.fileUrl && <div style={{ color: '#22c55e', fontSize: 15, marginTop: 8 }}>✓ File uploaded</div>}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setShowAddExtra(false)} style={{ ...s.ctaOutline, flex: 1 }}>Cancel</button>
                <button onClick={createExtraOrder} disabled={uploading} style={{ ...s.cta, flex: 1 }}>{uploading ? 'Uploading...' : 'Create'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notify Client - Invoice Created */}
      {createdInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 400, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(34,197,94,0.1)', borderBottom: '1px solid rgba(34,197,94,0.2)', padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>Invoice Created!</h2>
              <p style={{ color: GRAY, fontSize: 15, marginTop: 8 }}>Notify the client?</p>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ color: GRAY, fontSize: 14 }}>Client: <span style={{ color: WHITE, fontWeight: 600 }}>{createdInvoice.clientEmail}</span></div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 8 }}>Project: <span style={{ color: WHITE, fontWeight: 600 }}>{createdInvoice.projectName || '-'}</span></div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 8 }}>Amount: <span style={{ color: O, fontWeight: 700 }}>SAR {parseFloat(createdInvoice.amount || 0).toLocaleString()}</span></div>
              </div>
              <button onClick={() => {
                const msg = `Hello,\n\n📄 New Invoice from ImpressaBuild\n\n🏗 Project: ${createdInvoice.projectName || 'Your Project'}\n💰 Amount: SAR ${parseFloat(createdInvoice.amount || 0).toLocaleString()}\n📝 ${createdInvoice.description || 'Invoice'}\n\n🔗 View & pay online: https://app.impressabuild.com\n\nBank Details:\n🏦 ImpressaBuild Solutions Company\n🏛 Saudi National Bank\n📝 IBAN: SA5810000010400000923106\n\nBest regards,\nImpressaBuild Team`;
                const phone = createdInvoice.clientPhone ? createdInvoice.clientPhone.replace(/[^0-9]/g, '') : '';
                window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
              }} style={{ width: '100%', padding: '14px 20px', background: '#25D366', border: 'none', borderRadius: 12, color: WHITE, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
                📱 Notify via WhatsApp
              </button>
              <button onClick={() => setCreatedInvoice(null)} style={{ ...s.ctaOutline, width: '100%' }}>Skip</button>
            </div>
          </div>
        </div>
      )}

      {/* Notify Client - Extra Order Created */}
      {createdExtra && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 400, overflow: 'hidden' }}>
            <div style={{ background: 'rgba(249,115,22,0.1)', borderBottom: '1px solid rgba(249,115,22,0.2)', padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>Extra Order Created!</h2>
              <p style={{ color: GRAY, fontSize: 15, marginTop: 8 }}>Notify the client?</p>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ color: GRAY, fontSize: 14 }}>Client: <span style={{ color: WHITE, fontWeight: 600 }}>{createdExtra.clientEmail}</span></div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 8 }}>Type: <span style={{ color: WHITE, fontWeight: 600 }}>{createdExtra.type}</span></div>
                <div style={{ color: GRAY, fontSize: 14, marginTop: 8 }}>Amount: <span style={{ color: O, fontWeight: 700 }}>SAR {parseFloat(createdExtra.amount || 0).toLocaleString()}</span></div>
              </div>
              <button onClick={() => {
                const msg = `Hello,\n\n📋 Extra Order from ImpressaBuild\n\n🏗 Project: ${createdExtra.projectName || 'Your Project'}\n📝 Type: ${createdExtra.type}\n💰 Amount: SAR ${parseFloat(createdExtra.amount || 0).toLocaleString()}\n${createdExtra.description ? `📄 Details: ${createdExtra.description}` : ''}\n\n🔗 View & pay online: https://app.impressabuild.com\n\nBest regards,\nImpressaBuild Team`;
                const phone = createdExtra.clientPhone ? createdExtra.clientPhone.replace(/[^0-9]/g, '') : '';
                window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
              }} style={{ width: '100%', padding: '14px 20px', background: '#25D366', border: 'none', borderRadius: 12, color: WHITE, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
                📱 Notify via WhatsApp
              </button>
              <button onClick={() => setCreatedExtra(null)} style={{ ...s.ctaOutline, width: '100%' }}>Skip</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminArchive({ projects, notify }) {
  const unarchiveProject = async (projectId) => {
    try {
      await updateDoc(doc(db, 'projects', projectId), { archived: false, updatedAt: serverTimestamp() });
      notify('Project restored');
    } catch (err) { notify('Error'); }
  };

  return (
    <div>
      <h1 style={{ color: WHITE, fontSize: 28, fontWeight: 700, marginBottom: 32 }}>Archive</h1>
      {projects.map(project => (
        <div key={project.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 24, marginBottom: 16, opacity: 0.7 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ color: WHITE, fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>{project.name}</h3>
              <div style={{ color: GRAY, fontSize: 14 }}>{project.clientName}</div>
            </div>
            <button onClick={() => unarchiveProject(project.id)} style={{ padding: '10px 20px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 8, color: O, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>Restore</button>
          </div>
        </div>
      ))}
      {projects.length === 0 && <div style={{ color: GRAY, textAlign: 'center', padding: 40 }}>No archived projects</div>}
    </div>
  );
}

function CreateProjectModal({ onClose, notify, pmUser }) {
  const [form, setForm] = useState({ name: '', clientName: '', company: '', clientEmail: '', clientPhone: '', type: 'Exhibition Stand', priority: 'medium', stage: 'design', quotationUrl: '', quotationName: '', quotationAmount: '', downPaymentPercent: '50' });
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [clients, setClients] = useState([]);

  // Fetch clients from users collection
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        const list = snap.docs.map(d => d.data()).filter(u => u.email && u.email !== ADMIN_EMAIL);
        setClients(list);
      } catch (e) {}
    };
    fetchClients();
  }, []);

  const selectClient = (email) => {
    const client = clients.find(c => c.email === email);
    if (client) {
      setForm(p => ({
        ...p,
        clientName: client.name || '',
        company: client.company || '',
        clientEmail: client.email || '',
        clientPhone: client.phone || ''
      }));
    }
  };

  const handleQuotationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      notify('File too large. Max 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/quotations');
      
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
        { method: 'POST', body: formData }
      );
      
      const data = await response.json();
      if (data.secure_url) {
        setForm(p => ({ ...p, quotationUrl: data.secure_url, quotationName: file.name }));
        notify('Quotation uploaded!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error(err);
      notify('Upload failed');
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.clientEmail) { 
      notify('Please fill Project Name and select a Client'); 
      return; 
    }
    setSaving(true);
    try {
      const hasQuotation = form.quotationUrl && form.quotationAmount;
      
      if (hasQuotation) {
        // Create as order with quotation → client must approve
        await addDoc(collection(db, 'orders'), {
          userEmail: form.clientEmail.toLowerCase().trim(),
          userName: form.clientName,
          company: form.company,
          clientPhone: form.clientPhone.replace(/\s/g, '').replace(/^0/, '966') || null,
          projectName: form.name,
          category: form.type,
          type: form.type,
          status: 'approved',
          quoteStage: 5,
          quotationFileUrl: form.quotationUrl,
          quotationFileName: form.quotationName,
          quotationAmount: parseFloat(form.quotationAmount) || 0,
          firstInvoicePercent: parseInt(form.downPaymentPercent) || 50,
          quotationStatus: 'pending_approval',
          clientNotification: 'Quotation ready for review',
          ...(pmUser ? { pmEmail: pmUser.email, pmName: pmUser.name || '' } : {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        // Notify client
        createPushNotification(form.clientEmail.toLowerCase().trim(), 'quotation_ready', 'Quotation Ready', 'A quotation for "' + form.name + '" is ready for your review. Please log in to approve.', form.name);
        notify('Quotation sent to client for approval!');
      } else {
        // No quotation yet — create as draft order
        await addDoc(collection(db, 'orders'), {
          userEmail: form.clientEmail.toLowerCase().trim(),
          userName: form.clientName,
          company: form.company,
          clientPhone: form.clientPhone.replace(/\s/g, '').replace(/^0/, '966') || null,
          projectName: form.name,
          category: form.type,
          type: form.type,
          status: 'approved',
          quoteStage: 0,
          quotationStatus: 'pending',
          ...(pmUser ? { pmEmail: pmUser.email, pmName: pmUser.name || '' } : {}),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        notify('Project created as draft — upload quotation in Orders tab to send to client.');
      }
      
      if (form.clientEmail) {
        setCreatedProject({ name: form.name, clientEmail: form.clientEmail.toLowerCase().trim(), clientPhone: form.clientPhone.replace(/\s/g, '').replace(/^0/, '966') || null });
        setShowInvite(true);
      } else {
        onClose();
      }
    } catch (err) { 
      console.error('Error creating project:', err);
      notify('Error: ' + err.message); 
    }
    setSaving(false);
  };

  const sendInvitation = (method) => {
    const appUrl = 'https://app.impressabuild.com';
    const msg = `Hello,

Welcome to ImpressaBuild! Your client portal is ready.

🔗 Login: ${appUrl}
📧 Email: ${createdProject.clientEmail}
🔑 Password: 123456

Select "Client Portal" to access your project: ${createdProject.name}

⚠️ You will be asked to set a new password on your first login.

For any questions, contact us at contact@impressabuild.com

Best regards,
ImpressaBuild Team`;

    if (method === 'whatsapp') {
      const phone = createdProject.clientPhone ? createdProject.clientPhone.replace(/[^0-9]/g, '') : '';
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else {
      window.open(`mailto:${createdProject.clientEmail}?subject=${encodeURIComponent('ImpressaBuild - Your Client Portal Access')}&body=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  if (showInvite && createdProject) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 420, overflow: 'hidden' }}>
          <div style={{ background: OGLOW, borderBottom: '1px solid ' + OBORDER, padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h2 style={{ color: WHITE, fontSize: 22, fontWeight: 700, margin: 0 }}>Project Created!</h2>
            <p style={{ color: GRAY, fontSize: 14, marginTop: 8 }}>Send login invitation to client?</p>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ background: CARD, borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ color: GRAY, fontSize: 14, marginBottom: 4 }}>Project</div>
              <div style={{ color: WHITE, fontWeight: 600 }}>{createdProject.name}</div>
              <div style={{ color: GRAY, fontSize: 14, marginTop: 12, marginBottom: 4 }}>Client Email</div>
              <div style={{ color: O, fontWeight: 600 }}>{createdProject.clientEmail}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <button onClick={() => sendInvitation('whatsapp')} style={{ flex: 1, background: '#25D366', border: 'none', borderRadius: 12, padding: '14px 16px', color: WHITE, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                📱 WhatsApp
              </button>
              <button onClick={() => sendInvitation('email')} style={{ flex: 1, background: '#3b82f6', border: 'none', borderRadius: 12, padding: '14px 16px', color: WHITE, fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                ✉️ Email
              </button>
            </div>
            <button onClick={onClose} style={{ ...s.ctaOutline, width: '100%' }}>Skip</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: 24, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ color: WHITE, fontSize: 22, fontWeight: 700, margin: 0 }}>New Project / Quotation</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          <FInput label="Project Name *" value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="GITEX Booth 2026" />
          
          {/* Client Selector */}
          <div style={{ marginBottom: 18 }}>
            <div style={s.inputLabel}>Select Client *</div>
            <select value={form.clientEmail} onChange={e => selectClient(e.target.value)} style={{ ...s.input, cursor: 'pointer' }}>
              <option value="">-- Select a client --</option>
              {clients.map(c => (
                <option key={c.email} value={c.email}>{c.company || c.name} ({c.email})</option>
              ))}
            </select>
          </div>
          
          {form.clientEmail && (
            <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 14, marginBottom: 18 }}>
              <div style={{ color: O, fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>CLIENT INFO</div>
              <div style={{ color: WHITE, fontSize: 14 }}><span style={{ color: GRAY }}>Name: </span>{form.clientName}</div>
              <div style={{ color: WHITE, fontSize: 14, marginTop: 4 }}><span style={{ color: GRAY }}>Company: </span>{form.company || '-'}</div>
              <div style={{ color: WHITE, fontSize: 14, marginTop: 4 }}><span style={{ color: GRAY }}>Email: </span>{form.clientEmail}</div>
              <div style={{ color: WHITE, fontSize: 14, marginTop: 4 }}><span style={{ color: GRAY }}>Phone: </span>{form.clientPhone || '-'}</div>
            </div>
          )}
          
          <div style={{ marginBottom: 18 }}>
            <div style={s.inputLabel}>Category</div>
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={{ ...s.input, cursor: 'pointer' }}>
              {CATEGORIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>

          {/* Quotation Upload (Optional) */}
          <div style={{ marginBottom: 18 }}>
            <div style={s.inputLabel}>Quotation PDF (Optional — upload to send for client approval)</div>
            {form.quotationUrl ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: CARD, border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, padding: '12px 16px' }}>
                <span style={{ fontSize: 20 }}>📄</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.quotationName}</div>
                  <div style={{ color: '#22c55e', fontSize: 14 }}>Uploaded</div>
                </div>
                <button onClick={() => setForm(p => ({ ...p, quotationUrl: '', quotationName: '' }))} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '6px 12px', color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
              </div>
            ) : (
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: CARD, border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 12, padding: '16px 20px', cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}>
                <span style={{ fontSize: 20 }}>{uploading ? '⏳' : '📎'}</span>
                <span style={{ color: GRAY, fontSize: 14 }}>{uploading ? 'Uploading...' : 'Upload PDF or Image'}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleQuotationUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
            )}
          </div>
          
          {/* Quotation Amount & Down Payment - shown when quotation uploaded */}
          {form.quotationUrl && (
            <div style={{ background: 'rgba(255,107,0,0.05)', border: '1px solid ' + OBORDER, borderRadius: 12, padding: 16, marginBottom: 18 }}>
              <div style={{ color: O, fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>QUOTATION DETAILS</div>
              <FInput label="Total Amount (SAR) *" value={form.quotationAmount} onChange={v => setForm(p => ({ ...p, quotationAmount: v }))} placeholder="e.g. 50000" />
              <div style={{ marginBottom: 0 }}>
                <div style={s.inputLabel}>Down Payment %</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="number" min="1" max="100" value={form.downPaymentPercent} onChange={e => setForm(p => ({ ...p, downPaymentPercent: e.target.value }))} placeholder="50" style={{ ...s.input, marginBottom: 0, flex: 1 }} />
                  <span style={{ color: GRAY, fontSize: 14 }}>%</span>
                </div>
                {form.quotationAmount && form.downPaymentPercent && (
                  <div style={{ color: O, fontSize: 13, fontWeight: 600, marginTop: 6 }}>Down Payment: SAR {Math.round(parseFloat(form.quotationAmount) * parseFloat(form.downPaymentPercent) / 100).toLocaleString()}</div>
                )}
              </div>
            </div>
          )}

          {/* Info text */}
          <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10, padding: 12, marginBottom: 16 }}>
            <div style={{ color: '#3b82f6', fontSize: 12, lineHeight: 1.5 }}>
              {form.quotationUrl && form.quotationAmount ? 
                '📋 Quotation will be sent to client for approval. Project is created only after client approves.' :
                '📝 This will create a draft order. You can upload the quotation later from the Orders tab.'
              }
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button onClick={onClose} style={{ ...s.ctaOutline, flex: 1 }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving || uploading} style={{ ...s.cta, flex: 1, opacity: (saving || uploading) ? 0.6 : 1 }}>{saving ? 'Sending...' : form.quotationUrl && form.quotationAmount ? 'Send for Approval' : 'Create Draft'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubTaskManager({ projectId, projectName, notify }) {
  const [tasks, setTasks] = useState([]);
  const [subs, setSubs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', subEmail: '', deadline: '', amount: '' });
  const [uploading, setUploading] = useState(false);
  const [briefFiles, setBriefFiles] = useState([]);
  const [payAmounts, setPayAmounts] = useState({});

  useEffect(() => {
    const q = query(collection(db, 'subTasks'), where('projectId', '==', projectId));
    const unsub = onSnapshot(q, (snap) => { setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() }))); });
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    (async () => { try { const snap = await getDocs(collection(db, 'subcontractors')); setSubs(snap.docs.map(d => ({ id: d.id, ...d.data() }))); } catch(e) {} })();
  }, []);

  const uploadBriefFile = async (e) => {
    const file = e.target.files[0]; if (!file) return; setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET); fd.append('folder', 'impressabuild/briefs');
      const data = await (await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`, { method: 'POST', body: fd })).json();
      if (data.secure_url) setBriefFiles(prev => [...prev, { url: data.secure_url, name: file.name }]);
    } catch(err) { notify('Upload failed'); } setUploading(false);
  };

  const addTask = async () => {
    if (!taskForm.title || !taskForm.subEmail) { notify('Title and partner required'); return; }
    const sub = subs.find(s => s.email === taskForm.subEmail);
    try {
      await addDoc(collection(db, 'subTasks'), {
        projectId, projectName, title: taskForm.title, description: taskForm.description,
        subEmail: taskForm.subEmail, subName: sub?.name || taskForm.subEmail, subCompany: sub?.company || '', subCategory: sub?.category || '',
        deadline: taskForm.deadline, amount: parseFloat(taskForm.amount) || 0, briefFiles,
        orderStatus: 'sent', partnerQuoteUrl: null, partnerQuoteAmount: 0,
        adminApproved: false, paid: false, paidAmount: 0, progress: 0, photos: [], createdAt: serverTimestamp()
      });
      createPushNotification(taskForm.subEmail, 'task_assigned', 'New Work Order', 'You have a new work order: "' + taskForm.title + '" for project "' + projectName + '". Please review and accept or reject.', projectName);
      notify('Work order sent!'); setShowAdd(false);
      setTaskForm({ title: '', description: '', subEmail: '', deadline: '', amount: '' }); setBriefFiles([]);
    } catch(e) { notify('Error: ' + e.message); }
  };

  const P_BG = '#1a1028';
  const P_BORDER = 'rgba(139,92,246,0.35)';
  const P_COLOR = '#a78bfa';

  return (
    <div style={{ marginBottom: 24, background: P_BG, border: '2px solid ' + P_BORDER, borderRadius: 16, overflow: 'hidden' }}>
      {/* Header - Collapsible */}
      <button onClick={() => setExpanded(!expanded)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(139,92,246,0.08)', border: 'none', borderBottom: expanded ? '1px solid ' + P_BORDER : 'none', cursor: 'pointer', fontFamily: FONT }}>
        <span style={{ fontSize: 20 }}>🔧</span>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ color: P_COLOR, fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>PARTNER WORK ORDERS</div>
          <div style={{ color: GRAY, fontSize: 15, marginTop: 2 }}>{tasks.length} order(s)</div>
        </div>
        <span style={{ color: P_COLOR, fontSize: 16 }}>{expanded ? '▼' : '▶'}</span>
      </button>

      {expanded && (
        <div style={{ padding: 14 }}>
          {/* Add button */}
          <button onClick={() => setShowAdd(!showAdd)} style={{ width: '100%', padding: '10px', background: 'rgba(139,92,246,0.12)', border: '1px dashed ' + P_BORDER, borderRadius: 10, color: P_COLOR, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, marginBottom: 12 }}>{showAdd ? '✕ Cancel' : '+ Send New Work Order'}</button>

          {/* Add form */}
          {showAdd && (
            <div style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid ' + P_BORDER, borderRadius: 12, padding: 14, marginBottom: 14 }}>
              <input value={taskForm.title} onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))} placeholder="Work order title *" style={{ ...s.input, marginBottom: 8, fontSize: 13 }} />
              <textarea value={taskForm.description} onChange={e => setTaskForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief / scope of work..." rows={3} style={{ ...s.input, marginBottom: 8, fontSize: 15, resize: 'none' }} />
              <select value={taskForm.subEmail} onChange={e => setTaskForm(p => ({ ...p, subEmail: e.target.value }))} style={{ ...s.input, marginBottom: 8, fontSize: 15, cursor: 'pointer' }}>
                <option value="">-- Select Partner --</option>
                {subs.map(sub => <option key={sub.email} value={sub.email}>{sub.name} ({sub.category})</option>)}
              </select>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input type="date" value={taskForm.deadline} onChange={e => setTaskForm(p => ({ ...p, deadline: e.target.value }))} style={{ ...s.input, marginBottom: 0, fontSize: 13 }} />
                <input type="number" value={taskForm.amount} onChange={e => setTaskForm(p => ({ ...p, amount: e.target.value }))} placeholder="Budget (SAR)" style={{ ...s.input, marginBottom: 0, fontSize: 13 }} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', background: CARD, border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 8, cursor: 'pointer', marginBottom: 8, opacity: uploading ? 0.6 : 1 }}>
                <span style={{ fontSize: 13 }}>{uploading ? '⏳' : '📎'}</span>
                <span style={{ color: GRAY, fontSize: 12 }}>{uploading ? 'Uploading...' : 'Attach files'}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={uploadBriefFile} style={{ display: 'none' }} disabled={uploading} />
              </label>
              {briefFiles.length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>{briefFiles.map((f, i) => (
                <div key={i} style={{ padding: '3px 8px', background: 'rgba(139,92,246,0.1)', border: '1px solid ' + P_BORDER, borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: P_COLOR, fontSize: 10 }}>{f.name.substring(0, 15)}</span>
                  <button onClick={() => setBriefFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 14, cursor: 'pointer' }}>×</button>
                </div>
              ))}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={addTask} style={{ flex: 1, padding: '10px', background: '#8b5cf6', border: 'none', borderRadius: 8, color: WHITE, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Send Work Order</button>
                <button onClick={async () => {
                  if (!taskForm.title || !taskForm.subEmail) { notify('Title and partner required'); return; }
                  if (!taskForm.amount) { notify('Amount required for direct assign'); return; }
                  const sub = subs.find(ss => ss.email === taskForm.subEmail);
                  try {
                    await addDoc(collection(db, 'subTasks'), {
                      projectId, projectName, title: taskForm.title, description: taskForm.description,
                      subEmail: taskForm.subEmail, subName: sub?.name || taskForm.subEmail, subCompany: sub?.company || '', subCategory: sub?.category || '',
                      deadline: taskForm.deadline, amount: parseFloat(taskForm.amount) || 0, briefFiles,
                      orderStatus: 'approved', partnerQuoteUrl: null, partnerQuoteAmount: parseFloat(taskForm.amount) || 0,
                      adminApproved: true, paid: false, paidAmount: 0, progress: 0, photos: [], createdAt: serverTimestamp()
                    });
                    createPushNotification(taskForm.subEmail, 'task_assigned', 'Work Assigned', 'You have been assigned: "' + taskForm.title + '" for "' + projectName + '". Amount: SAR ' + (parseFloat(taskForm.amount) || 0).toLocaleString(), projectName);
                    notify('Directly assigned!'); setShowAdd(false);
                    setTaskForm({ title: '', description: '', subEmail: '', deadline: '', amount: '' }); setBriefFiles([]);
                  } catch(e) { notify('Error: ' + e.message); }
                }} style={{ flex: 1, padding: '10px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: '#22c55e', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Direct Assign</button>
              </div>
            </div>
          )}

          {/* Task cards */}
          {tasks.length === 0 && !showAdd && <div style={{ color: GRAY, textAlign: 'center', padding: 16, fontSize: 13 }}>No work orders sent yet</div>}
          {tasks.map(task => {
            const due = (task.amount || 0) - (task.paidAmount || 0);
            return (
            <div key={task.id} style={{ background: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, marginBottom: 8, overflow: 'hidden' }}>
              {/* Card header */}
              <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{task.title}</div>
                  <div style={{ color: P_COLOR, fontSize: 15, marginTop: 2 }}>{task.subCompany || task.subName} • {task.subCategory}</div>
                  {task.deadline && <div style={{ color: GRAY, fontSize: 14, marginTop: 2 }}>Due: {task.deadline}</div>}
                </div>
                <span style={{ padding: '5px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                  background: task.orderStatus === 'sent' ? 'rgba(59,130,246,0.15)' : task.orderStatus === 'accepted' ? 'rgba(245,158,11,0.15)' : task.orderStatus === 'quoted' ? 'rgba(139,92,246,0.2)' : task.orderStatus === 'approved' || task.orderStatus === 'in_progress' ? 'rgba(34,197,94,0.15)' : task.orderStatus === 'rejected_by_partner' || task.orderStatus === 'quote_rejected' ? 'rgba(239,68,68,0.15)' : 'rgba(139,92,246,0.15)',
                  color: task.orderStatus === 'sent' ? '#3b82f6' : task.orderStatus === 'accepted' ? '#f59e0b' : task.orderStatus === 'quoted' ? '#a78bfa' : task.orderStatus === 'approved' || task.orderStatus === 'in_progress' ? '#22c55e' : task.orderStatus === 'rejected_by_partner' || task.orderStatus === 'quote_rejected' ? '#ef4444' : '#8b5cf6'
                }}>{task.orderStatus === 'sent' ? 'Sent' : task.orderStatus === 'accepted' ? 'Accepted' : task.orderStatus === 'quoted' ? 'Quote In' : task.orderStatus === 'approved' ? 'Approved' : task.orderStatus === 'in_progress' ? task.progress + '%' : task.orderStatus === 'rejected_by_partner' ? 'Declined' : task.orderStatus === 'quote_rejected' ? 'Rejected' : task.progress >= 100 ? 'Done' : 'Pending'}</span>
              </div>

              {/* Financials row */}
              {(task.amount > 0 || task.partnerQuoteAmount > 0) && (
                <div style={{ padding: '0 14px 10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 8 }}>
                  {task.amount > 0 && <div style={{ background: 'rgba(139,92,246,0.08)', borderRadius: 8, padding: '6px 10px' }}><div style={{ color: GRAY, fontSize: 11 }}>Budget</div><div style={{ color: P_COLOR, fontSize: 14, fontWeight: 700 }}>SAR {task.amount.toLocaleString()}</div></div>}
                  {task.partnerQuoteAmount > 0 && <div style={{ background: 'rgba(255,107,0,0.08)', borderRadius: 8, padding: '6px 10px' }}><div style={{ color: GRAY, fontSize: 11 }}>Quote</div><div style={{ color: O, fontSize: 14, fontWeight: 700 }}>SAR {task.partnerQuoteAmount.toLocaleString()}</div></div>}
                  {task.paidAmount > 0 && <div style={{ background: 'rgba(34,197,94,0.08)', borderRadius: 8, padding: '6px 10px' }}><div style={{ color: GRAY, fontSize: 11 }}>Paid</div><div style={{ color: '#22c55e', fontSize: 14, fontWeight: 700 }}>SAR {task.paidAmount.toLocaleString()}</div></div>}
                  {task.adminApproved && due > 0 && <div style={{ background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '6px 10px' }}><div style={{ color: GRAY, fontSize: 11 }}>Due</div><div style={{ color: '#f59e0b', fontSize: 14, fontWeight: 700 }}>SAR {due.toLocaleString()}</div></div>}
                </div>
              )}

              {/* Progress bar */}
              {(task.orderStatus === 'approved' || task.orderStatus === 'in_progress') && task.progress > 0 && (
                <div style={{ padding: '0 14px 10px' }}>
                  <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
                    <div style={{ height: 4, width: task.progress + '%', background: task.progress >= 100 ? '#22c55e' : '#8b5cf6', borderRadius: 2 }} />
                  </div>
                </div>
              )}

              {/* Photos */}
              {task.photos && task.photos.length > 0 && (
                <div style={{ padding: '0 14px 10px', display: 'flex', gap: 4 }}>
                  {task.photos.slice(0, 5).map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" style={{ width: 36, height: 36, borderRadius: 4, overflow: 'hidden' }}>
                      <img src={p.url.replace('/upload/', '/upload/w_72,h_72,c_fill/')} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </a>
                  ))}
                </div>
              )}

              {/* Actions bar */}
              <div style={{ padding: '8px 14px', background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(139,92,246,0.1)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Quote received - approve/reject */}
                {task.orderStatus === 'quoted' && (<>
                  {task.partnerQuoteUrl && <a href={task.partnerQuoteUrl} target="_blank" rel="noopener noreferrer" style={{ padding: '8px 14px', background: 'rgba(139,92,246,0.1)', border: '1px solid ' + P_BORDER, borderRadius: 6, color: P_COLOR, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>📄 View</a>}
                  <button onClick={async () => {
                    await updateDoc(doc(db, 'subTasks', task.id), { orderStatus: 'approved', adminApproved: true, amount: task.partnerQuoteAmount || task.amount, updatedAt: serverTimestamp() });
                    createPushNotification(task.subEmail, 'quote_approved', 'Quote Approved!', 'Your quote for "' + task.title + '" has been approved.', task.projectName);
                    notify('Approved!');
                  }} style={{ padding: '8px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>✅ Approve</button>
                  <button onClick={async () => {
                    await updateDoc(doc(db, 'subTasks', task.id), { orderStatus: 'quote_rejected', updatedAt: serverTimestamp() });
                    createPushNotification(task.subEmail, 'quote_rejected', 'Quote Rejected', 'Your quote for "' + task.title + '" was rejected.', task.projectName);
                    notify('Rejected');
                  }} style={{ padding: '8px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, color: '#ef4444', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>✗ Reject</button>
                </>)}

                {/* Payment - partial amount input */}
                {task.adminApproved && due > 0 && (
                  <div style={{ width: '100%', marginTop: 4 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="number" placeholder={due.toLocaleString()} value={payAmounts[task.id] || ''} onChange={e => setPayAmounts(p => ({ ...p, [task.id]: e.target.value }))} style={{ flex: 1, padding: '10px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: WHITE, fontSize: 15, fontFamily: FONT }} />
                      <button onClick={async () => {
                        const payAmt = Math.round((parseFloat(payAmounts[task.id]) || due) * 100) / 100;
                        const actualPay = Math.min(Math.max(payAmt, 1), due);
                        const newPaid = Math.round(((task.paidAmount || 0) + actualPay) * 100) / 100;
                        const fullyPaid = newPaid >= (task.amount || 0);
                        await updateDoc(doc(db, 'subTasks', task.id), { paidAmount: newPaid, paid: fullyPaid, paidAt: serverTimestamp() });
                        createPushNotification(task.subEmail, 'payment_sent', 'Payment Received', 'Payment of SAR ' + actualPay.toLocaleString() + ' for "' + task.title + '" has been sent. ' + (fullyPaid ? 'Fully paid.' : 'Remaining: SAR ' + (task.amount - newPaid).toLocaleString()), task.projectName);
                        notify('Payment of SAR ' + actualPay.toLocaleString() + ' recorded!');
                        setPayAmounts(p => ({ ...p, [task.id]: '' }));
                      }} style={{ padding: '10px 20px', background: '#22c55e', border: 'none', borderRadius: 8, color: WHITE, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Pay</button>
                    </div>
                  </div>
                )}

                {task.paid && <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 600 }}>✅ Fully Paid</span>}

                <button onClick={async () => { if (window.confirm('Delete?')) { await deleteDoc(doc(db, 'subTasks', task.id)); notify('Deleted'); } }} style={{ marginLeft: 'auto', padding: '7px 12px', background: 'none', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 6, color: '#ef4444', fontSize: 13, cursor: 'pointer', fontFamily: FONT }}>🗑</button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChangeOrderCard({ cr, crStatus, notify }) {
  const [amount, setAmount] = useState(cr.quotationAmount || '');
  const [workProg, setWorkProg] = useState(cr.workProgress || 0);
  const [uploading, setUploading] = useState(false);

  const uploadQuotation = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/change-orders');
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.secure_url) {
        await updateDoc(doc(db, 'changeRequests', cr.id), { quotationFileUrl: data.secure_url, quotationFileName: file.name });
        notify('Quotation uploaded!');
      }
    } catch (err) { notify('Upload failed'); }
    setUploading(false);
  };

  return (
    <div style={{ background: 'rgba(139,92,246,0.05)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 12, padding: 14, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ color: WHITE, fontSize: 14, fontWeight: 700 }}>{cr.title}</div>
        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: crStatus === 'approved' ? 'rgba(34,197,94,0.15)' : crStatus === 'awaiting' ? 'rgba(245,158,11,0.15)' : crStatus === 'rejected' ? 'rgba(239,68,68,0.15)' : 'rgba(139,92,246,0.15)', color: crStatus === 'approved' ? '#22c55e' : crStatus === 'awaiting' ? '#f59e0b' : crStatus === 'rejected' ? '#ef4444' : '#8b5cf6' }}>{crStatus === 'approved' ? 'Approved' : crStatus === 'awaiting' ? 'Awaiting Client' : crStatus === 'rejected' ? 'Rejected' : 'Pending'}</span>
      </div>
      <div style={{ color: GRAY, fontSize: 13 }}>{cr.description}</div>
      {cr.quotationAmount > 0 && <div style={{ color: O, fontSize: 14, fontWeight: 700, marginTop: 4 }}>SAR {cr.quotationAmount.toLocaleString()}</div>}
      {cr.clientComment && <div style={{ color: GRAY, fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>Client: "{cr.clientComment}"</div>}
      {cr.quotationFileUrl && <a href={cr.quotationFileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '4px 10px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 6, color: O, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>📄 {cr.quotationFileName || 'View Quotation'}</a>}
      
      {/* Pending: Enter cost, upload quotation, send */}
      {crStatus === 'pending' && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <input type="number" placeholder="Cost (SAR)" value={amount} onChange={e => setAmount(e.target.value)} style={{ flex: 1, padding: '8px 12px', background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: WHITE, fontSize: 13, fontFamily: FONT }} />
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', background: CARD, border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 8, cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}>
              <span style={{ fontSize: 14 }}>{uploading ? '⏳' : '📎'}</span>
              <span style={{ color: GRAY, fontSize: 12 }}>{uploading ? 'Uploading...' : 'Upload Quotation (optional)'}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadQuotation} style={{ display: 'none' }} disabled={uploading} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={async () => {
              const amt = parseFloat(amount) || 0;
              if (!amt) { notify('Enter a cost'); return; }
              await updateDoc(doc(db, 'changeRequests', cr.id), { quotationAmount: amt, quotationStatus: 'pending_approval', updatedAt: serverTimestamp() });
              createPushNotification(cr.userEmail, 'cr_cost', 'Change Order Cost Submitted', 'A cost of SAR ' + amt.toLocaleString() + ' has been submitted for your change request on "' + cr.projectName + '". Please review and approve.', cr.projectName);
              notify('Cost sent for approval!');
            }} style={{ flex: 1, padding: '10px 14px', background: O, border: 'none', borderRadius: 8, color: BG, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Send Cost for Approval</button>
            <button onClick={async () => {
              await updateDoc(doc(db, 'changeRequests', cr.id), { status: 'rejected', updatedAt: serverTimestamp() });
              createPushNotification(cr.userEmail, 'cr_rejected', 'Change Request Rejected', 'Your change request for "' + cr.projectName + '" has been rejected.', cr.projectName);
              notify('Rejected');
            }} style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>Reject</button>
          </div>
        </div>
      )}
      
      {/* Approved: Work progress with Save & Send button */}
      {crStatus === 'approved' && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: GRAY, fontSize: 12 }}>Work Progress</span>
            <span style={{ color: O, fontSize: 13, fontWeight: 700 }}>{workProg}%</span>
          </div>
          <input type="range" min="0" max="100" value={workProg} onChange={e => setWorkProg(parseInt(e.target.value))} style={{ width: '100%', accentColor: O, marginBottom: 8 }} />
          <button onClick={async () => {
            await updateDoc(doc(db, 'changeRequests', cr.id), { workProgress: workProg, updatedAt: serverTimestamp() });
            createPushNotification(cr.userEmail, 'cr_progress', 'Change Order Progress', 'Work on your change request for "' + cr.projectName + '" is now at ' + workProg + '%.', cr.projectName);
            notify('Progress saved & client notified!');
          }} style={{ width: '100%', padding: '10px 14px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 8, color: O, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>Save & Send Update</button>
        </div>
      )}
    </div>
  );
}

function ProjectDetailModal({ project, changeRequests, onClose, notify }) {
  const [progress, setProgress] = useState(project.progress || 0);
  const [stage, setStage] = useState(project.stage || 'design');
  const [uploading, setUploading] = useState(false);
  const [pms, setPms] = useState([]);
  const [currentPm, setCurrentPm] = useState(project.pmEmail || '');

  useEffect(() => {
    (async () => {
      try { const snap = await getDocs(collection(db, 'projectManagers')); setPms(snap.docs.map(d => ({ id: d.id, ...d.data() }))); } catch(e) {}
    })();
  }, []);

  const assignPm = async (pmEmail) => {
    const pm = pms.find(p => p.email === pmEmail);
    setCurrentPm(pmEmail);
    try {
      await updateDoc(doc(db, 'projects', project.id), { pmEmail: pmEmail || null, pmName: pm?.name || null, updatedAt: serverTimestamp() });
      if (pmEmail && pm) {
        createPushNotification(pmEmail, 'pm_assigned', 'Project Assigned', 'You have been assigned to project "' + project.name + '".', project.name);
        notify('Assigned to ' + pm.name);
      } else {
        notify('PM unassigned');
      }
    } catch(e) { notify('Error'); }
  };

  const updateProject = async (updates) => {
    try {
      await updateDoc(doc(db, 'projects', project.id), { ...updates, updatedAt: serverTimestamp() });
      notify('Updated');
      // Push notification to client for progress or stage changes
      if (project.clientEmail) {
        if (updates.progress !== undefined) {
          createPushNotification(project.clientEmail, 'progress', 'Project Progress Updated', 'Project "' + project.name + '" is now at ' + updates.progress + '% progress', project.name);
        }
        if (updates.stage !== undefined) {
          const stageLabel = PROJECT_STAGES.find(s => s.id === updates.stage)?.label || updates.stage;
          createPushNotification(project.clientEmail, 'stage_update', 'Project Stage Updated', 'Project "' + project.name + '" has moved to: ' + stageLabel, project.name);
        }
      }
    } catch (err) { notify('Error'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', `impressabuild/projects/${project.id}`);
      
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      const data = await response.json();
      if (data.secure_url) {
        const photos = [...(project.photos || []), { url: data.secure_url, name: file.name, uploadedAt: new Date().toISOString() }];
        await updateDoc(doc(db, 'projects', project.id), { photos, updatedAt: serverTimestamp() });
        notify('Photo uploaded');
        // Push notification to client
        if (project.clientEmail) {
          createPushNotification(project.clientEmail, 'photo', 'New Photos Added', 'New photos uploaded to "' + project.name + '"', project.name);
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) { 
      console.error(err);
      notify('Upload failed'); 
    }
    setUploading(false);
  };

  const deletePhoto = async (index) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      const photos = [...(project.photos || [])];
      photos.splice(index, 1);
      await updateDoc(doc(db, 'projects', project.id), { photos, updatedAt: serverTimestamp() });
      notify('Photo deleted');
    } catch (err) { notify('Error deleting'); }
  };

  const handleQuotationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      notify('File too large. Max 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/quotations');
      const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`, { method: 'POST', body: formData });
      const data = await response.json();
      if (data.secure_url) {
        await updateDoc(doc(db, 'projects', project.id), { 
          quotationUrl: data.secure_url, 
          quotationName: file.name,
          updatedAt: serverTimestamp() 
        });
        notify('Quotation uploaded!');
        // Push notification to client
        if (project.clientEmail) {
          createPushNotification(project.clientEmail, 'quotation', 'Quotation Ready', 'A quotation has been uploaded for "' + project.name + '"', project.name);
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error(err);
      notify('Upload failed');
    }
    setUploading(false);
  };

  const deleteQuotation = async () => {
    if (!window.confirm('Delete quotation?')) return;
    try {
      await updateDoc(doc(db, 'projects', project.id), { 
        quotationUrl: null, 
        quotationName: null,
        updatedAt: serverTimestamp() 
      });
      notify('Quotation deleted');
    } catch (err) { notify('Error deleting'); }
  };

  const archiveProject = async () => {
    if (!window.confirm('Complete and archive this project? A thank you note will be sent to the client.')) return;
    try {
      await updateDoc(doc(db, 'projects', project.id), { archived: true, stage: 'complete', progress: 100, updatedAt: serverTimestamp() });
      // Push notification to client
      if (project.clientEmail) {
        createPushNotification(project.clientEmail, 'project_complete', 'Project Completed! 🎉', 'Thank you for choosing ImpressaBuild! Your project "' + project.name + '" has been completed and delivered. We look forward to working with you again.', project.name);
      }
      // Open WhatsApp with thank you message
      if (project.clientPhone) {
        const phone = project.clientPhone.replace(/^0/, '966').replace(/[^0-9]/g, '');
        const msg = `Dear ${project.clientName || 'Valued Client'},\n\n🎉 Congratulations!\n\nYour project *"${project.name}"* has been completed and delivered!\n\nThank you for choosing ImpressaBuild. We truly appreciate your trust in our work and we look forward to partnering with you again.\n\n⭐ If you have any feedback, we'd love to hear from you.\n\nBest regards,\nImpressaBuild Team`;
        window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
      }
      notify('Project archived & client notified');
      onClose();
    } catch (err) { notify('Error'); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: 24, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ color: WHITE, fontSize: 22, fontWeight: 700, margin: 0 }}>{project.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
            <div><div style={{ color: GRAY, fontSize: 15, marginBottom: 4 }}>Client</div><div style={{ color: WHITE, fontSize: 16, fontWeight: 600 }}>{project.clientName}</div></div>
            <div><div style={{ color: GRAY, fontSize: 15, marginBottom: 4 }}>Type</div><div style={{ color: WHITE, fontSize: 16, fontWeight: 600 }}>{project.type}</div></div>
          </div>

          {/* Project Manager Assignment */}
          {pms.length > 0 && (
            <div style={{ marginBottom: 24, background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: 14 }}>
              <div style={{ color: '#3b82f6', fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>👔 PROJECT MANAGER</div>
              <select value={currentPm} onChange={e => assignPm(e.target.value)} style={{ ...s.input, marginBottom: 0, cursor: 'pointer', fontSize: 14 }}>
                <option value="">-- Not Assigned --</option>
                {pms.map(pm => <option key={pm.email} value={pm.email}>{pm.name} ({pm.email})</option>)}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ color: GRAY, fontSize: 14, fontWeight: 600 }}>Progress</span>
              <span style={{ color: O, fontSize: 18, fontWeight: 700 }}>{progress}%</span>
            </div>
            <input type="range" min="0" max="100" value={progress} onChange={e => setProgress(parseInt(e.target.value))} style={{ width: '100%', accentColor: O }} />
            <button onClick={() => updateProject({ progress })} style={{ marginTop: 12, padding: '10px 16px', background: O, border: 'none', borderRadius: 8, color: BG, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Save & Send Progress Update</button>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ color: GRAY, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Stage</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {PROJECT_STAGES.map(st => (
                <button key={st.id} onClick={() => { setStage(st.id); updateProject({ stage: st.id }); }} style={{ padding: '8px 16px', borderRadius: 100, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, background: stage === st.id ? st.color + '20' : 'rgba(255,255,255,0.02)', color: stage === st.id ? st.color : GRAY, border: stage === st.id ? '1px solid ' + st.color + '40' : '1px solid rgba(255,255,255,0.06)' }}>{st.label}</button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ color: GRAY, fontSize: 14, fontWeight: 600 }}>Photos ({project.photos?.length || 0})</span>
              <label style={{ padding: '8px 16px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 8, color: O, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {uploading ? 'Uploading...' : '+ Upload'}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {(project.photos || []).map((photo, i) => (
                <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', background: CARD }}>
                  <img src={photo.url} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button onClick={() => deletePhoto(i)} style={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: '50%', background: 'rgba(239,68,68,0.9)', border: 'none', color: WHITE, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
              ))}
            </div>
          </div>

          {/* Quotation Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ color: GRAY, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Quotation</div>
            {project.quotationUrl ? (
              <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: CARD, border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, padding: '14px 16px', marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>📄</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.quotationName || 'Quotation'}</div>
                </div>
                <button onClick={deleteQuotation} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 14px', color: '#ef4444', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={googleViewerUrl(project.quotationUrl)} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, textDecoration: 'none', color: '#22c55e', fontWeight: 600, fontSize: 13, fontFamily: FONT }}>👁 View</a>
                <a href={project.quotationUrl} target="_blank" rel="noopener noreferrer" download style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 14px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8, textDecoration: 'none', color: '#3b82f6', fontWeight: 600, fontSize: 13, fontFamily: FONT }}>⬇ Download</a>
              </div>
              </>
            ) : (
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: CARD, border: '1px dashed rgba(255,255,255,0.2)', borderRadius: 12, padding: '18px 20px', cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}>
                <span style={{ fontSize: 20 }}>{uploading ? '⏳' : '📎'}</span>
                <span style={{ color: GRAY, fontSize: 14 }}>{uploading ? 'Uploading...' : 'Upload Quotation (PDF or Image)'}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleQuotationUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
            )}
          </div>

          {/* Change Requests for this project */}
          {(() => {
            const projectCRs = (changeRequests || []).filter(cr => cr.projectId === project.id || cr.projectName === project.name);
            if (projectCRs.length === 0) return null;
            return (
              <div style={{ marginBottom: 24 }}>
                <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>CHANGE ORDERS ({projectCRs.length})</div>
                {projectCRs.map(cr => {
                  const crStatus = cr.quotationStatus === 'approved' ? 'approved' : cr.quotationStatus === 'pending_approval' ? 'awaiting' : cr.status === 'rejected' ? 'rejected' : 'pending';
                  return (
                  <ChangeOrderCard key={cr.id} cr={cr} crStatus={crStatus} notify={notify} />
                  );
                })}
              </div>
            );
          })()}

          {/* Complete & Archive + Notify Client */}
          <div style={{ display: 'flex', gap: 12, paddingTop: 16, marginBottom: 20, borderTop: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' }}>
            <button onClick={archiveProject} style={{ padding: '12px 20px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, color: '#22c55e', fontWeight: 700, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 8 }}>✅ Complete & Archive</button>
            {project.clientEmail && (
              <button onClick={() => {
                const msg = `Hello,\n\n📢 Project Update!\n\n🏗 Project: ${project.name}\n📊 Progress: ${progress}%\n📍 Stage: ${PROJECT_STAGES.find(s => s.id === stage)?.label || stage}\n${project.photos?.length > 0 ? `📸 Photos: ${project.photos.length} uploaded` : ''}\n${project.quotationUrl ? '📄 Quotation: Available in your portal' : ''}\n\n🔗 View details: https://app.impressabuild.com\n\nBest regards,\nImpressaBuild Team`;
                const phone = project.clientPhone ? project.clientPhone.replace(/[^0-9]/g, '') : '';
                window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
              }} style={{ padding: '12px 20px', background: '#25D366', border: 'none', borderRadius: 10, color: WHITE, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 8 }}>
                📱 Notify Client
              </button>
            )}
          </div>

          {/* Subcontractor Tasks */}
          <SubTaskManager projectId={project.id} projectName={project.name} notify={notify} />

          {/* Client Feedback */}
          {project.feedbackSubmitted && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>CLIENT FEEDBACK</div>
              <div style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: 14 }}>
                <div style={{ color: '#f59e0b', fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{'★'.repeat(project.surveyRating || 0)}{'☆'.repeat(5 - (project.surveyRating || 0))} <span style={{ fontSize: 14, color: GRAY }}>{project.surveyRating}/5</span></div>
                {project.feedbackComment && <div style={{ color: WHITE, fontSize: 14, lineHeight: 1.5 }}>"{project.feedbackComment}"</div>}
                <div style={{ color: GRAY, fontSize: 12, marginTop: 6 }}>{project.feedbackDate?.toDate?.()?.toLocaleDateString?.() || ''}</div>
              </div>
            </div>
          )}

          {/* Close button - centered */}
          <div style={{ paddingTop: 16, textAlign: 'center' }}>
            <button onClick={onClose} style={{ padding: '14px 60px', background: O, border: 'none', borderRadius: 12, color: BG, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// CLIENT PORTAL COMPONENTS
// ============================================

// Hook to get all emails in the same company
function useCompanyEmails(user) {
  const [emails, setEmails] = useState([user.email]);
  useEffect(() => {
    if (!user.company) { setEmails([user.email]); return; }
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        const companyEmails = [];
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.company && data.company.toLowerCase() === user.company.toLowerCase()) {
            companyEmails.push(data.email);
          }
        });
        if (companyEmails.length > 0) setEmails(companyEmails);
        else setEmails([user.email]);
      } catch(e) { setEmails([user.email]); }
    })();
  }, [user.email, user.company]);
  return emails;
}
function Home({ loaded, go, user }) {
  const [projects, setProjects] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [projectCount, setProjectCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [showOrderDetail, setShowOrderDetail] = useState(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [approving, setApproving] = useState(false);
  const [changeRequests, setCRs] = useState([]);
  const [pushNotifications, setPushNotifications] = useState([]);
  const companyEmails = useCompanyEmails(user);

  useEffect(() => {
    const q = query(collection(db, 'projects'), where('clientEmail', 'in', companyEmails));
    const unsub = onSnapshot(q, (snap) => {
      const allProjs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(allProjs.filter(p => !p.archived));
      const completed = allProjs.filter(p => p.archived && p.stage === 'complete' && !p.thankYouSeen);
      setCompletedProjects(completed);
      setProjectCount(allProjs.filter(p => !p.archived).length);
      if (completed.length > 0) {
        setTimeout(() => {
          completed.forEach(async (proj) => {
            try { await updateDoc(doc(db, 'projects', proj.id), { thankYouSeen: true }); } catch (e) {}
          });
        }, 5000);
      }
    });
    return () => unsub();
  }, [companyEmails]);

  useEffect(() => {
    const q = query(collection(db, 'orders'), where('userEmail', 'in', companyEmails));
    const unsub = onSnapshot(q, (snap) => {
      const ords = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      ords.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setOrders(ords);
    });
    return () => unsub();
  }, [companyEmails]);

  useEffect(() => {
    const q = query(collection(db, 'changeRequests'), where('userEmail', 'in', companyEmails));
    const unsub = onSnapshot(q, (snap) => {
      setCRs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, [companyEmails]);

  useEffect(() => {
    const q = query(collection(db, 'notifications'), where('clientEmail', 'in', companyEmails));
    const unsub = onSnapshot(q, (snap) => {
      const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(n => !n.read).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPushNotifications(notifs);
    });
    return () => unsub();
  }, [user.email]);

  const dismissNotification = async (notifId) => {
    try { await updateDoc(doc(db, 'notifications', notifId), { read: true }); } catch(e) {}
  };

  // Approve order quotation → auto-create project + invoice
  const approveOrderQuotation = async (order) => {
    setApproving(true);
    try {
      // Update order
      await updateDoc(doc(db, 'orders', order.id), {
        quotationStatus: 'approved',
        clientComment: approvalComment || '',
        clientNotification: 'Quotation approved by client',
        updatedAt: serverTimestamp()
      });
      // Auto-create project from order data
      const projectName = order.projectName || ((order.category || 'New Project') + ' - ' + (order.type || 'Custom'));
      await addDoc(collection(db, 'projects'), {
        name: projectName,
        clientName: order.userName || '',
        clientEmail: order.userEmail || '',
        clientPhone: order.clientPhone || '',
        company: order.company || '',
        type: order.category || 'Custom Fabrication',
        priority: 'medium',
        stage: 'design',
        progress: 0,
        photos: [],
        quotationUrl: order.quotationFileUrl || null,
        quotationName: order.quotationFileName || null,
        totalAmount: order.quotationAmount || 0,
        archived: false,
        pmEmail: order.pmEmail || null,
        pmName: order.pmName || null,
        sourceOrderId: order.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      // Auto-create invoice if there's an amount
      if (order.quotationAmount && order.quotationAmount > 0) {
        const invoicePercent = order.firstInvoicePercent || 50;
        const invoiceAmount = order.quotationAmount * invoicePercent / 100;
        await addDoc(collection(db, 'invoices'), {
          clientEmail: order.userEmail,
          projectName: projectName,
          description: 'Down Payment (' + invoicePercent + '%) - ' + (order.company ? order.company + ' / ' : '') + projectName,
          totalAmount: order.quotationAmount,
          amount: invoiceAmount,
          paid: 0,
          status: 'pending',
          fileUrl: order.quotationFileUrl || '',
          createdAt: serverTimestamp()
        });
      }
      // Notify admin
      const amountText = order.quotationAmount ? ' (SAR ' + order.quotationAmount.toLocaleString() + ')' : '';
      createPushNotification(ADMIN_EMAIL, 'quotation_approved', 'Quotation Approved — Proceed!', (order.userName || user.email) + ' approved the quotation for "' + projectName + '"' + amountText + '. Project and invoice created automatically. Please proceed.', '');
      setApprovalComment('');
      setShowOrderDetail(null);
    } catch (err) { console.error(err); }
    setApproving(false);
  };

  // Reject order quotation
  const rejectOrderQuotation = async (order) => {
    setApproving(true);
    try {
      await updateDoc(doc(db, 'orders', order.id), {
        quotationStatus: 'rejected',
        clientComment: approvalComment || '',
        clientNotification: 'Quotation rejected by client',
        updatedAt: serverTimestamp()
      });
      createPushNotification(ADMIN_EMAIL, 'quotation_rejected', 'Quotation Rejected', (order.userName || user.email) + ' rejected the quotation for "' + order.category + ' - ' + order.type + '". Comment: ' + (approvalComment || 'No comment'), '');
      setApprovalComment('');
      setShowOrderDetail(null);
    } catch (err) { console.error(err); }
    setApproving(false);
  };

  // Approve change request quotation → update project
  const approveCRQuotation = async (cr) => {
    setApproving(true);
    try {
      await updateDoc(doc(db, 'changeRequests', cr.id), {
        quotationStatus: 'approved',
        status: 'approved',
        clientComment: approvalComment || '',
        updatedAt: serverTimestamp()
      });
      // Auto-create an invoice for the change request amount
      if (cr.quotationAmount && cr.quotationAmount > 0) {
        await addDoc(collection(db, 'invoices'), {
          clientEmail: cr.userEmail,
          projectName: cr.projectName,
          description: 'Change Order Invoice: ' + cr.title + ' for "' + cr.projectName + '"',
          totalAmount: cr.quotationAmount,
          amount: cr.quotationAmount,
          paid: 0,
          status: 'pending',
          fileUrl: cr.quotationFileUrl || '',
          createdAt: serverTimestamp()
        });
        // Notify client about the invoice
        createPushNotification(cr.userEmail, 'invoice', 'Change Order Invoice Created', 'An invoice of SAR ' + cr.quotationAmount.toLocaleString() + ' has been created for your change request "' + cr.title + '" on "' + cr.projectName + '". Please check your Financials.', cr.projectName);
      }
      // Update the related project
      if (cr.projectId) {
        try {
          const projRef = doc(db, 'projects', cr.projectId);
          const projSnap = await getDoc(projRef);
          if (projSnap.exists()) {
            await updateDoc(projRef, { updatedAt: serverTimestamp() });
          }
        } catch (e) { console.error('Project update error:', e); }
      }
      const amountText = cr.quotationAmount ? ' (SAR ' + cr.quotationAmount.toLocaleString() + ')' : '';
      createPushNotification(ADMIN_EMAIL, 'cr_quotation_approved', 'Change Request Approved — Proceed!', (user.name || user.email) + ' approved the change request "' + cr.title + '" for "' + cr.projectName + '"' + amountText + '. Invoice has been created automatically. Please proceed with the work.', cr.projectName);
      setApprovalComment('');
      setShowOrderDetail(null);
    } catch (err) { console.error(err); }
    setApproving(false);
  };

  // Reject change request quotation
  const rejectCRQuotation = async (cr) => {
    setApproving(true);
    try {
      await updateDoc(doc(db, 'changeRequests', cr.id), {
        quotationStatus: 'rejected',
        clientComment: approvalComment || '',
        updatedAt: serverTimestamp()
      });
      createPushNotification(ADMIN_EMAIL, 'cr_quotation_rejected', 'Change Request Quotation Rejected', (user.name || user.email) + ' rejected the change request quotation for "' + cr.projectName + '". Comment: ' + (approvalComment || 'No comment'), cr.projectName);
      setApprovalComment('');
      setShowOrderDetail(null);
    } catch (err) { console.error(err); }
    setApproving(false);
  };

  // Pending quotation approvals
  const pendingOrderQuotes = useMemo(() => orders.filter(o => o.quotationFileUrl && (o.quotationStatus === 'pending_approval' || (o.quoteStage >= 5 && o.quotationStatus !== 'approved' && o.quotationStatus !== 'rejected'))), [orders]);
  const pendingCRQuotes = useMemo(() => changeRequests.filter(cr => cr.quotationStatus === 'pending_approval'), [changeRequests]);

  // Mark notification as read
  const markNotificationRead = async (orderId) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { notificationRead: true });
    } catch (err) {}
  };

  const cc = useMemo(() => {
    const counts = {};
    projects.forEach(p => { const cat = p.type || 'Custom Fabrication'; counts[cat] = (counts[cat] || 0) + 1; });
    return counts;
  }, [projects]);
  
  // Get unread notifications
  const unreadOrders = useMemo(() => orders.filter(o => o.clientNotification && !o.notificationRead), [orders]);
  
  // Active quotes in progress (include stage 5 until quotation is sent for approval)
  const activeQuotes = useMemo(() => orders.filter(o => o.status === 'approved' && typeof o.quoteStage === 'number' && (o.quoteStage < 5 || (o.quoteStage === 5 && !o.quotationFileUrl))), [orders]);

  // Section Header Component - BIGGER FONTS
  const SectionHeader = ({ icon, title, count }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 12, 
      marginBottom: 16,
      paddingBottom: 12,
      borderBottom: '2px solid rgba(255,255,255,0.08)'
    }}>
      <span style={{ fontSize: 26 }}>{icon}</span>
      <span style={{ color: WHITE, fontSize: 20, fontWeight: 700, flex: 1 }}>{title}</span>
      {count !== undefined && (
        <span style={{ 
          background: O, 
          color: BG, 
          fontSize: 14, 
          fontWeight: 700, 
          padding: '5px 12px', 
          borderRadius: 20 
        }}>{count}</span>
      )}
    </div>
  );
  
  return (
    <>
      {/* Notification Banner */}
      {unreadOrders.length > 0 && (
        <div style={{ padding: '12px 22px 0' }}>
          <SectionHeader icon="🔔" title="New Notifications" count={unreadOrders.length} />
          {unreadOrders.map(order => (
            <div 
              key={order.id} 
              onClick={() => { setShowOrderDetail(order); markNotificationRead(order.id); }}
              style={{ 
                background: 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(249,115,22,0.05))', 
                border: '1px solid rgba(249,115,22,0.3)', 
                borderRadius: 16, padding: 16, marginBottom: 10, cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 24 }}>📬</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{order.clientNotification}</div>
                  <div style={{ color: GRAY, fontSize: 14 }}>{order.category} • {order.type}</div>
                </div>
                <span style={{ color: O, fontSize: 18 }}>→</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Completed Project Thank You - auto-dismisses after first view */}
      {completedProjects.length > 0 && (
        <div style={{ padding: '12px 22px 0' }}>
          {completedProjects.map(proj => (
            <div key={proj.id} style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 16, padding: 18, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 28 }}>🎉</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#22c55e', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Project Completed!</div>
                  <div style={{ color: WHITE, fontSize: 15, fontWeight: 600 }}>{proj.name}</div>
                  <div style={{ color: GRAY, fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>Thank you for choosing ImpressaBuild! We truly appreciate your trust and look forward to working with you again.</div>
                </div>
                <button onClick={async (e) => {
                  e.stopPropagation();
                  try { await updateDoc(doc(db, 'projects', proj.id), { thankYouSeen: true }); } catch (err) {}
                }} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 20, cursor: 'pointer', padding: 4 }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Push Notifications */}
      {pushNotifications.length > 0 && (
        <div style={{ padding: '12px 22px 0' }}>
          {pushNotifications.slice(0, 5).map(n => (
            <div key={n.id} style={{ background: n.type === 'payment_thanks' ? 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))' : n.type === 'project_complete' ? 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))' : 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))', border: '1px solid ' + (n.type === 'payment_thanks' || n.type === 'project_complete' ? 'rgba(34,197,94,0.3)' : 'rgba(59,130,246,0.3)'), borderRadius: 16, padding: 16, marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 24 }}>{n.type === 'payment_thanks' ? '💰' : n.type === 'project_complete' ? '🎉' : n.type === 'progress' ? '📊' : n.type === 'stage_update' ? '📍' : n.type === 'photo_upload' ? '📸' : '🔔'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 15, fontWeight: 700 }}>{n.title}</div>
                  <div style={{ color: GRAY, fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{n.body}</div>
                </div>
                <button onClick={() => dismissNotification(n.id)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 18, cursor: 'pointer', padding: 4 }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending Quotation Approvals - Orders and Change Requests - SHOWN FIRST */}
      {(pendingOrderQuotes.length > 0 || pendingCRQuotes.length > 0) && (
        <div style={{ padding: "12px 22px 0" }}>
          <SectionHeader icon="📄" title="Quotations to Approve" count={pendingOrderQuotes.length + pendingCRQuotes.length} />
          {pendingOrderQuotes.map(order => (
            <div key={order.id} onClick={() => { setShowOrderDetail(order); setApprovalComment(''); }} style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.04))', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 16, padding: 16, marginBottom: 10, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 24 }}>📄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>Quotation Ready</div>
                  <div style={{ color: GRAY, fontSize: 13 }}>{order.projectName || order.category} • {order.type}</div>
                </div>
                <span style={{ padding: '5px 12px', borderRadius: 20, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: 12, fontWeight: 700 }}>REVIEW</span>
              </div>
            </div>
          ))}
          {pendingCRQuotes.map(cr => (
            <div key={cr.id} onClick={() => { setShowOrderDetail({ ...cr, _isCR: true }); setApprovalComment(''); }} style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(139,92,246,0.04))', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 16, padding: 16, marginBottom: 10, cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 24 }}>🔄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>Change Order Cost</div>
                  <div style={{ color: GRAY, fontSize: 13 }}>{cr.title} • {cr.projectName}</div>
                  {cr.quotationAmount && <div style={{ color: O, fontSize: 13, fontWeight: 600, marginTop: 2 }}>SAR {cr.quotationAmount.toLocaleString()}</div>}
                </div>
                <span style={{ padding: '5px 12px', borderRadius: 20, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontSize: 12, fontWeight: 700 }}>REVIEW</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Welcome Header */}
      <div style={{ position: "relative", padding: "28px 22px", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 30% 20%, " + OGLOW + " 0%, transparent 60%)" }} />
        <div style={{ position: "relative", zIndex: 2, opacity: loaded ? 1 : 0, transform: loaded ? "none" : "translateY(28px)", transition: "all .8s" }}>
          <div style={{ color: GRAY, fontSize: 17, marginBottom: 4 }}>Welcome back,</div>
          <h1 style={{ color: WHITE, fontSize: 28, fontWeight: 700, margin: "0 0 4px" }}>{user.name || user.email.split('@')[0]}</h1>
          <div style={{ color: O, fontSize: 16, marginBottom: 22 }}>{user.company || user.email.split('@')[1]?.split('.')[0] || 'Client'}</div>
          <h2 style={{ color: WHITE, fontSize: 28, fontWeight: 700, lineHeight: 1.3, margin: 0 }}>Your Projects,<br /><span style={{ color: O }}>Real-Time Updates.</span></h2>
          <p style={{ color: GRAY, fontSize: 16, lineHeight: 1.65, marginTop: 14 }}>Precision in Production, Excellence in Execution.</p>
          <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
            <button style={{ ...s.cta, fontSize: 17, padding: "16px 28px" }} onClick={() => go("Order")}>Place Order →</button>
            <button style={{ ...s.ctaOutline, fontSize: 17, padding: "16px 24px" }} onClick={() => go("Projects")}>My Projects ({projectCount})</button>
          </div>
        </div>
      </div>

      {/* Active Quotations Section */}
      {activeQuotes.length > 0 && (
        <div style={{ padding: "0 22px 24px" }}>
          <SectionHeader icon="📋" title="Quotation Progress" count={activeQuotes.length} />
          {activeQuotes.map(order => {
            const stage = QUOTE_STAGES[order.quoteStage] || QUOTE_STAGES[0];
            return (
              <div 
                key={order.id} 
                onClick={() => setShowOrderDetail(order)}
                style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 16, marginBottom: 10, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <div style={{ color: WHITE, fontSize: 15, fontWeight: 700 }}>{order.category}</div>
                    <div style={{ color: GRAY, fontSize: 14 }}>{order.type}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: 20 }}>
                    <span style={{ fontSize: 14 }}>{stage.icon}</span>
                    <span style={{ color: stage.color, fontSize: 14, fontWeight: 600 }}>{stage.name}</span>
                  </div>
                </div>
                {/* Mini Progress Bar */}
                <div style={{ display: 'flex', gap: 4 }}>
                  {QUOTE_STAGES.map((s, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        flex: 1, height: 4, borderRadius: 2,
                        background: order.quoteStage >= idx ? s.color : 'rgba(255,255,255,0.1)'
                      }} 
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Active Projects Section */}
      {projects.length > 0 && (
        <div style={{ padding: "0 22px 24px" }}>
          <SectionHeader icon="🏗" title="Active Projects" count={projects.length} />
          <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 6 }}>
            {projects.slice(0, 5).map(p => {
              const stageInfo = PROJECT_STAGES.find(s => s.id === p.stage) || PROJECT_STAGES[0];
              return (
                <div key={p.id} onClick={() => go("Projects")} style={{ minWidth: 200, background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "16px 18px", flexShrink: 0, cursor: 'pointer' }}>
                  <div style={{ color: WHITE, fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{p.name}</div>
                  <div style={{ color: GRAY, fontSize: 14, marginBottom: 12 }}>{p.type || 'Custom'}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${p.progress || 0}%`, height: '100%', background: stageInfo?.color || O, borderRadius: 2 }} />
                    </div>
                    <span style={{ color: stageInfo?.color || O, fontSize: 14, fontWeight: 600 }}>{p.progress || 0}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Categories Overview */}
      {Object.keys(cc).length > 0 && (
        <div style={{ padding: "0 22px 24px" }}>
          <SectionHeader icon="📊" title="Your Categories" />
          <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 6 }}>
            {CATEGORIES.filter(c => cc[c.name]).map(c => (
              <div key={c.name} style={{ minWidth: 130, background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "16px 18px", flexShrink: 0 }}>
                <div style={{ fontSize: 28 }}>{c.icon}</div>
                <div style={{ color: WHITE, fontSize: 14, fontWeight: 700, marginTop: 8 }}>{c.name}</div>
                <div style={{ color: c.color, fontSize: 24, fontWeight: 700, marginTop: 6 }}>{cc[c.name]}</div>
                <div style={{ color: GRAY, fontSize: 15 }}>project{cc[c.name] > 1 ? "s" : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ padding: "0 22px 28px" }}>
        <SectionHeader icon="⚡" title="Quick Actions" />
        {[
          { icon: "📝", title: "Place New Order", sub: "Choose a service category", tap: "Order" }, 
          { icon: "📊", title: "Track Projects", sub: projectCount + " active projects", tap: "Projects" }, 
          { icon: "💰", title: "View Financials", sub: "Invoices & payments", tap: "Financials" }, 
          { icon: "👥", title: "Team Members", sub: "Manage your team", tap: "Account" },
          { icon: "📞", title: "Contact Us", sub: "KSA & US offices", tap: "Contact" }
        ].map((q, i) => (
          <button key={i} style={s.quickCard} onClick={() => go(q.tap)}>
            <span style={{ fontSize: 28 }}>{q.icon}</span>
            <div style={{ flex: 1, textAlign: "left" }}><div style={{ color: WHITE, fontSize: 17, fontWeight: 700 }}>{q.title}</div><div style={{ color: GRAY, fontSize: 15, marginTop: 4 }}>{q.sub}</div></div>
            <span style={{ color: O, fontSize: 24, fontWeight: 700 }}>›</span>
          </button>
        ))}
      </div>

      {/* Order Detail Modal */}
      {showOrderDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 99999, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }} onClick={() => setShowOrderDetail(null)}>
          <div style={{ minHeight: '100%', padding: '40px 20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div style={{ background: BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 450, marginTop: 20, marginBottom: 20 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>Order Status</h2>
              <button onClick={() => setShowOrderDetail(null)} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {/* Order Info */}
              {(() => {
                const needsApproval = showOrderDetail.quotationFileUrl && showOrderDetail.quotationStatus !== 'approved' && showOrderDetail.quotationStatus !== 'rejected';
                return (<>
              <div style={{ marginBottom: 20 }}>
                <div style={{ color: WHITE, fontSize: 18, fontWeight: 700 }}>{showOrderDetail.projectName || showOrderDetail.category}</div>
                <div style={{ color: GRAY, fontSize: 14 }}>{showOrderDetail.category}{showOrderDetail.type ? ' • ' + showOrderDetail.type : ''}{showOrderDetail.location ? ' • ' + showOrderDetail.location : ''}</div>
              </div>

              {/* Status */}
              <div style={{ marginBottom: 20 }}>
                <span style={{ 
                  padding: '6px 14px', borderRadius: 100, fontSize: 15, fontWeight: 700,
                  background: needsApproval ? 'rgba(245,158,11,0.15)' : showOrderDetail.status === 'pending' ? 'rgba(245,158,11,0.15)' : showOrderDetail.quotationStatus === 'approved' ? 'rgba(34,197,94,0.15)' : showOrderDetail.quotationStatus === 'rejected' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                  color: needsApproval ? '#f59e0b' : showOrderDetail.status === 'pending' ? '#f59e0b' : showOrderDetail.quotationStatus === 'approved' ? '#22c55e' : showOrderDetail.quotationStatus === 'rejected' ? '#ef4444' : '#22c55e',
                  textTransform: 'uppercase'
                }}>{needsApproval ? 'AWAITING YOUR APPROVAL' : showOrderDetail.quotationStatus === 'approved' ? 'APPROVED' : showOrderDetail.quotationStatus === 'rejected' ? 'REJECTED' : showOrderDetail.status}</span>
              </div>

              {/* Prominent Approval Section */}
              {needsApproval && (
                <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.03))', border: '2px solid rgba(245,158,11,0.4)', borderRadius: 16, padding: 20, marginBottom: 16 }}>
                  <div style={{ color: '#f59e0b', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>📋 Quotation Ready for Review</div>
                  
                  <a href={googleViewerUrl(showOrderDetail.quotationFileUrl)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 12, textDecoration: 'none', marginBottom: 14 }}>
                    <span style={{ fontSize: 22 }}>📄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: WHITE, fontSize: 15, fontWeight: 600 }}>{showOrderDetail.quotationFileName || 'View Quotation'}</div>
                      <div style={{ color: '#3b82f6', fontSize: 13 }}>Tap to view document</div>
                    </div>
                    <span style={{ color: '#3b82f6', fontSize: 20 }}>↗</span>
                  </a>
                  
                  {showOrderDetail.quotationAmount > 0 && (
                    <div style={{ background: CARD, borderRadius: 10, padding: 14, marginBottom: 14, textAlign: 'center' }}>
                      <div style={{ color: GRAY, fontSize: 12, marginBottom: 4 }}>Total Amount</div>
                      <div style={{ color: O, fontSize: 24, fontWeight: 700 }}>SAR {showOrderDetail.quotationAmount.toLocaleString()}</div>
                    </div>
                  )}
                  
                  <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Please review and respond:</div>
                  <textarea value={approvalComment} onChange={e => setApprovalComment(e.target.value)} placeholder="Add a comment (optional)..." rows={2} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 14px', color: WHITE, fontSize: 14, fontFamily: FONT, resize: 'none', marginBottom: 12, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button disabled={approving} onClick={() => showOrderDetail._isCR ? approveCRQuotation(showOrderDetail) : approveOrderQuotation(showOrderDetail)} style={{ flex: 1, padding: '16px 20px', background: '#22c55e', border: 'none', borderRadius: 12, color: WHITE, fontWeight: 700, fontSize: 16, cursor: 'pointer', fontFamily: FONT, opacity: approving ? 0.5 : 1 }}>
                      {approving ? '...' : '✓ Approve'}
                    </button>
                    <button disabled={approving} onClick={async () => {
                      setApproving(true);
                      try {
                        if (showOrderDetail._isCR) {
                          await updateDoc(doc(db, 'changeRequests', showOrderDetail.id), { quotationStatus: 'rejected', clientComment: approvalComment, updatedAt: serverTimestamp() });
                        } else {
                          await updateDoc(doc(db, 'orders', showOrderDetail.id), { quotationStatus: 'rejected', clientComment: approvalComment, updatedAt: serverTimestamp() });
                        }
                        notify('Rejected');
                        setShowOrderDetail(null);
                      } catch(e) { notify('Error'); }
                      setApproving(false);
                    }} style={{ flex: 1, padding: '16px 20px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 12, color: '#ef4444', fontWeight: 700, fontSize: 16, cursor: 'pointer', fontFamily: FONT, opacity: approving ? 0.5 : 1 }}>
                      {approving ? '...' : '✗ Reject'}
                    </button>
                  </div>
                </div>
              )}

              {/* Quotation Progress - only show when NOT needing approval */}
              {showOrderDetail.status === 'approved' && typeof showOrderDetail.quoteStage === 'number' && !needsApproval && (
                <div style={{ background: CARD, borderRadius: 16, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 1.5, marginBottom: 16 }}>QUOTATION PROGRESS</div>
                  
                  {/* Timeline */}
                  <div>
                    {QUOTE_STAGES.map((stage, idx) => {
                      const isComplete = showOrderDetail.quoteStage >= idx;
                      const isCurrent = showOrderDetail.quoteStage === idx;
                      const historyItem = showOrderDetail.quoteHistory?.find(h => h.stage === idx);
                      return (
                        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: idx < QUOTE_STAGES.length - 1 ? 16 : 0 }}>
                          <div style={{ 
                            width: 36, height: 36, borderRadius: '50%', 
                            background: isComplete ? stage.color : 'rgba(255,255,255,0.06)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, flexShrink: 0,
                            boxShadow: isCurrent ? `0 0 0 4px ${stage.color}33` : 'none'
                          }}>
                            {stage.icon}
                          </div>
                          <div style={{ flex: 1, paddingTop: 6 }}>
                            <div style={{ color: isComplete ? WHITE : GRAY, fontSize: 14, fontWeight: isComplete ? 600 : 400 }}>{stage.name}</div>
                            {historyItem && (
                              <div style={{ color: GRAY, fontSize: 15, marginTop: 2 }}>
                                {new Date(historyItem.timestamp).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                          {isComplete && <span style={{ color: stage.color, fontSize: 18 }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quotation File (for already approved/rejected - read only) */}
              {showOrderDetail.quotationFileUrl && !needsApproval && (
                <div style={{ background: CARD, borderRadius: 16, padding: 16, marginBottom: 16 }}>
                  <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>{showOrderDetail._isCR ? 'CHANGE REQUEST QUOTATION' : 'QUOTATION DOCUMENT'}</div>
                  
                  {/* File link */}
                  <a href={googleViewerUrl(showOrderDetail.quotationFileUrl)} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 12, textDecoration: 'none', marginBottom: 14 }}>
                    <span style={{ fontSize: 22 }}>📄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: WHITE, fontSize: 15, fontWeight: 600 }}>{showOrderDetail.quotationFileName || 'Quotation'}</div>
                      <div style={{ color: '#3b82f6', fontSize: 13 }}>Tap to view document</div>
                      {showOrderDetail.quotationAmount && <div style={{ color: O, fontSize: 14, fontWeight: 700, marginTop: 4 }}>SAR {showOrderDetail.quotationAmount.toLocaleString()}</div>}
                    </div>
                    <span style={{ color: '#3b82f6', fontSize: 20 }}>↗</span>
                  </a>
                  
                  {/* Approval status */}
                  {showOrderDetail.quotationStatus === 'pending_approval' && (
                    <div>
                      <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Please review the quotation and respond:</div>
                      <textarea value={approvalComment} onChange={e => setApprovalComment(e.target.value)} placeholder="Add a comment (optional)..." rows={3} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '12px 14px', color: WHITE, fontSize: 14, fontFamily: FONT, resize: 'none', marginBottom: 12, boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button disabled={approving} onClick={() => showOrderDetail._isCR ? approveCRQuotation(showOrderDetail) : approveOrderQuotation(showOrderDetail)} style={{ flex: 1, padding: '14px 20px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 12, color: '#22c55e', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: FONT, opacity: approving ? 0.5 : 1 }}>
                          {approving ? '...' : '✓ Approve'}
                        </button>
                        <button disabled={approving} onClick={() => showOrderDetail._isCR ? rejectCRQuotation(showOrderDetail) : rejectOrderQuotation(showOrderDetail)} style={{ flex: 1, padding: '14px 20px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 12, color: '#ef4444', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: FONT, opacity: approving ? 0.5 : 1 }}>
                          {approving ? '...' : '✗ Reject'}
                        </button>
                      </div>
                    </div>
                  )}
                  {showOrderDetail.quotationStatus === 'approved' && (
                    <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10 }}>
                      <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 600 }}>✅ You approved this quotation</span>
                    </div>
                  )}
                  {showOrderDetail.quotationStatus === 'rejected' && (
                    <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10 }}>
                      <span style={{ color: '#ef4444', fontSize: 14, fontWeight: 600 }}>❌ You rejected this quotation</span>
                    </div>
                  )}
                </div>
              )}

              {/* Notification Message */}
              {showOrderDetail.clientNotification && (
                <div style={{ background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 12, padding: 14, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>💬</span>
                    <div style={{ color: WHITE, fontSize: 14, lineHeight: 1.5 }}>{showOrderDetail.clientNotification}</div>
                  </div>
                </div>
              )}
              
              {/* Close button */}
              </>); })()}
              <button onClick={() => setShowOrderDetail(null)} style={{ width: '100%', padding: '14px 20px', background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: WHITE, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, marginTop: 8 }}>Close</button>
            </div>
          </div>
          </div>
        </div>
      )}
    </>
  );
}

function OrderForm({ user, onDone, onCancel, notify }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ projectName: "", category: "", type: "", otherType: "", location: "", area: "", budget: "", timeline: "", details: "", attachmentLink: "", attachmentUrl: "", attachmentFileName: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const selectedCat = CATEGORIES.find(c => c.name === form.category);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      notify("File too large. Max 10MB.");
      return;
    }
    
    setUploading(true);
    set("attachmentFileName", file.name);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'impressabuild/orders');
      
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
        { method: 'POST', body: formData }
      );
      
      const data = await response.json();
      if (data.secure_url) {
        set("attachmentUrl", data.secure_url);
        notify("File uploaded!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error(err);
      notify("Upload failed. Try again.");
      set("attachmentFileName", "");
    }
    setUploading(false);
  };

  const removeFile = () => {
    set("attachmentUrl", "");
    set("attachmentFileName", "");
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'orders'), { 
        ...form, 
        userEmail: user.email, 
        userName: user.name || user.email.split('@')[0], 
        company: user.company || user.email.split('@')[1]?.split('.')[0] || 'Client', 
        status: 'pending', 
        createdAt: serverTimestamp() 
      });
      // Notify admin of new order
      createPushNotification(
        ADMIN_EMAIL,
        'new_order',
        'New Order Received',
        'New order from ' + (user.name || user.email) + ' — ' + (form.category || 'General'),
        ''
      );
    } catch (err) { console.error(err); }
    setSubmitting(false);
    setSubmitted(true);
    notify("Order submitted!");
  };

  if (submitted) {
    const ref = "PRJ-2026-" + String(Math.floor(Math.random() * 900 + 100));
    return (
      <div style={{ padding: "40px 22px 100px", textAlign: "center" }}>
        <div style={{ width: 90, height: 90, borderRadius: "50%", background: "rgba(76,212,154,0.12)", border: "2px solid rgba(76,212,154,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", fontSize: 40 }}>✓</div>
        <h2 style={{ color: WHITE, fontSize: 28, fontWeight: 700, marginTop: 20 }}>Order Submitted!</h2>
        <p style={{ color: GRAY, fontSize: 17, marginTop: 8 }}>We will contact you within 24 hours.</p>
        <div style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 18, padding: 22, marginTop: 24, textAlign: "left" }}>
          <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2 }}>ORDER REFERENCE</div>
          <div style={{ color: WHITE, fontSize: 26, fontWeight: 700, marginTop: 8 }}>{ref}</div>
          <div style={{ color: GRAY, fontSize: 16, marginTop: 8 }}>{form.category}</div>
        </div>
        <button style={{ ...s.cta, marginTop: 24, width: "100%" }} onClick={onDone}>Track My Projects →</button>
      </div>
    );
  }

  // Render step content separately to avoid recreating on each render
  const renderStepContent = () => {
    if (step === 0) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {CATEGORIES.map(c => (
            <button key={c.name} onClick={() => { set("category", c.name); set("type", ""); }} style={{ display: "flex", alignItems: "center", gap: 14, padding: "18px 20px", background: form.category === c.name ? "rgba(255,107,0,0.1)" : CARD, border: "1.5px solid " + (form.category === c.name ? O : "rgba(255,255,255,0.06)"), borderRadius: 16, cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
              <span style={{ fontSize: 28 }}>{c.icon}</span>
              <div style={{ flex: 1 }}><div style={{ color: form.category === c.name ? O : WHITE, fontSize: 17, fontWeight: 700 }}>{c.name}</div></div>
              {form.category === c.name && <span style={{ color: O, fontSize: 22 }}>✓</span>}
            </button>
          ))}
        </div>
      );
    }
    if (step === 1) {
      return selectedCat && selectedCat.options.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {selectedCat.options.map(opt => (
            <button key={opt} onClick={() => set("type", opt)} style={{ ...s.chip, background: form.type === opt ? O : CARD, color: form.type === opt ? BG : WHITE, borderColor: form.type === opt ? O : "rgba(255,255,255,0.06)" }}>{opt}</button>
          ))}
        </div>
      ) : (
        <div><div style={s.inputLabel}>Describe your project</div><textarea value={form.otherType} onChange={e => { set("otherType", e.target.value); set("type", "Other"); }} placeholder="Tell us what you need..." rows={4} style={{ ...s.input, resize: "none", fontFamily: FONT }} /></div>
      );
    }
    if (step === 2) {
      return (
        <>
          <FInput label="Project Name" value={form.projectName} onChange={v => set("projectName", v)} placeholder="e.g. GITEX 2026 Booth" />
          <FInput label="Location" value={form.location} onChange={v => set("location", v)} placeholder="Riyadh, KSA" />
          <FInput label="Area / Size" value={form.area} onChange={v => set("area", v)} placeholder="e.g. 250 sqm" />
          <div style={{ marginBottom: 18 }}><div style={s.inputLabel}>Budget Range</div><div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {["Under 50K SAR", "50K–150K SAR", "150K–500K SAR", "500K+ SAR"].map(b => (
              <button key={b} onClick={() => set("budget", b)} style={{ ...s.chip, background: form.budget === b ? O : CARD, color: form.budget === b ? BG : WHITE }}>{b}</button>
            ))}</div></div>
          <div style={{ marginBottom: 18 }}><div style={s.inputLabel}>Timeline</div><div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {["1–2 weeks", "1 month", "2–3 months", "3+ months"].map(t => (
              <button key={t} onClick={() => set("timeline", t)} style={{ ...s.chip, background: form.timeline === t ? O : CARD, color: form.timeline === t ? BG : WHITE }}>{t}</button>
            ))}</div></div>
          <div style={{ marginBottom: 18 }}><div style={s.inputLabel}>Additional Details</div><textarea value={form.details} onChange={e => set("details", e.target.value)} placeholder="Describe your vision, requirements, inspiration..." rows={4} style={{ ...s.input, resize: "none", fontFamily: FONT }} /></div>
          
          <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: 16, marginBottom: 10 }}>
            <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>📎 ATTACHMENTS (OPTIONAL)</div>
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: GRAY, fontSize: 15, marginBottom: 10 }}>Upload a file (PDF, Image, PowerPoint, etc. - Max 10MB)</p>
              {uploading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', background: 'rgba(249,115,22,0.1)', border: '1.5px solid rgba(249,115,22,0.3)', borderRadius: 12 }}>
                  <span style={{ fontSize: 20, animation: 'spin 1s linear infinite' }}>⏳</span>
                  <span style={{ color: O, fontSize: 14, fontWeight: 600 }}>Uploading {form.attachmentFileName}...</span>
                </div>
              ) : !form.attachmentUrl ? (
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', background: 'rgba(255,255,255,0.03)', border: '1.5px dashed rgba(255,255,255,0.15)', borderRadius: 12, cursor: 'pointer' }}>
                  <span style={{ fontSize: 20 }}>📁</span>
                  <span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>Choose File</span>
                  <input type="file" accept=".pdf,.ppt,.pptx,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp" onChange={handleFileSelect} style={{ display: 'none' }} />
                </label>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>✅</span>
                    <span style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{form.attachmentFileName}</span>
                  </div>
                  <button onClick={removeFile} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
              <span style={{ color: GRAY, fontSize: 14 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.1)' }} />
            </div>
            <p style={{ color: GRAY, fontSize: 15, marginBottom: 10 }}>Share a link (Google Drive, Dropbox, Pinterest, etc.)</p>
            <input value={form.attachmentLink} onChange={e => set("attachmentLink", e.target.value)} placeholder="https://drive.google.com/... or any URL" style={{ ...s.input, width: '100%', boxSizing: 'border-box' }} />
            {form.attachmentLink && <div style={{ color: '#22c55e', fontSize: 15, marginTop: 8 }}>✓ Link attached</div>}
          </div>
        </>
      );
    }
    return null;
  };

  const stepTitles = ["Service Category", "Project Type", "Project Details"];
  const isStepValid = step === 0 ? form.category : step === 1 ? (form.type && (form.type !== "Other" || form.otherType)) : (form.projectName && form.location);

  return (
    <div style={s.page}>
      <button style={s.linkBtn} onClick={onCancel}>← Back to Home</button>
      <div style={{ height: 14 }} />
      <Label>New Order</Label>
      <h2 style={s.pageTitle}>Place an Order</h2>
      <div style={{ display: "flex", gap: 8, marginBottom: 22 }}>
        {[0, 1, 2].map(i => <div key={i} style={{ flex: 1, height: 5, borderRadius: 3, background: i <= step ? O : "rgba(255,255,255,0.08)" }} />)}
      </div>
      <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 6 }}>STEP {step + 1} OF 3</div>
      <h3 style={{ color: WHITE, fontSize: 24, fontWeight: 700, marginBottom: 18 }}>{stepTitles[step]}</h3>
      {renderStepContent()}
      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        {step > 0 && <button style={{ ...s.ctaOutline, padding: "16px 22px" }} onClick={() => setStep(step - 1)}>← Back</button>}
        <button style={{ ...s.cta, flex: 1, padding: "16px 24px", opacity: isStepValid ? 1 : .4, pointerEvents: isStepValid ? "auto" : "none" }} onClick={() => step < 2 ? setStep(step + 1) : handleSubmit()} disabled={submitting}>
          {submitting ? "Submitting..." : step < 2 ? "Continue →" : "Submit Order ✓"}
        </button>
      </div>
    </div>
  );
}

function MyProjects({ user, notify }) {
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [changeOrder, setChangeOrder] = useState(null);
  const [coForm, setCoForm] = useState({ type: "", desc: "", attachmentUrl: "", attachmentName: "" });
  const [coUploading, setCoUploading] = useState(false);
  const [coSent, setCoSent] = useState(false);
  const [projects, setProjects] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const companyEmails = useCompanyEmails(user);

  useEffect(() => {
    const q = query(collection(db, 'projects'), where('clientEmail', 'in', companyEmails));
    const unsub = onSnapshot(q, (snap) => {
      const projs = snap.docs.map(doc => {
        const d = doc.data();
        return {
          id: doc.id,
          name: d.name || 'Untitled Project',
          category: d.type || 'Custom Fabrication',
          type: d.type || 'Custom',
          stage: PROJECT_STAGES.findIndex(s => s.id === d.stage) || 0,
          stageId: d.stage || 'design',
          progress: d.progress || 0,
          date: d.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently',
          photos: d.photos || [],
          quotationUrl: d.quotationUrl || null,
          quotationName: d.quotationName || null,
          archived: d.archived || false,
          feedbackSubmitted: d.feedbackSubmitted || false,
          surveyRating: d.surveyRating || 0,
          feedbackComment: d.feedbackComment || '',
          surveySubmitted: d.surveySubmitted || false
        };
      });
      projs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setProjects(projs);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching projects:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [companyEmails]);

  useEffect(() => {
    const q = query(collection(db, 'changeRequests'), where('userEmail', 'in', companyEmails));
    const unsub = onSnapshot(q, (snap) => {
      const crs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      crs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setChangeRequests(crs);
    });
    return () => unsub();
  }, [user.email]);

  const activeProjects = projects.filter(p => !p.archived);
  const completedProjects = projects.filter(p => p.archived);
  const cats = ["All", ...new Set(activeProjects.map(p => p.category))];
  const filtered = filter === "All" ? activeProjects : activeProjects.filter(p => p.category === filter);
  const catInfo = name => CATEGORIES.find(c => c.name === name) || CATEGORIES[5];
  const handleDl = (id, label) => { setDownloading(id); setTimeout(() => { setDownloading(null); notify("Downloaded: " + label); }, 1500); };

  if (loading) {
    return (
      <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
          <div style={{ color: GRAY, fontSize: 16 }}>Loading your projects...</div>
        </div>
      </div>
    );
  }

  if (changeOrder !== null) {
    const proj = projects[changeOrder];
    if (coSent) return (
      <div style={{ ...s.page, textAlign: "center", paddingTop: 50 }}>
        <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(76,212,154,0.12)", border: "2px solid rgba(76,212,154,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", fontSize: 34 }}>✓</div>
        <h2 style={{ color: WHITE, fontSize: 26, fontWeight: 700, marginTop: 18 }}>Request Submitted!</h2>
        <p style={{ color: GRAY, fontSize: 17, marginTop: 8 }}>Change order for <b style={{ color: WHITE }}>{proj.name}</b> has been sent.</p>
        <button style={{ ...s.cta, marginTop: 24, width: "100%" }} onClick={() => { setChangeOrder(null); setCoSent(false); setCoForm({ type: "", desc: "", attachmentUrl: "", attachmentName: "" }); }}>← Back to Projects</button>
      </div>
    );
    return (
      <div style={s.page}>
        <button style={s.linkBtn} onClick={() => { setChangeOrder(null); setCoForm({ type: "", desc: "", attachmentUrl: "", attachmentName: "" }); }}>← Back</button>
        <div style={{ height: 14 }} />
        <Label>Change Order</Label>
        <h2 style={s.pageTitle}>Additional Request</h2>
        <div style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "16px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 24 }}>{catInfo(proj.category).icon}</span>
          <div><div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{proj.name}</div><div style={{ color: GRAY, fontSize: 14 }}>{proj.id}</div></div>
        </div>
        <div style={{ marginBottom: 18 }}><div style={s.inputLabel}>Request Type</div><div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {["Design Change", "Scope Addition", "Material Change", "Timeline Change", "Budget Adjustment", "Other"].map(t => (
            <button key={t} onClick={() => setCoForm(prev => ({ ...prev, type: t }))} style={{ ...s.chip, background: coForm.type === t ? O : CARD, color: coForm.type === t ? BG : WHITE }}>{t}</button>
          ))}</div></div>
        <div style={{ marginBottom: 18 }}><div style={s.inputLabel}>Describe the change</div><textarea value={coForm.desc} onChange={e => setCoForm(prev => ({ ...prev, desc: e.target.value }))} placeholder="Please describe..." rows={4} style={{ ...s.input, resize: "none", fontFamily: FONT }} /></div>
        
        {/* File attachment */}
        <div style={{ marginBottom: 18 }}>
          <div style={s.inputLabel}>Attach Photo or Document (optional)</div>
          {coForm.attachmentUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: CARD, border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, padding: '14px 16px' }}>
              <span style={{ fontSize: 20 }}>📎</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: WHITE, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{coForm.attachmentName}</div>
                <div style={{ color: '#22c55e', fontSize: 13 }}>Uploaded</div>
              </div>
              <button onClick={() => setCoForm(prev => ({ ...prev, attachmentUrl: '', attachmentName: '' }))} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '6px 12px', color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
            </div>
          ) : (
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '16px 20px', background: CARD, border: '1px dashed rgba(255,255,255,0.15)', borderRadius: 12, cursor: 'pointer', opacity: coUploading ? 0.6 : 1 }}>
              <span style={{ fontSize: 18 }}>{coUploading ? '⏳' : '📎'}</span>
              <span style={{ color: GRAY, fontSize: 14 }}>{coUploading ? 'Uploading...' : 'Upload Photo, PDF, or Document'}</span>
              <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 10 * 1024 * 1024) { notify('File too large. Max 10MB'); return; }
                setCoUploading(true);
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
                  formData.append('folder', 'impressabuild/change-orders');
                  const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`, { method: 'POST', body: formData });
                  const data = await response.json();
                  if (data.secure_url) {
                    setCoForm(prev => ({ ...prev, attachmentUrl: data.secure_url, attachmentName: file.name }));
                  }
                } catch (err) { console.error(err); notify('Upload failed'); }
                setCoUploading(false);
              }} style={{ display: 'none' }} disabled={coUploading} />
            </label>
          )}
        </div>
        
        <button style={{ ...s.cta, width: "100%", opacity: coForm.type && coForm.desc ? 1 : .4, pointerEvents: coForm.type && coForm.desc ? "auto" : "none" }} onClick={async () => {
          try {
            const crData = { projectId: proj.id, projectName: proj.name, title: coForm.type, description: coForm.desc, requestedBy: user.name, userEmail: user.email, status: 'pending', createdAt: serverTimestamp() };
            if (coForm.attachmentUrl) {
              crData.attachmentUrl = coForm.attachmentUrl;
              crData.attachmentName = coForm.attachmentName;
            }
            await addDoc(collection(db, 'changeRequests'), crData);
            // Notify admin of new change request
            createPushNotification(
              ADMIN_EMAIL,
              'change_request',
              'New Change Request',
              (user.name || user.email) + ' requested a ' + coForm.type + ' for "' + proj.name + '"',
              proj.name
            );
          } catch (err) { console.error(err); }
          setCoSent(true);
          notify("Change order submitted!");
        }}>Submit Change Order →</button>
      </div>
    );
  }

  if (selected !== null) {
    const proj = filtered[selected]; const ci = catInfo(proj.category);
    return (
      <div style={s.page}>
        <button style={s.linkBtn} onClick={() => setSelected(null)}>← Back</button>
        <div style={{ height: 14 }} />
        <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 100, background: ci.color + "18", color: ci.color, fontSize: 15, fontWeight: 700, marginBottom: 10 }}>{ci.icon} {proj.category}</div>
        <Label>{proj.id}</Label>
        <h2 style={s.pageTitle}>{proj.name}</h2>
        <div style={{ color: GRAY, fontSize: 16, marginBottom: 20 }}>{proj.type} • Started {proj.date}</div>
        <div style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 20, padding: "20px 16px", marginBottom: 20 }}>
          <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 16, paddingLeft: 4 }}>PROGRESS</div>
          <div style={{ display: "flex", justifyContent: "space-between", position: "relative" }}>
            <div style={{ position: "absolute", top: 20, left: 24, right: 24, height: 3, background: "rgba(255,255,255,0.06)" }} />
            <div style={{ position: "absolute", top: 20, left: 24, width: "calc(" + (proj.stage / 4) * 100 + "% - 48px)", height: 3, background: "linear-gradient(90deg, " + O + ", rgba(255,107,0,0.4))" }} />
            {STAGES.map((st, i) => { const done = i <= proj.stage, active = i === proj.stage; return (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 7, zIndex: 1, flex: 1 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: done ? (active ? O : "rgba(255,107,0,0.2)") : "rgba(255,255,255,0.04)", border: "2px solid " + (done ? O : "rgba(255,255,255,0.08)"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, boxShadow: active ? "0 0 18px " + OGLOW : "none" }}>{STAGE_ICONS[i]}</div>
                <span style={{ fontSize: 14, fontWeight: 700, color: done ? (active ? O : "rgba(255,107,0,0.6)") : "rgba(255,255,255,0.2)" }}>{st}</span>
              </div>
            ); })}
          </div>
          <div style={{ marginTop: 18, padding: "0 4px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span style={{ color: GRAY, fontSize: 15 }}>Overall</span><span style={{ color: O, fontSize: 16, fontWeight: 700 }}>{proj.progress}%</span></div>
            <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}><div style={{ height: 6, width: proj.progress + "%", background: O, borderRadius: 3 }} /></div>
          </div>
        </div>
        {proj.stage < 4 && (
          <button style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "18px 20px", background: "rgba(255,107,0,0.06)", border: "1.5px dashed " + OBORDER, borderRadius: 16, cursor: "pointer", fontFamily: FONT, marginBottom: 20, textAlign: "left" }} onClick={() => { const idx = projects.indexOf(proj); setChangeOrder(idx); }}>
            <span style={{ fontSize: 24 }}>🔄</span>
            <div style={{ flex: 1 }}><div style={{ color: O, fontSize: 17, fontWeight: 700 }}>Change Order</div><div style={{ color: GRAY, fontSize: 15, marginTop: 3 }}>Request modifications</div></div>
            <span style={{ color: O, fontSize: 20 }}>→</span>
          </button>
        )}
        {proj.quotationUrl && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 12 }}>QUOTATION</div>
            <div style={{ background: CARD, border: "1px solid rgba(34,197,94,0.3)", borderRadius: 16, padding: "18px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                <span style={{ fontSize: 28 }}>📄</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: WHITE, fontSize: 16, fontWeight: 700 }}>{proj.quotationName || 'Quotation'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={googleViewerUrl(proj.quotationUrl)} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, textDecoration: 'none', color: '#22c55e', fontWeight: 600, fontSize: 14, fontFamily: FONT }}>
                  👁 View
                </a>
                <a href={proj.quotationUrl} target="_blank" rel="noopener noreferrer" download style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, textDecoration: 'none', color: '#3b82f6', fontWeight: 600, fontSize: 14, fontFamily: FONT }}>
                  ⬇ Download
                </a>
              </div>
            </div>
          </div>
        )}
        {proj.photos.length > 0 && <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div><div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2 }}>PHOTOS</div><div style={{ color: GRAY, fontSize: 15, marginTop: 3 }}>{proj.photos.length} files</div></div>
          </div>
          {proj.photos.map((ph, idx) => (
            <a key={idx} href={ph.url} target="_blank" rel="noopener noreferrer" style={{ ...s.photoCard, textDecoration: 'none', cursor: 'pointer' }}>
              {ph.url && /\.(jpg|jpeg|png|gif|webp)/i.test(ph.url) ? (
                <img src={ph.url} alt={ph.name || 'Photo'} style={{ width: 54, height: 54, borderRadius: 14, objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 54, height: 54, borderRadius: 14, background: "linear-gradient(135deg, " + O + ", " + O + "88)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🖼</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ color: WHITE, fontSize: 16, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ph.name || 'Photo ' + (idx + 1)}</div><div style={{ color: GRAY, fontSize: 14, marginTop: 3 }}>{ph.uploadedAt ? new Date(ph.uploadedAt).toLocaleDateString() : 'Recently'}</div></div>
              <div style={{ ...s.dlBtn, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: O }}>↗</span></div>
            </a>
          ))}
        </>}
        {proj.photos.length === 0 && <div style={{ textAlign: "center", padding: "28px 0", color: GRAY, fontSize: 16 }}>No photos uploaded yet.</div>}
        
        {/* Change Orders for this project */}
        {(() => {
          const projectCRs = changeRequests.filter(cr => cr.projectId === proj.id || cr.projectName === proj.name);
          if (projectCRs.length === 0) return null;
          return (
            <div style={{ marginTop: 20 }}>
              <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 12 }}>CHANGE ORDERS ({projectCRs.length})</div>
              {projectCRs.map(cr => (
                <div key={cr.id} style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: 16, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: WHITE, fontSize: 15, fontWeight: 700 }}>{cr.title}</div>
                      <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>{cr.description}</div>
                    </div>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, flexShrink: 0,
                      background: cr.quotationStatus === 'approved' ? 'rgba(34,197,94,0.15)' : cr.quotationStatus === 'pending_approval' ? 'rgba(245,158,11,0.15)' : cr.quotationStatus === 'rejected' ? 'rgba(239,68,68,0.15)' : cr.status === 'approved' ? 'rgba(34,197,94,0.15)' : cr.status === 'rejected' ? 'rgba(239,68,68,0.15)' : 'rgba(139,92,246,0.15)',
                      color: cr.quotationStatus === 'approved' ? '#22c55e' : cr.quotationStatus === 'pending_approval' ? '#f59e0b' : cr.quotationStatus === 'rejected' ? '#ef4444' : cr.status === 'approved' ? '#22c55e' : cr.status === 'rejected' ? '#ef4444' : '#8b5cf6'
                    }}>{cr.quotationStatus === 'approved' ? 'Approved' : cr.quotationStatus === 'pending_approval' ? 'Awaiting Approval' : cr.quotationStatus === 'rejected' ? 'Rejected' : cr.status === 'approved' ? 'Approved' : cr.status === 'rejected' ? 'Rejected' : 'Pending'}</span>
                  </div>
                  {cr.quotationAmount > 0 && (
                    <div style={{ color: O, fontSize: 14, fontWeight: 700, marginBottom: 6 }}>SAR {cr.quotationAmount.toLocaleString()}</div>
                  )}
                  {/* Work Progress Bar */}
                  {cr.quotationStatus === 'approved' && typeof cr.workProgress === 'number' && (
                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: GRAY, fontSize: 12 }}>Work Progress</span>
                        <span style={{ color: '#22c55e', fontSize: 13, fontWeight: 700 }}>{cr.workProgress}%</span>
                      </div>
                      <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3 }}>
                        <div style={{ height: 6, width: cr.workProgress + '%', background: '#22c55e', borderRadius: 3, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  )}
                  {cr.quotationFileUrl && (
                    <a href={cr.quotationFileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, textDecoration: 'none', marginTop: 6 }}>
                      <span style={{ fontSize: 14 }}>📄</span>
                      <span style={{ color: '#3b82f6', fontSize: 13, fontWeight: 600 }}>{cr.quotationFileName || 'View Quotation'}</span>
                      <span style={{ color: '#3b82f6', fontSize: 14, marginLeft: 'auto' }}>↗</span>
                    </a>
                  )}
                  {cr.attachmentUrl && (
                    <a href={cr.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 8, textDecoration: 'none', marginTop: 6 }}>
                      <span style={{ fontSize: 14 }}>📎</span>
                      <span style={{ color: '#8b5cf6', fontSize: 13, fontWeight: 600 }}>{cr.attachmentName || 'Your Attachment'}</span>
                      <span style={{ color: '#8b5cf6', fontSize: 14, marginLeft: 'auto' }}>↗</span>
                    </a>
                  )}
                  
                  {/* Approve/Reject buttons for pending approval */}
                  {cr.quotationStatus === 'pending_approval' && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={async () => {
                          try {
                            await updateDoc(doc(db, 'changeRequests', cr.id), { quotationStatus: 'approved', status: 'approved', updatedAt: serverTimestamp() });
                            if (cr.quotationAmount && cr.quotationAmount > 0) {
                              await addDoc(collection(db, 'invoices'), {
                                clientEmail: cr.userEmail, projectName: cr.projectName,
                                description: 'Change Order Invoice: ' + cr.title + ' for "' + cr.projectName + '"',
                                totalAmount: cr.quotationAmount, amount: cr.quotationAmount, paid: 0, status: 'pending',
                                fileUrl: cr.quotationFileUrl || '', createdAt: serverTimestamp()
                              });
                              createPushNotification(cr.userEmail, 'invoice', 'Change Order Invoice Created', 'An invoice of SAR ' + cr.quotationAmount.toLocaleString() + ' has been created for your change request "' + cr.title + '" on "' + cr.projectName + '".', cr.projectName);
                            }
                            createPushNotification(ADMIN_EMAIL, 'cr_quotation_approved', 'Change Request Approved — Proceed!', (user.name || user.email) + ' approved the change request "' + cr.title + '" for "' + cr.projectName + '". Please proceed with the work.', cr.projectName);
                            notify('Change order approved!');
                          } catch (err) { console.error(err); notify('Error'); }
                        }} style={{ flex: 1, padding: '12px 16px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 10, color: '#22c55e', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: FONT }}>
                          ✓ Approve
                        </button>
                        <button onClick={async () => {
                          try {
                            await updateDoc(doc(db, 'changeRequests', cr.id), { quotationStatus: 'rejected', updatedAt: serverTimestamp() });
                            createPushNotification(ADMIN_EMAIL, 'cr_quotation_rejected', 'Change Request Rejected', (user.name || user.email) + ' rejected the change request "' + cr.title + '" for "' + cr.projectName + '".', cr.projectName);
                            notify('Change order rejected');
                          } catch (err) { console.error(err); notify('Error'); }
                        }} style={{ flex: 1, padding: '12px 16px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 10, color: '#ef4444', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: FONT }}>
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div style={{ color: GRAY, fontSize: 12, marginTop: 8 }}>{cr.createdAt?.toDate?.()?.toLocaleDateString?.() || 'Recently'}</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div style={s.page}>
      <Label>My Projects</Label>
      <h2 style={s.pageTitle}>Project Tracker</h2>
      <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 20, paddingBottom: 6 }}>
        {cats.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{ padding: "10px 18px", borderRadius: 100, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap", background: filter === c ? O : CARD, color: filter === c ? BG : WHITE, border: "1px solid " + (filter === c ? O : "rgba(255,255,255,0.06)") }}>
            {c === "All" ? "All" : (catInfo(c).icon + " " + c)}
          </button>
        ))}
      </div>
      {filtered.length === 0 && <div style={{ textAlign: "center", padding: "40px 0", color: GRAY }}>No projects in this category.</div>}
      {filtered.map((p, i) => { const ci = catInfo(p.category); return (
        <button key={i} style={s.projCard} onClick={() => setSelected(i)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: 100, background: ci.color + "18", color: ci.color, fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{ci.icon} {p.category}</div>
              <div style={{ color: WHITE, fontSize: 18, fontWeight: 700 }}>{p.name}</div>
              <div style={{ color: GRAY, fontSize: 15, marginTop: 4 }}>{p.type} • {p.date}</div>
            </div>
            <div style={{ padding: "6px 14px", borderRadius: 100, fontSize: 15, fontWeight: 700, background: p.stage === 4 ? "rgba(76,212,154,0.12)" : OGLOW, color: p.stage === 4 ? GREEN : O, flexShrink: 0 }}>{STAGES[p.stage]}</div>
          </div>
          <div style={{ marginTop: 14 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: GRAY, fontSize: 14 }}>Progress</span><span style={{ color: O, fontSize: 15, fontWeight: 700 }}>{p.progress}%</span></div>
            <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}><div style={{ height: 5, width: p.progress + "%", background: p.stage === 4 ? GREEN : O, borderRadius: 3 }} /></div>
          </div>
          {p.photos && p.photos.length > 0 && <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}><span style={{ fontSize: 14 }}>🖼</span><span style={{ color: GRAY, fontSize: 14 }}>{p.photos.length} photos</span><span style={{ color: O, fontSize: 18, marginLeft: "auto" }}>→</span></div>}
        </button>
      ); })}
      
      {/* Completed Projects */}
      {completedProjects.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ color: '#22c55e', fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>✅</span> COMPLETED PROJECTS ({completedProjects.length})
          </div>
          {completedProjects.map((p) => (
            <div key={p.id} style={{ background: CARD, border: '1px solid rgba(34,197,94,0.2)', borderRadius: 16, padding: 18, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: 100, background: 'rgba(34,197,94,0.12)', color: '#22c55e', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>✅ Completed</div>
                  <div style={{ color: WHITE, fontSize: 18, fontWeight: 700 }}>{p.name}</div>
                  <div style={{ color: GRAY, fontSize: 15, marginTop: 4 }}>{p.type} • {p.date}</div>
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}><div style={{ height: 5, width: "100%", background: '#22c55e', borderRadius: 3 }} /></div>
              </div>
              
              {/* Feedback */}
              {!p.feedbackSubmitted ? (
                <FeedbackWidget key={'feedback-' + p.id} projectId={p.id} projectName={p.name} userName={user.name || user.email} notify={notify} />
              ) : (
                <div style={{ marginTop: 12, padding: '12px 14px', background: 'rgba(34,197,94,0.08)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16 }}>✅</span>
                    <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 600 }}>Feedback submitted — {'★'.repeat(p.surveyRating || 0)}{'☆'.repeat(5 - (p.surveyRating || 0))}</span>
                  </div>
                  {p.feedbackComment && <div style={{ color: GRAY, fontSize: 13, marginTop: 4 }}>"{p.feedbackComment}"</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackWidget({ projectId, projectName, userName, notify }) {
  const [selected, setSelected] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const submitFeedback = async () => {
    if (!selected) { notify('Please select a rating'); return; }
    setSubmitting(true);
    try {
      await updateDoc(doc(db, 'projects', projectId), { 
        feedbackSubmitted: true,
        surveySubmitted: true,
        surveyRating: selected,
        feedbackComment: comment.trim(),
        feedbackDate: serverTimestamp()
      });
      createPushNotification(ADMIN_EMAIL, 'feedback', 'Client Feedback Received', userName + ' rated "' + projectName + '" ' + selected + '/5 stars' + (comment.trim() ? ': ' + comment.trim().substring(0, 60) : ''), projectName);
      notify('Thank you for your feedback!');
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  return (
    <div style={{ marginTop: 16, padding: 16, background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.15)', borderRadius: 12 }}>
      <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1, marginBottom: 12 }}>⭐ RATE YOUR EXPERIENCE</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
        {[1, 2, 3, 4, 5].map(star => (
          <button key={star} onClick={() => !submitting && setSelected(star)} disabled={submitting}
            style={{ width: 44, height: 44, borderRadius: 10, background: selected >= star ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.03)', border: selected >= star ? '2px solid #f59e0b' : '2px solid rgba(255,255,255,0.1)', cursor: submitting ? 'wait' : 'pointer', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: selected >= star ? '#f59e0b' : 'rgba(255,255,255,0.3)' }}>{selected >= star ? '★' : '☆'}</span>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: GRAY, fontSize: 11, paddingLeft: 4, paddingRight: 4, marginBottom: 12 }}>
        <span>Poor</span><span>Excellent</span>
      </div>
      <div style={{ color: GRAY, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>💬 Comments (optional)</div>
      <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Any comments or suggestions..." rows={3} style={{ width: '100%', padding: '10px 12px', background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: WHITE, fontSize: 14, fontFamily: FONT, resize: 'none', marginBottom: 10, boxSizing: 'border-box' }} />
      <button onClick={submitFeedback} disabled={submitting || !selected} style={{ width: '100%', padding: '12px 16px', background: selected ? O : CARD, border: 'none', borderRadius: 10, color: selected ? BG : GRAY, fontSize: 15, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', fontFamily: FONT, opacity: submitting ? 0.6 : 1 }}>
        {submitting ? 'Submitting...' : selected ? 'Submit ' + selected + '/5 Rating' : 'Select a rating'}
      </button>
    </div>
  );
}

function TapCardModal({ payInfo, user, onClose, onCharge }) {
  const [cardReady, setCardReady] = useState(false);
  const [processing, setProcessing] = useState(false);

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, overflowY: 'auto' }}>
      <div style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, width: '100%', maxWidth: 440, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ color: WHITE, fontSize: 20, fontWeight: 700, margin: 0 }}>Card Payment</h2>
          <button onClick={onClose} disabled={processing} style={{ background: 'none', border: 'none', color: GRAY, fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ color: O, fontSize: 26, fontWeight: 700, marginBottom: 16 }}>SAR {payInfo.amount.toLocaleString()}</div>
        
        <div style={{ marginBottom: 16 }}>
          <TapCard
            publicKey={TAP_PUBLIC_KEY}
            merchant={{ id: TAP_MERCHANT_ID }}
            transaction={{ amount: payInfo.amount, currency: Currencies.SAR }}
            customer={{
              name: [{ lang: Locale.EN, first: user.name || 'Customer', last: '', middle: '' }],
              nameOnCard: user.name || 'Customer',
              editable: true,
              contact: {
                email: user.email,
                phone: { countryCode: '966', number: (user.phone || '500000000').replace(/[^0-9]/g, '').replace(/^966/, '').replace(/^0/, '') }
              }
            }}
            acceptance={{ supportedBrands: ['VISA', 'MASTERCARD', 'MADA', 'AMEX'], supportedCards: ['CREDIT', 'DEBIT'] }}
            fields={{ cardHolder: true }}
            addons={{ displayPaymentBrands: true, loader: true, saveCard: false }}
            interface={{ locale: Locale.EN, theme: Theme.DARK, edges: Edges.CURVED, direction: Direction.LTR }}
            onReady={() => setCardReady(true)}
            onSuccess={(data) => { onCharge(data); }}
            onError={(err) => {
              console.error('Card error:', err);
              setProcessing(false);
              let msg = 'Card validation failed.';
              try {
                if (typeof err === 'string') msg = err;
                else if (err?.message) msg = err.message;
                else if (err?.errors?.length) msg = err.errors.map(e => e.description || e.code).join(', ');
                else msg = JSON.stringify(err).substring(0, 200);
              } catch(e) {}
              alert('Payment error: ' + msg);
            }}
          />
        </div>

        <button
          disabled={!cardReady || processing}
          onClick={() => { setProcessing(true); try { tokenize(); } catch(e) { setProcessing(false); alert('Please complete card details first'); } }}
          style={{ width: '100%', padding: '16px', background: (!cardReady || processing) ? 'rgba(255,107,0,0.4)' : O, border: 'none', borderRadius: 12, color: '#0A0A0A', fontSize: 16, fontWeight: 700, cursor: (!cardReady || processing) ? 'default' : 'pointer', fontFamily: FONT }}
        >
          {processing ? 'Processing...' : !cardReady ? 'Loading secure form...' : 'Pay SAR ' + payInfo.amount.toLocaleString()}
        </button>
        <div style={{ color: GRAY, fontSize: 12, textAlign: 'center', marginTop: 12 }}>🔒 Secured by Tap Payments. Your card details are encrypted.</div>
      </div>
    </div>,
    document.body
  );
}

function FinancialsPage({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [extraOrders, setExtraOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [invoicesExpanded, setInvoicesExpanded] = useState(false);
  const [expandedProj, setExpandedProj] = useState(null);
  const companyEmails = useCompanyEmails(user);

  useEffect(() => {
    const q1 = query(collection(db, 'invoices'), where('clientEmail', 'in', companyEmails));
    const unsub1 = onSnapshot(q1, (snap) => {
      const invs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      invs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setInvoices(invs);
      setLoading(false);
    }, () => setLoading(false));

    const q2 = query(collection(db, 'extraOrders'), where('clientEmail', 'in', companyEmails));
    const unsub2 = onSnapshot(q2, (snap) => {
      const extras = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      extras.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setExtraOrders(extras);
    }, () => {});

    return () => { unsub1(); unsub2(); };
  }, [companyEmails]);

  // ===== TAP PAYMENTS - CARD SDK V2 + CLOUD FUNCTION FLOW =====
  const [tapPayModal, setTapPayModal] = useState(null); // { type: 'invoice'|'extra', item, amount }
  const tapVerifiedRef = useRef(false);

  const startHostedPayment = async (type, item, payAmount, description) => {
    try {
      localStorage.setItem('pendingTapPayment', JSON.stringify({ type, id: item.id, amount: payAmount }));
      const res = await fetch(TAP_CHARGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: payAmount,
          currency: 'SAR',
          description: description,
          customer: {
            first_name: user.name || user.email.split('@')[0],
            last_name: '',
            email: user.email,
            phone: { country_code: '966', number: (user.phone || '500000000').replace(/[^0-9]/g, '').replace(/^966/, '').replace(/^0/, '') }
          },
          reference: { transaction: 'txn_' + Date.now(), order: item.id },
          meta: { type: type, id: item.id },
          redirectUrl: window.location.origin + '/'
        })
      });
      const data = await res.json();
      if (data.error) {
        alert('Payment failed: ' + data.error);
        localStorage.removeItem('pendingTapPayment');
        setPayingInvoice(null);
        return;
      }
      if (data.redirect) {
        window.location.href = data.redirect; // Tap hosted payment page
      } else if (data.status === 'CAPTURED') {
        if (type === 'invoice') await updateInvoiceAfterPayment(item.id, payAmount, data.id);
        else await updateExtraOrderAfterPayment(item.id, payAmount, data.id);
        localStorage.removeItem('pendingTapPayment');
        setPayingInvoice(null);
      } else {
        alert('Could not start payment (status: ' + (data.status || 'unknown') + '). Please try again.');
        localStorage.removeItem('pendingTapPayment');
        setPayingInvoice(null);
      }
    } catch (e) {
      alert('Payment error: ' + e.message);
      localStorage.removeItem('pendingTapPayment');
      setPayingInvoice(null);
    }
  };

  const handlePayInvoice = (invoice, customAmount) => {
    const invDue = (invoice.amount || 0) - (invoice.paid || 0);
    const payAmount = Math.round((customAmount || invDue) * 100) / 100;
    if (payAmount <= 0 || payAmount > invDue) { alert('Invalid payment amount'); return; }
    setPayingInvoice(invoice.id);
    startHostedPayment('invoice', invoice, payAmount, (invoice.description || 'Invoice Payment') + ' - ' + (invoice.projectName || ''));
  };

  const handlePayExtraOrder = (extraOrder) => {
    const eoDue = (extraOrder.amount || 0) - (extraOrder.paid || 0);
    if (eoDue <= 0) return;
    setPayingInvoice(extraOrder.id);
    startHostedPayment('extra', extraOrder, Math.round(eoDue * 100) / 100, 'Extra: ' + (extraOrder.description || 'Extra Order'));
  };

  // Update invoice after successful payment
  const updateInvoiceAfterPayment = async (invoiceId, amount, chargeId) => {
    try {
      const inv = invoices.find(i => i.id === invoiceId);
      if (inv) {
        if (inv.tapChargeId === chargeId) { return; } // webhook already recorded this charge
        const invoiceRef = doc(db, 'invoices', invoiceId);
        const newPaid = (inv.paid || 0) + amount;
        const newStatus = newPaid >= (inv.amount || 0) ? 'paid' : 'partial';
        await updateDoc(invoiceRef, { 
          paid: newPaid, 
          status: newStatus,
          lastPaymentDate: serverTimestamp(),
          lastPaymentAmount: amount,
          tapChargeId: chargeId
        });
        alert('✅ Payment successful! Thank you.');
      }
    } catch (err) {
      console.error('Error updating invoice:', err);
      alert('Payment received but update failed. Please contact support.');
    }
  };
  
  // Update extra order after successful payment
  const updateExtraOrderAfterPayment = async (extraOrderId, amount, chargeId) => {
    try {
      const eo = extraOrders.find(e => e.id === extraOrderId);
      if (eo) {
        if (eo.tapChargeId === chargeId) { return; } // webhook already recorded this charge
        const eoRef = doc(db, 'extraOrders', extraOrderId);
        const newPaid = (eo.paid || 0) + amount;
        const newStatus = newPaid >= (eo.amount || 0) ? 'paid' : 'partial';
        await updateDoc(eoRef, { 
          paid: newPaid, 
          status: newStatus,
          lastPaymentDate: serverTimestamp(),
          lastPaymentAmount: amount,
          tapChargeId: chargeId
        });
        alert('✅ Payment successful! Thank you.');
      }
    } catch (err) {
      console.error('Error updating extra order:', err);
      alert('Payment received but update failed. Please contact support.');
    }
  };

  // Send token to Cloud Function to create the charge
  const processTapCharge = async (tokenData) => {
    try {
      const p = tapPayModal;
      if (!p) return;
      localStorage.setItem('pendingTapPayment', JSON.stringify({ type: p.type, id: p.item.id, amount: p.amount }));
      const res = await fetch(TAP_CHARGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: tokenData.id,
          amount: p.amount,
          currency: 'SAR',
          description: p.type === 'invoice' ? ((p.item.description || 'Invoice Payment') + ' - ' + (p.item.projectName || '')) : ('Extra: ' + (p.item.description || 'Extra Order')),
          customer: {
            first_name: user.name || user.email.split('@')[0],
            last_name: '',
            email: user.email,
            phone: { country_code: '966', number: (user.phone || '500000000').replace(/[^0-9]/g, '').replace(/^966/, '').replace(/^0/, '') }
          },
          reference: { transaction: 'txn_' + Date.now(), order: p.item.id },
          redirectUrl: window.location.origin + '/'
        })
      });
      const data = await res.json();
      if (data.error) {
        alert('Payment failed: ' + data.error);
        localStorage.removeItem('pendingTapPayment');
        setTapPayModal(null);
        return;
      }
      if (data.status === 'CAPTURED') {
        if (p.type === 'invoice') await updateInvoiceAfterPayment(p.item.id, p.amount, data.id);
        else await updateExtraOrderAfterPayment(p.item.id, p.amount, data.id);
        localStorage.removeItem('pendingTapPayment');
        setTapPayModal(null);
      } else if (data.redirect) {
        // 3D Secure - redirect to bank verification
        window.location.href = data.redirect;
      } else {
        alert('Payment status: ' + (data.status || 'unknown') + '. If money was deducted, contact support.');
        localStorage.removeItem('pendingTapPayment');
        setTapPayModal(null);
      }
    } catch (e) {
      alert('Payment error: ' + e.message);
      setTapPayModal(null);
    }
  };

  // Verify payment after returning from 3D Secure redirect
  useEffect(() => {
    if (tapVerifiedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const tapId = params.get('tap_id');
    if (!tapId) return;
    const pendingRaw = localStorage.getItem('pendingTapPayment');
    if (!pendingRaw) { window.history.replaceState({}, '', window.location.pathname); return; }
    if (invoices.length === 0 && extraOrders.length === 0) return; // wait for data to load
    tapVerifiedRef.current = true;
    (async () => {
      try {
        const pending = JSON.parse(pendingRaw);
        const res = await fetch(TAP_VERIFY_URL + '?charge_id=' + encodeURIComponent(tapId));
        const data = await res.json();
        if (data.status === 'CAPTURED') {
          if (pending.type === 'invoice') await updateInvoiceAfterPayment(pending.id, pending.amount, tapId);
          else await updateExtraOrderAfterPayment(pending.id, pending.amount, tapId);
        } else {
          alert('Payment was not completed (status: ' + data.status + '). Please try again.');
        }
      } catch (e) { console.error('Verify error:', e); }
      localStorage.removeItem('pendingTapPayment');
      window.history.replaceState({}, '', window.location.pathname);
    })();
  }, [invoices, extraOrders]);


  // Calculate totals with useMemo - FIXED: totalAmount is per PROJECT not per invoice
  const { projectTotals, totalProjectAmount, totalInvoiced, totalInvoicePaid, totalExtras, totalExtrasPaid, grandTotal, grandPaid, grandRemaining, invoiceDue } = useMemo(() => {
    // Get unique projects and their totalAmount (take the max totalAmount per project)
    const projTotals = {};
    invoices.forEach(inv => {
      const projKey = inv.projectName || 'unknown';
      const invTotal = inv.totalAmount || 0;
      if (!projTotals[projKey] || invTotal > projTotals[projKey]) {
        projTotals[projKey] = invTotal;
      }
    });
    const totalProjAmt = Object.values(projTotals).reduce((sum, val) => sum + val, 0);
    const totalInv = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    const totalInvPaid = invoices.reduce((sum, inv) => sum + (inv.paid || 0), 0);
    const totalExt = extraOrders.reduce((sum, eo) => sum + (eo.amount || 0), 0);
    const totalExtPaid = extraOrders.reduce((sum, eo) => sum + (eo.paid || 0), 0);
    const gTotal = totalProjAmt + totalExt;
    const gPaid = totalInvPaid + totalExtPaid;
    const gRemaining = gTotal - gPaid;
    const invDue = totalInv - totalInvPaid + totalExt - totalExtPaid;
    return { projectTotals: projTotals, totalProjectAmount: totalProjAmt, totalInvoiced: totalInv, totalInvoicePaid: totalInvPaid, totalExtras: totalExt, totalExtrasPaid: totalExtPaid, grandTotal: gTotal, grandPaid: gPaid, grandRemaining: gRemaining, invoiceDue: invDue };
  }, [invoices, extraOrders]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-SA', { style: 'currency', currency: 'SAR' }).format(amount);
  }, []);

  return (
    <div style={s.page}>
      {tapPayModal && <TapCardModal payInfo={tapPayModal} user={user} onClose={() => setTapPayModal(null)} onCharge={processTapCharge} />}
      <Label>Financials</Label>
      <h2 style={s.pageTitle}>Payments & Invoices</h2>

      {/* Grand Total Bar */}
      <div style={{ background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 16, padding: 16, marginBottom: 20 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: GRAY, fontSize: 15, fontWeight: 600, letterSpacing: 1 }}>TOTAL PROJECT VALUE</div>
          <div style={{ color: WHITE, fontSize: 26, fontWeight: 700, marginTop: 4 }}>{formatCurrency(grandTotal)}</div>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: GRAY, fontSize: 13 }}>Invoiced</div>
            <div style={{ color: WHITE, fontSize: 15, fontWeight: 600 }}>{formatCurrency(totalInvoiced + totalExtras)}</div>
          </div>
          <div>
            <div style={{ color: GRAY, fontSize: 13 }}>Paid</div>
            <div style={{ color: '#22c55e', fontSize: 15, fontWeight: 600 }}>{formatCurrency(grandPaid)}</div>
          </div>
          <div>
            <div style={{ color: GRAY, fontSize: 13 }}>Remaining</div>
            <div style={{ color: '#ef4444', fontSize: 15, fontWeight: 600 }}>{formatCurrency(grandRemaining)}</div>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 16, marginBottom: 24 }}>
        <div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>BANK DETAILS</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr><td style={{ color: GRAY, fontSize: 15, paddingBottom: 8, width: 80, verticalAlign: 'top' }}>Company</td><td style={{ color: WHITE, fontSize: 15, fontWeight: 600, paddingBottom: 8 }}>ImpressaBuild Solutions Company</td></tr>
            <tr><td style={{ color: GRAY, fontSize: 15, paddingBottom: 8, verticalAlign: 'top' }}>Bank</td><td style={{ color: WHITE, fontSize: 15, fontWeight: 600, paddingBottom: 8 }}>Saudi National Bank</td></tr>
            <tr><td style={{ color: GRAY, fontSize: 15, paddingBottom: 8, verticalAlign: 'top' }}>IBAN</td><td style={{ color: O, fontSize: 15, fontWeight: 600, paddingBottom: 8, wordBreak: 'break-all' }}>SA5810000010400000923106</td></tr>
            <tr><td style={{ color: GRAY, fontSize: 15, verticalAlign: 'top' }}>Swift Code</td><td style={{ color: WHITE, fontSize: 15, fontWeight: 600 }}>NCBKSAJE</td></tr>
          </tbody>
        </table>
      </div>

      {/* Invoices Folder */}
      <button onClick={() => setInvoicesExpanded(!invoicesExpanded)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', background: CARD, border: '1px solid ' + (invoicesExpanded ? O : 'rgba(255,255,255,0.06)'), borderRadius: 16, cursor: 'pointer', fontFamily: FONT, textAlign: 'left', marginBottom: 12 }}>
        <span style={{ fontSize: 22 }}>{invoicesExpanded ? '📂' : '📁'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: WHITE, fontSize: 17, fontWeight: 700 }}>Invoices</div>
          <div style={{ color: GRAY, fontSize: 13 }}>{invoices.length + extraOrders.length} item(s)</div>
        </div>
        <div style={{ color: invoiceDue > 0 ? '#ef4444' : '#22c55e', fontSize: 14, fontWeight: 600 }}>{invoiceDue > 0 ? formatCurrency(invoiceDue) + ' due' : '✅ All Paid'}</div>
        <span style={{ color: GRAY, fontSize: 18, marginLeft: 4 }}>{invoicesExpanded ? '▼' : '▶'}</span>
      </button>

      {invoicesExpanded && (
        <div style={{ marginLeft: 16, marginBottom: 20 }}>
          {(() => {
            const grouped = {};
            invoices.forEach(inv => { const k = inv.projectName || 'General'; if (!grouped[k]) grouped[k] = []; grouped[k].push(inv); });
            extraOrders.forEach(eo => { const k = eo.projectName || 'General'; if (!grouped[k]) grouped[k] = []; grouped[k].push({ ...eo, _isExtra: true }); });
            return Object.entries(grouped).map(([projName, items]) => {
              const projDue = items.reduce((s, i) => s + ((i.amount || 0) - (i.paid || 0)), 0);
              const isOpen = expandedProj === projName;
              return (
                <div key={projName} style={{ marginBottom: 8 }}>
                  <button onClick={() => setExpandedProj(isOpen ? null : projName)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: CARD, border: '1px solid ' + (isOpen ? 'rgba(255,107,0,0.3)' : 'rgba(255,255,255,0.04)'), borderRadius: 12, cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
                    <span style={{ fontSize: 16 }}>{isOpen ? '📂' : '📁'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: WHITE, fontSize: 14, fontWeight: 700 }}>{projName}</div>
                    </div>
                    <div style={{ color: projDue > 0 ? '#ef4444' : '#22c55e', fontSize: 13, fontWeight: 600 }}>{projDue > 0 ? formatCurrency(projDue) : '✅'}</div>
                    <span style={{ color: GRAY, fontSize: 14 }}>{isOpen ? '▼' : '▶'}</span>
                  </button>
                  {isOpen && (
                    <div style={{ marginLeft: 14, marginTop: 6 }}>
                      {items.map((inv, i) => {
                        const invDue = (inv.amount || 0) - (inv.paid || 0);
                        const isPaid = inv.status === 'paid' || invDue <= 0;
                        return (
                          <div key={i} style={{ background: BG, border: '1px solid ' + (isPaid ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'), borderRadius: 10, padding: 12, marginBottom: 6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <div style={{ color: WHITE, fontSize: 13, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv._isExtra ? '🔧 ' : ''}{(inv.description || 'Invoice').replace(/ - .*$/, '').replace(/ for ".*"$/, '')}</div>
                              <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: isPaid ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: isPaid ? '#22c55e' : '#ef4444', marginLeft: 8 }}>{isPaid ? 'PAID' : 'DUE'}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 16 }}>
                              <div><div style={{ color: GRAY, fontSize: 11 }}>Amount</div><div style={{ color: WHITE, fontSize: 14, fontWeight: 600 }}>{formatCurrency(inv.amount || 0)}</div></div>
                              <div><div style={{ color: GRAY, fontSize: 11 }}>{isPaid ? 'Paid' : 'Due'}</div><div style={{ color: isPaid ? '#22c55e' : '#ef4444', fontSize: 14, fontWeight: 600 }}>{isPaid ? formatCurrency(inv.paid || 0) : formatCurrency(invDue)}</div></div>
                            </div>
                            {inv.fileUrl && <a href={inv.fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: 8, padding: '6px 12px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 8, color: O, fontSize: 12, fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}>📄 View Invoice</a>}
                            {!isPaid && invDue > 0 && !inv._isExtra && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                                  <span style={{ color: GRAY, fontSize: 12 }}>Pay amount:</span>
                                  <input type="number" defaultValue={invDue} min="1" max={invDue} step="0.01"
                                    onChange={e => { inv._payAmount = parseFloat(e.target.value); }}
                                    style={{ flex: 1, padding: '8px 10px', background: CARD, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, color: WHITE, fontSize: 13, fontFamily: FONT }} />
                                  <span style={{ color: GRAY, fontSize: 12 }}>SAR</span>
                                </div>
                                <button onClick={() => {
                                  const payAmt = inv._payAmount || invDue;
                                  if (payAmt <= 0 || payAmt > invDue) { alert('Please enter a valid amount between 1 and ' + invDue); return; }
                                  handlePayInvoice(inv, payAmt);
                                }} disabled={paymentProcessing} style={{ width: '100%', padding: '10px 14px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: 8, color: WHITE, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>💳 Pay Now</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* Contact for questions */}
    </div>
  );
}

function ContactPage({ notify }) {
  const [sent, setSent] = useState(false);
  return (
    <div style={s.page}>
      <Label>Get in Touch</Label>
      <h2 style={s.pageTitle}>Contact Us</h2>
      {[
        { icon: "📍", label: "Offices", value: "KSA — Riyadh\nUSA — Cumberland, RI" },
        { icon: "📱", label: "WhatsApp", value: "+966 58 091 4414", action: () => window.open('https://wa.me/966580914414', '_blank') },
        { icon: "📞", label: "Call", value: "+966 58 091 4414 (KSA)\n+1 (774) 991-4616 (US)", action: () => window.open('tel:+966580914414') },
        { icon: "✉️", label: "Email", value: "contact@impressabuild.com", action: () => window.open('mailto:contact@impressabuild.com') },
        { icon: "🔗", label: "Links", value: "linktr.ee/impressabuild", action: () => window.open('https://linktr.ee/impressabuild', '_blank') }
      ].map((c, i) => (
        <div key={i} onClick={c.action} style={{ ...s.contactCard, cursor: c.action ? 'pointer' : 'default' }}><span style={{ fontSize: 28 }}>{c.icon}</span><div style={{ flex: 1 }}><div style={{ color: O, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>{c.label}</div><div style={{ color: WHITE, fontSize: 17, marginTop: 6, whiteSpace: "pre-line", lineHeight: 1.6 }}>{c.value}</div></div>{c.action && <span style={{ color: O, fontSize: 18 }}>→</span>}</div>
      ))}
      <div style={{ marginTop: 26 }}>
        <Label>Send a Message</Label>
        {!sent ? <div style={{ marginTop: 14 }}><input placeholder="Your Name" style={s.input} /><div style={{ height: 12 }} /><input placeholder="Email" style={s.input} /><div style={{ height: 12 }} /><textarea placeholder="Message" rows={4} style={{ ...s.input, resize: "none", fontFamily: FONT }} /><button style={{ ...s.cta, width: "100%", marginTop: 14 }} onClick={() => { setSent(true); notify("Message sent!"); }}>Send →</button></div>
          : <div style={{ textAlign: "center", padding: "28px 0" }}><div style={{ fontSize: 40, color: GREEN }}>✓</div><p style={{ color: O, fontWeight: 700, marginTop: 8, fontSize: 18 }}>Sent!</p></div>}
      </div>
    </div>
  );
}

function AccountPage({ user, logout, onShowInstall }) {
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberForm, setMemberForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [creating, setCreating] = useState(false);
  const companyEmails = useCompanyEmails(user);
  
  useEffect(() => {
    const q = query(collection(db, 'projects'), where('clientEmail', 'in', companyEmails));
    const unsub = onSnapshot(q, (snap) => {
      setProjects(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(p => !p.archived));
    });
    return () => unsub();
  }, [companyEmails]);

  const [userCompanyName, setUserCompanyName] = useState(user.company || '');

  // Fetch team members - look up company from Firestore if needed
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        let company = user.company || '';
        // If no company in user object, look it up from Firestore
        if (!company) {
          snap.docs.forEach(d => {
            const data = d.data();
            if (data.email && data.email.toLowerCase() === user.email.toLowerCase() && data.company) {
              company = data.company;
            }
          });
        }
        setUserCompanyName(company);
        if (!company) return;
        const members = [];
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.company && data.company.toLowerCase() === company.toLowerCase()) {
            members.push({ id: d.id, ...data });
          }
        });
        setTeamMembers(members);
      } catch(e) {}
    })();
  }, [user.email, user.company, creating]);

  const addTeamMember = async () => {
    if (!memberForm.name || !memberForm.email || !memberForm.password) { alert('Name, email and password required'); return; }
    if (memberForm.password.length < 6) { alert('Password must be at least 6 characters'); return; }
    setCreating(true);
    try {
      if (!auth.currentUser) await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
      await addDoc(collection(db, 'users'), {
        name: memberForm.name,
        email: memberForm.email.toLowerCase().trim(),
        company: userCompanyName,
        phone: memberForm.phone,
        password: memberForm.password,
        isFirstLogin: true,
        isCompanyAdmin: false,
        addedBy: user.email,
        createdAt: serverTimestamp()
      });
      setShowAddMember(false);
      setMemberForm({ name: '', email: '', phone: '', password: '' });
    } catch(e) { alert('Error: ' + e.message); }
    setCreating(false);
  };

  const removeTeamMember = async (member) => {
    if (member.email === user.email) { alert("You can't remove yourself"); return; }
    if (member.isCompanyAdmin) { alert("Can't remove the company admin"); return; }
    if (!window.confirm('Remove ' + member.name + ' from the team?')) return;
    try {
      if (!auth.currentUser) await signInWithEmailAndPassword(auth, ADMIN_EMAIL, MASTER_PASSWORD);
      await deleteDoc(doc(db, 'users', member.id));
      setTeamMembers(prev => prev.filter(m => m.id !== member.id));
    } catch(e) { alert('Error'); }
  };

  const userName = user.name || user.email.split('@')[0];
  const userCompany = userCompanyName || user.email.split('@')[1]?.split('.')[0] || 'Client';
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  return (
    <div style={s.page}>
      <Label>My Account</Label>
      <h2 style={s.pageTitle}>Profile</h2>
      <div style={{ display: "flex", alignItems: "center", gap: 16, background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 20, padding: "20px 18px", marginBottom: 20 }}>
        <IconMark size={54} />
        <div>
          <div style={{ color: WHITE, fontSize: 20, fontWeight: 700 }}>{userName}</div>
          <div style={{ color: GRAY, fontSize: 16, marginTop: 4 }}>{userCompany}</div>
          <div style={{ color: O, fontSize: 15, marginTop: 4 }}>{user.email}</div>
          {(user.isCompanyAdmin || user.isCompanyAdmin === undefined) && <div style={{ color: '#8b5cf6', fontSize: 12, marginTop: 4, fontWeight: 600 }}>Company Admin</div>}
        </div>
      </div>
      <div style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 18, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}><span style={{ fontSize: 20 }}>📊</span><span style={{ color: WHITE, fontSize: 17 }}>Projects</span></div>
          <span style={{ color: O, fontSize: 20, fontWeight: 700 }}>{projects.length}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}><span style={{ fontSize: 20 }}>👥</span><span style={{ color: WHITE, fontSize: 17 }}>Team Members</span></div>
          <span style={{ color: O, fontSize: 20, fontWeight: 700 }}>{teamMembers.length}</span>
        </div>
      </div>

      {/* Team Members Section */}
      {userCompanyName ? (
        <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 18, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ color: WHITE, fontSize: 17, fontWeight: 700 }}>👥 Team Members</div>
            {(user.isCompanyAdmin || user.isCompanyAdmin === undefined) && (
              <button onClick={() => setShowAddMember(!showAddMember)} style={{ padding: '6px 14px', background: OGLOW, border: '1px solid ' + OBORDER, borderRadius: 8, color: O, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>{showAddMember ? 'Cancel' : '+ Add'}</button>
            )}
          </div>

          {showAddMember && (
            <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <input value={memberForm.name} onChange={e => setMemberForm(p => ({ ...p, name: e.target.value }))} placeholder="Full Name *" style={{ ...s.input, marginBottom: 8, fontSize: 15 }} />
              <input value={memberForm.email} onChange={e => setMemberForm(p => ({ ...p, email: e.target.value }))} placeholder="Email *" style={{ ...s.input, marginBottom: 8, fontSize: 15 }} />
              <input value={memberForm.phone} onChange={e => setMemberForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone (optional)" style={{ ...s.input, marginBottom: 8, fontSize: 15 }} />
              <input value={memberForm.password} onChange={e => setMemberForm(p => ({ ...p, password: e.target.value }))} placeholder="Password (min 6 chars) *" style={{ ...s.input, marginBottom: 12, fontSize: 15 }} />
              <button onClick={addTeamMember} disabled={creating} style={{ ...s.cta, width: '100%', opacity: creating ? 0.6 : 1 }}>{creating ? 'Adding...' : 'Add Team Member'}</button>
            </div>
          )}

          {teamMembers.map(member => (
            <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: member.email === user.email ? OGLOW : 'rgba(255,255,255,0.04)', border: '1px solid ' + (member.email === user.email ? OBORDER : 'rgba(255,255,255,0.08)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: member.email === user.email ? O : GRAY, fontSize: 16, fontWeight: 700 }}>{(member.name || '?')[0].toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: WHITE, fontSize: 15, fontWeight: 600 }}>{member.name} {member.email === user.email ? <span style={{ color: O, fontSize: 11 }}>(You)</span> : ''}</div>
                <div style={{ color: GRAY, fontSize: 13 }}>{member.email}</div>
              </div>
              {member.isCompanyAdmin && <span style={{ color: '#8b5cf6', fontSize: 11, fontWeight: 600 }}>Admin</span>}
              {(user.isCompanyAdmin || user.isCompanyAdmin === undefined) && member.email !== user.email && !member.isCompanyAdmin && (
                <button onClick={() => removeTeamMember(member)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 18, cursor: 'pointer' }}>×</button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: CARD, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 18, padding: '20px', marginBottom: 20 }}>
          <div style={{ color: GRAY, fontSize: 15, textAlign: 'center' }}>Team members will be available once your company profile is set up. Contact ImpressaBuild support.</div>
        </div>
      )}
      
      {!isStandalone && (
        <button onClick={onShowInstall} style={{ display: "flex", alignItems: "center", gap: 16, width: "100%", background: OGLOW, border: "1px solid " + OBORDER, borderRadius: 18, padding: "18px 20px", marginBottom: 20, cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
          <span style={{ fontSize: 28 }}>📲</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: O, fontSize: 15, fontWeight: 700, letterSpacing: 2, marginBottom: 4 }}>INSTALL APP</div>
            <div style={{ color: GRAY, fontSize: 15 }}>Add to your home screen</div>
          </div>
          <span style={{ color: O, fontSize: 20 }}>→</span>
        </button>
      )}
      
      {isStandalone && (
        <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 18, padding: "18px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 24 }}>✅</span>
          <div style={{ color: "#22c55e", fontSize: 16, fontWeight: 600 }}>App installed on your device</div>
        </div>
      )}
      
      <button style={{ ...s.ctaOutline, width: "100%", textAlign: "center", color: "#E85454", borderColor: "rgba(232,84,84,0.2)", marginBottom: 40 }} onClick={logout}>Sign Out</button>
    </div>
  );
}

// ============================================
// STYLES
// ============================================
const s = {
  root: { minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "stretch", background: BG, fontFamily: FONT, overflowX: "hidden", width: "100%", maxWidth: "100vw" },
  app: { width: "100%", maxWidth: 500, margin: "0 auto", background: BG, display: "flex", flexDirection: "column", minHeight: "100vh", position: "relative", paddingTop: "env(safe-area-inset-top, 0px)" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", flexShrink: 0 },
  burger: { background: "none", border: "none", padding: 12, cursor: "pointer", display: "flex", flexDirection: "column", gap: 6, WebkitTapHighlightColor: "transparent" },
  bLine: { width: 24, height: 2.5, background: O, borderRadius: 2, transition: "all .3s ease" },
  scroll: { flex: 1, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", paddingBottom: 100 },
  tabBar: { display: "flex", justifyContent: "space-around", alignItems: "center", padding: "12px 8px calc(24px + env(safe-area-inset-bottom, 0px))", background: BG, borderTop: "1px solid rgba(255,255,255,0.08)", flexShrink: 0, position: "sticky", bottom: 0 },
  tabBtn: { background: "none", border: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer", position: "relative", padding: "4px 18px", fontFamily: FONT, WebkitTapHighlightColor: "transparent" },
  tabDot: { width: 6, height: 6, borderRadius: "50%", background: O, position: "absolute", bottom: -5 },
  page: { padding: "18px 22px 120px" },
  pageTitle: { color: WHITE, fontSize: 28, fontWeight: 700, margin: "0 0 14px" },
  cta: { padding: "18px 28px", background: O, border: "none", borderRadius: 14, color: WHITE, fontSize: 17, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center", justifyContent: "center", WebkitTapHighlightColor: "transparent" },
  ctaOutline: { padding: "18px 24px", background: "transparent", border: "1.5px solid " + OBORDER, borderRadius: 14, color: O, fontSize: 17, fontWeight: 700, cursor: "pointer", fontFamily: FONT, WebkitTapHighlightColor: "transparent" },
  input: { width: "100%", padding: "17px 18px", background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, color: WHITE, fontSize: 18, outline: "none", fontFamily: FONT, boxSizing: "border-box" },
  inputLabel: { color: GRAY, fontSize: 15, fontWeight: 700, letterSpacing: .5, marginBottom: 10 },
  chip: { padding: "12px 18px", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 100, fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: FONT, transition: "all .2s", WebkitTapHighlightColor: "transparent" },
  chipBtn: { padding: "10px 18px", background: OGLOW, border: "1px solid " + OBORDER, borderRadius: 100, color: O, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: FONT, WebkitTapHighlightColor: "transparent" },
  quickCard: { display: "flex", alignItems: "center", gap: 16, width: "100%", background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "18px 20px", marginBottom: 12, cursor: "pointer", fontFamily: FONT, textAlign: "left", WebkitTapHighlightColor: "transparent" },
  projCard: { display: "block", width: "100%", background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 18, padding: "20px 20px", marginBottom: 14, cursor: "pointer", fontFamily: FONT, textAlign: "left", WebkitTapHighlightColor: "transparent" },
  photoCard: { display: "flex", alignItems: "center", gap: 16, background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "14px 16px", marginBottom: 10 },
  dlBtn: { width: 46, height: 46, borderRadius: 12, background: OGLOW, border: "1px solid " + OBORDER, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, color: O, fontSize: 18, WebkitTapHighlightColor: "transparent" },
  contactCard: { display: "flex", alignItems: "flex-start", gap: 16, background: CARD, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "18px 20px", marginBottom: 12 },
  linkBtn: { background: "none", border: "none", color: O, fontSize: 16, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: FONT, WebkitTapHighlightColor: "transparent" },
};
